import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactTable from 'react-table';
import { useSubscription } from '@apollo/react-hooks';
import { Row, ButtonGroup, Button, Col, Badge, Modal, ModalBody, ModalFooter } from 'reactstrap';
import ReactBSAlert from 'react-bootstrap-sweetalert';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import tz from 'moment-timezone';
import './MerchantOrderTable.css';
import { formatETA } from '../Helpers/functions/TimesFormat';

import {
    FaHourglassHalf,
    FaPeopleCarry,
    FaHome,
    FaRegStar,
    FaRegCheckCircle,
    FaClock,
    FaBookmark,
    FaCaretDown,
    FaCaretRight,
    FaExclamationTriangle,
    FaStar
} from 'react-icons/fa';

import { SUBSCRIBE_TO_ORDERS } from '../../graphql/ShipmentQueries';
import { formatTimestamp } from '../../helpers/formatter';

import Loading from '../Global/Loading';
import Car, { SUV, Truck, BoxTruck, Trailer } from '../../assets/svgs/CarTypes';
import * as shipment_urls from '../../urls/api-shipments/merchant';
import * as inventory_urls from '../../urls/api-inventory/admin';
import * as user_urls from '../../urls/api-users/admin';
import useSound from 'use-sound';
import Sound from '../../assets/CashRegister.mp3';
import IncomingTextSound from '../../assets/example.mp3';
import DispatchMap from '../Dispatch/DispatchMap';
import { ResizableBox } from 'react-resizable';
import { useGlobalContext } from '../../context';
import MessageLogsContainer from '../Dispatch/HelperComponents/MessageLogsContainer';
import FileViewer from '../SharedComponents/FileViewer';
import FileUpload from '../SharedComponents/FileUpload';
import FedexLabelModal from '../SharedComponents/FedexLabelModal';
import FedexTrackingModal from '../SharedComponents/FedexTrackingModal';
import PaymentRetryModal from '../SharedComponents/PaymentRetryModal';
import PhotoGallery from '../SharedComponents/PhotoGallery';
import { useTheme } from '../../contexts/ThemeContext';
import { getStoredCustomerDriverReviews, saveStoredCustomerDriverReview } from '../../utils/customerDriverReviewStorage';
import { DASHBOARD_PERF_MARKS, markDashboardPerformance, measureDashboardPerformance } from '../../utils/dashboardPerformance';
import { isDashboardLocalDebugMode, isLocalLikeHost } from '../../utils/environmentMode';
import { getVehicleRenderUrl, isSignedAssetUrl, normalizeAssetUrl } from '../../utils/vehicleImage';
import { buildAuthorizationHeader, hasStoredSessionTokens, notifySessionExpired, renewSessionSilently } from '../../Auth/session';
import dashboardConfig from '../../config';
import VolumeDiscountMeter, { computeMonthlySpendFromShipments } from './VolumeDiscountMeter';
import { useEntitlements } from '../../services/entitlementsService';
import { UpgradeHint } from '../SharedComponents/featureLock';
import { preferFreshShipmentWithTrackingHistory } from './merchantShipmentMerge';
import { saveSavedDraftEditState } from '../../utils/savedDraftEditState';
import { buildMerchantStoreShipmentWhereClause, shouldUseMerchantHasuraFeed } from './merchantFeedSource';

const vehicleTypeOptions = require('../../vehicle_types');
const DRAFT_CREATION_PATH = '/admin/delivery_creation';
const MERCHANT_IN_PROGRESS_SHIPMENT_STATUSES = ['shipment_ready_for_driver', 'shipment_assigned', 'shipment_claimed', 'shipment_enroute', 'shipment_pending_review'];

const VEHICLE_TYPE_LABELS = vehicleTypeOptions.reduce(
    (lookup, option) => {
        lookup[option.val] = option.label;
        return lookup;
    },
    {}
);

Object.assign(VEHICLE_TYPE_LABELS, {
    car: 'Car',
    pickup_truck: '1/2 Ton Pickup',
    truck: '1/2 Ton Pickup',
    heavy_duty_truck: '1 Ton Pickup',
    trailer: 'Truck and Trailer',
    van: 'Sprinter Van',
    semi_truck: 'Semi/Tractor',
    tractor_semi: 'Semi/Tractor'
}
);
const getVehicleTypeLabel = vehicleType => {
    if (!vehicleType) {
        return '';
    }

    return (
        VEHICLE_TYPE_LABELS[vehicleType] ||
        vehicleType
            .split('_')
            .map(segment => segment.charAt(0).toUpperCase() + segment.substring(1))
            .join(' ')
    );
};
const getCandidateShipmentStoreIds = candidateShipment => {
    if (!candidateShipment) {
        return [];
    }

    const storeIds = [];
    const shipmentLevelStoreId =
        (candidateShipment.customer_blob && candidateShipment.customer_blob.store_id) ||
        (candidateShipment.shipping_fee_blob && candidateShipment.shipping_fee_blob.shipment && candidateShipment.shipping_fee_blob.shipment.store_id) ||
        null;

    if (shipmentLevelStoreId != null && shipmentLevelStoreId !== '') {
        storeIds.push(String(shipmentLevelStoreId));
    }

    if (Array.isArray(candidateShipment.Orders)) {
        candidateShipment.Orders.forEach(orderRecord => {
            if (orderRecord && orderRecord.store_id != null && orderRecord.store_id !== '') {
                storeIds.push(String(orderRecord.store_id));
            }
        });
    }

    return Array.from(new Set(storeIds));
};

const normalizeStoreIds = values => Array.from(new Set(
    (Array.isArray(values) ? values : [values])
        .map(value => (value != null ? String(value).trim() : ''))
        .filter(Boolean)
));

const areStoreIdSetsEqual = (left, right) => {
    const normalizedLeft = normalizeStoreIds(left);
    const normalizedRight = normalizeStoreIds(right);

    if (normalizedLeft.length !== normalizedRight.length) {
        return false;
    }

    return normalizedLeft.every(storeId => normalizedRight.includes(storeId));
};

const normalizeEmailCandidate = value => {
    if (!value || typeof value !== 'string') {
        return '';
    }

    return value.trim().toLowerCase();
};

const resolveSingleStoreIdFromShipments = candidateShipments => {
    if (!Array.isArray(candidateShipments) || candidateShipments.length === 0) {
        return '';
    }

    const storeIds = Array.from(new Set(candidateShipments.flatMap(getCandidateShipmentStoreIds)));

    return storeIds.length === 1 ? storeIds[0] : '';
};

const isLikelyUserIdentifier = value => {
    if (!value || typeof value !== 'string') {
        return false;
    }

    return value.includes('|') || /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(value);
};

const getNestedShipmentString = (sources, fieldNames) => {
    for (const source of sources) {
        if (!source || typeof source !== 'object') {
            continue;
        }

        for (const fieldName of fieldNames) {
            const value = source[fieldName];

            if (typeof value === 'string' && value.trim() !== '') {
                return value.trim();
            }
        }
    }

    return '';
};

const getShipmentPlacedByDisplayName = shipment => {
    if (!shipment || typeof shipment !== 'object') {
        return '';
    }

    const shipInfo = shipment.shipping_fee_blob && shipment.shipping_fee_blob.shipment && typeof shipment.shipping_fee_blob.shipment === 'object'
        ? shipment.shipping_fee_blob.shipment
        : {};
    const customerBlob = shipment.customer_blob && typeof shipment.customer_blob === 'object'
        ? shipment.customer_blob
        : {};
    const invoiceTo = shipment.invoice_to && typeof shipment.invoice_to === 'object'
        ? shipment.invoice_to
        : {};
    const namedContact = getNestedShipmentString(
        [shipment, shipInfo, customerBlob, invoiceTo],
        ['placed_by_name', 'order_placer_name', 'created_by_name', 'merchant_name', 'contact_name', 'name', 'store_name']
    );
    const customerBlobName = [customerBlob.first_name, customerBlob.last_name].filter(Boolean).join(' ').trim();
    const rawPlacedBy = typeof shipment.placed_by === 'string'
        ? shipment.placed_by.replace(/\(behalf\)$/i, '').trim()
        : '';

    return namedContact || customerBlobName || (!isLikelyUserIdentifier(rawPlacedBy) ? rawPlacedBy : '') || customerBlob.email || '';
};

const EMPTY_SHIPMENTS = [];
const MERCHANT_RECENT_SHIPMENT_QUEUE_KEY = 'merchant_orders_recent_created_snapshots';
const MERCHANT_RECENT_SHIPMENT_QUEUE_LIMIT = 15;
const MERCHANT_RECENT_SHIPMENT_MAX_AGE_MS = 60 * 1000;
const MERCHANT_ORDER_TABS = ['pending', 'saved', 'inProgress', 'completed'];
const CUSTOMER_DEMURRAGE_FREE_MINUTES = 30;
const DEFAULT_DEMURRAGE_INCREMENT_MINUTES = 15;
const LOCAL_MERCHANT_BOOTSTRAP_TIMEOUT_MS = 15000;
const HOSTED_MERCHANT_BOOTSTRAP_TIMEOUT_MS = 15000;
const LOCAL_SHIPMENTS_HASURA_MIN_INTERVAL_MS = 8000;
const LOCAL_SHIPMENTS_HASURA_RATE_LIMIT_BACKOFF_MS = 60000;
const createDefaultExpandedItems = () => ({ pending: {}, inProgress: {}, completed: {}, saved: {} });
const claimedOrderChatEndpoint = shipment_urls.toHostedShipmentUrl(`${shipment_urls.shipmentHost}/dispatch/chat/thread`);
const OPEN_CHAT_THREAD_POLL_INTERVAL_MS = 5000;
const CLAIMED_ORDER_CHAT_POLL_INTERVAL_MS = 30000;
const CLAIMED_ORDER_CHAT_SUMMARY_BATCH_SIZE = 100;
const PRODUCTION_SHIPMENTS_ACTION_HOST = 'https://api-shipments.gophr.app';
const DEVELOPMENT_SHIPMENTS_ACTION_HOST = 'https://dev-api-shipments.gophr.app';
const CUSTOMER_VISIBLE_CLAIMED_ORDER_CHAT_THREAD_TYPES = ['customer_driver', 'customer_dispatch'];
const CUSTOMER_DRIVER_REVIEW_MAX_LENGTH = 500;
const LOCAL_TEST_CUSTOMER_DRIVER_REVIEW_FRIENDLY_ID = 10583;
const isLocalhostBrowser = () =>
    typeof window !== 'undefined' && window.location && isLocalLikeHost(window.location.hostname);

const buildSubmittedCustomerDriverReviewSnapshot = ({ shipmentId, shipmentFriendlyId, driverAuth0Id, driverName, rating, reviewText }) => ({
    shipmentId,
    shipmentFriendlyId: shipmentFriendlyId || null,
    driverAuth0Id: driverAuth0Id || null,
    driverName: driverName || '',
    rating: Number(rating || 0),
    reviewText: reviewText || '',
    submittedAt: new Date().toISOString(),
});

const getRouteDriverAuth0Id = (routeDriver, shipment = null) => {
    const shipmentDriver = shipment && typeof shipment.driver === 'object' ? shipment.driver : null;
    const listedDriver = Array.isArray(shipment && shipment.Drivers) && shipment.Drivers.length > 0 ? shipment.Drivers[0] : null;
    const listedDriverBlob = listedDriver && typeof listedDriver.driver_blob === 'object' ? listedDriver.driver_blob : null;

    if (!routeDriver || typeof routeDriver !== 'object') {
        const shipmentAuth0Candidates = [
            shipmentDriver && shipmentDriver.auth0_id,
            shipmentDriver && shipmentDriver.user_auth0_id,
            shipmentDriver && shipmentDriver.User && shipmentDriver.User.auth0_id,
            listedDriver && listedDriver.auth0_id,
            listedDriver && listedDriver.user_auth0_id,
            listedDriverBlob && listedDriverBlob.auth0_id,
            listedDriverBlob && listedDriverBlob.auth0Id,
            listedDriverBlob && listedDriverBlob.user_auth0_id,
        ];

        for (const candidate of shipmentAuth0Candidates) {
            const normalizedCandidate = typeof candidate === 'string' ? candidate.trim() : '';

            if (normalizedCandidate) {
                return normalizedCandidate;
            }
        }

        return '';
    }

    const driverBlob = routeDriver.driver_blob && typeof routeDriver.driver_blob === 'object' ? routeDriver.driver_blob : null;
    const routeDriverDriver = routeDriver.Driver && typeof routeDriver.Driver === 'object' ? routeDriver.Driver : null;
    const routeDriverUser = routeDriver.User && typeof routeDriver.User === 'object' ? routeDriver.User : null;
    const auth0Candidates = [
        routeDriver.driver_auth0_id,
        routeDriver.auth0_id,
        routeDriver.user_auth0_id,
        routeDriverUser && routeDriverUser.auth0_id,
        routeDriverUser && routeDriverUser.user_auth0_id,
        routeDriverDriver && routeDriverDriver.auth0_id,
        routeDriverDriver && routeDriverDriver.user_auth0_id,
        driverBlob && driverBlob.auth0_id,
        driverBlob && driverBlob.auth0Id,
        driverBlob && driverBlob.user_auth0_id,
        shipmentDriver && shipmentDriver.auth0_id,
        shipmentDriver && shipmentDriver.user_auth0_id,
        shipmentDriver && shipmentDriver.User && shipmentDriver.User.auth0_id,
        listedDriver && listedDriver.auth0_id,
        listedDriver && listedDriver.user_auth0_id,
        listedDriverBlob && listedDriverBlob.auth0_id,
        listedDriverBlob && listedDriverBlob.auth0Id,
        listedDriverBlob && listedDriverBlob.user_auth0_id,
    ];

    for (const candidate of auth0Candidates) {
        const normalizedCandidate = typeof candidate === 'string' ? candidate.trim() : '';

        if (normalizedCandidate) {
            return normalizedCandidate;
        }
    }

    return '';
};

const getDriverReviewStatsKey = (routeDriver, shipment = null) => {
    const shipmentDriver = shipment && typeof shipment.driver === 'object' ? shipment.driver : null;
    const listedDriver = Array.isArray(shipment && shipment.Drivers) && shipment.Drivers.length > 0 ? shipment.Drivers[0] : null;
    const listedDriverBlob = listedDriver && typeof listedDriver.driver_blob === 'object' ? listedDriver.driver_blob : null;

    if ((!routeDriver || typeof routeDriver !== 'object') && !shipmentDriver && !listedDriver) {
        return '';
    }

    const driverAuth0Id = getRouteDriverAuth0Id(routeDriver, shipment);
    if (driverAuth0Id) {
        return `auth0:${driverAuth0Id}`;
    }

    const driverIdCandidates = [
        routeDriver && routeDriver.driver_id,
        routeDriver && routeDriver.user_id,
        shipmentDriver && shipmentDriver.driver_id,
        shipmentDriver && shipmentDriver.user_id,
        listedDriver && listedDriver.driver_id,
        listedDriver && listedDriver.user_id,
        listedDriverBlob && listedDriverBlob.driver_id,
        listedDriverBlob && listedDriverBlob.user_id,
    ];
    const driverId = driverIdCandidates
        .map(candidate => (candidate != null ? String(candidate).trim() : ''))
        .find(Boolean) || '';
    if (driverId) {
        return `driver:${driverId}`;
    }

    const driverBlob = routeDriver && routeDriver.driver_blob && typeof routeDriver.driver_blob === 'object' ? routeDriver.driver_blob : null;
    const fallbackSignature = [
        driverBlob && driverBlob.phone ? String(driverBlob.phone).trim() : '',
        driverBlob && driverBlob.email ? String(driverBlob.email).trim().toLowerCase() : '',
        driverBlob && driverBlob.first_name ? String(driverBlob.first_name).trim().toLowerCase() : '',
        driverBlob && driverBlob.last_name ? String(driverBlob.last_name).trim().toLowerCase() : '',
        listedDriverBlob && listedDriverBlob.phone ? String(listedDriverBlob.phone).trim() : '',
        listedDriverBlob && listedDriverBlob.email ? String(listedDriverBlob.email).trim().toLowerCase() : '',
        listedDriverBlob && listedDriverBlob.first_name ? String(listedDriverBlob.first_name).trim().toLowerCase() : '',
        listedDriverBlob && listedDriverBlob.last_name ? String(listedDriverBlob.last_name).trim().toLowerCase() : '',
    ].filter(Boolean).join('|');

    return fallbackSignature ? `fallback:${fallbackSignature}` : '';
};

const normalizeDriverReviewStats = stats => {
    if (!stats || typeof stats !== 'object') {
        return null;
    }

    const driverAuth0Id = stats.driver_auth0_id ? String(stats.driver_auth0_id).trim() : '';
    const driverKey = stats.driver_key ? String(stats.driver_key).trim() : (driverAuth0Id ? `auth0:${driverAuth0Id}` : '');

    if (!driverKey) {
        return null;
    }

    return {
        driver_key: driverKey,
        driver_auth0_id: driverAuth0Id || null,
        average_rating: Number(stats.average_rating || 0),
        review_count: Number(stats.review_count || 0),
        completed_orders_count: Number(stats.completed_orders_count || 0),
    };
};

const normalizeDriverCompletedCount = value => {
    const normalizedValue = Number(value);
    return Number.isFinite(normalizedValue) && normalizedValue >= 0 ? normalizedValue : 0;
};

const formatDriverCompletedOrdersLabel = completedOrdersCount => {
    const normalizedCount = Number(completedOrdersCount || 0);
    return `${normalizedCount} completed order${normalizedCount === 1 ? '' : 's'}`;
};

const formatDriverRatingSummary = stats => {
    if (!stats || Number(stats.review_count || 0) <= 0) {
        return '5.0 stars';
    }

    return `${Number(stats.average_rating || 0).toFixed(1)} stars`;
};

const isCustomerVisibleClaimedOrderThreadType = threadType => CUSTOMER_VISIBLE_CLAIMED_ORDER_CHAT_THREAD_TYPES.includes(threadType);

const formatCustomerDriverReviewDescription = description => {
    if (typeof description !== 'string') {
        return '';
    }

    const trimmedDescription = description.trim();

    if (!trimmedDescription) {
        return '';
    }

    if (trimmedDescription[0] !== '[' && trimmedDescription[0] !== '{') {
        return trimmedDescription;
    }

    try {
        const parsedDescription = JSON.parse(trimmedDescription);
        const candidateItems = Array.isArray(parsedDescription)
            ? parsedDescription
            : (parsedDescription && Array.isArray(parsedDescription.items) ? parsedDescription.items : [parsedDescription]);
        const formattedItems = candidateItems.map(candidateItem => {
            if (!candidateItem || typeof candidateItem !== 'object') {
                return '';
            }

            const quantity = Number(candidateItem.quantity || 0);
            const detail = typeof candidateItem.detail === 'string' ? candidateItem.detail.trim() : '';
            const unit = typeof candidateItem.unit === 'string' ? candidateItem.unit.trim() : '';
            const parts = [];

            if (Number.isFinite(quantity) && quantity > 0) {
                parts.push(String(quantity));
            }

            if (detail) {
                parts.push(detail);
            } else if (unit) {
                parts.push(unit);
            }

            if (unit && detail && unit.toLowerCase() !== 'units' && !detail.toLowerCase().includes(unit.toLowerCase())) {
                parts.push(unit.toLowerCase());
            }

            return parts.join(' ').trim();
        }).filter(Boolean);

        if (formattedItems.length === 0) {
            return 'Completed delivery items';
        }

        if (formattedItems.length === 1) {
            return formattedItems[0];
        }

        return `${formattedItems[0]} and ${formattedItems.length - 1} more item${formattedItems.length - 1 === 1 ? '' : 's'}`;
    } catch (_) {
        return 'Completed delivery items';
    }
};

const getCustomerDriverReviewDisplayName = review => {
    if (!review || typeof review !== 'object') {
        return 'your driver';
    }

    if (typeof review.driver_name === 'string' && review.driver_name.trim() !== '') {
        return review.driver_name.trim();
    }

    const driverBlob = review.driver_blob && typeof review.driver_blob === 'object' ? review.driver_blob : null;

    if (driverBlob) {
        const blobName = [driverBlob.first_name, driverBlob.last_name].filter(Boolean).join(' ').trim();

        if (blobName) {
            return blobName;
        }

        const fallbackName = String(driverBlob.user_name || driverBlob.email || '').trim();
        if (fallbackName) {
            return fallbackName;
        }
    }

    return 'your driver';
};

const normalizeCustomerDriverReviewPrompt = review => {
    if (!review || typeof review !== 'object' || typeof review.shipment_id !== 'string' || review.shipment_id.trim() === '') {
        return null;
    }

    return {
        ...review,
        shipment_id: review.shipment_id.trim(),
        shipment_friendly_id: review.shipment_friendly_id || null,
        completed_at: review.completed_at || null,
        description: formatCustomerDriverReviewDescription(review.description),
        driver_name: getCustomerDriverReviewDisplayName(review),
    };
};

const createEmptyCustomerDriverReviewDraft = () => ({ rating: 0, reviewText: '' });

const filterReviewStateByShipmentIds = (currentValue, allowedShipmentIds) => Object.keys(currentValue || {}).reduce((nextValue, shipmentId) => {
    if (allowedShipmentIds.has(shipmentId)) {
        nextValue[shipmentId] = currentValue[shipmentId];
    }

    return nextValue;
}, {});

const trySilentRenewForMerchantAuthError = () => {
    if (!hasStoredSessionTokens()) {
        return;
    }

    renewSessionSilently().catch(() => null);
};

const isAuthErrorStatus = status => status === 401 || status === 403;

const runMerchantRequestWithSessionRecovery = async requestFactory => {
    try {
        return await requestFactory();
    } catch (error) {
        const status = error && error.response ? error.response.status : null;

        if (!isAuthErrorStatus(status) || !hasStoredSessionTokens()) {
            throw error;
        }

        try {
            await renewSessionSilently();
            return await requestFactory();
        } catch (_) {
            notifySessionExpired({ reason: 'merchant_ready_for_pickup_unauthorized' });
            throw error;
        }
    }
};

const buildChatThreadSignature = messages => {
    if (!Array.isArray(messages)) {
        return '';
    }

    return messages
        .map(message => [
            message && (message.message_id || message.sid || ''),
            message && (message.updated_at || message.created_at || message.dateCreated || ''),
            message && (message.status || ''),
            message && (message.body || ''),
        ].join(':'))
        .join('|');
};

const getClaimedOrderChatThreadTypeParam = threadType => {
    if (!isCustomerVisibleClaimedOrderThreadType(threadType)) {
        return '';
    }

    return threadType;
};

const buildClaimedOrderThreadKey = thread => {
    if (!thread || !thread.shipment_id) {
        return '';
    }

    const threadType = thread.thread_type && thread.thread_type !== 'claimed_order'
        ? thread.thread_type
        : 'claimed_order';

    if (threadType === 'claimed_order') {
        return thread.shipment_id;
    }

    return `${thread.shipment_id}:${threadType}:${thread.participant_auth0_id || ''}`;
};

const getMerchantClaimedOrderChatSeenStorageKey = scope => ['merchant-claimed-order-chat-seen', scope || 'anonymous'].join(':');

const readMerchantClaimedOrderChatSeenMap = scope => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return {};
    }

    try {
        const rawValue = window.localStorage.getItem(getMerchantClaimedOrderChatSeenStorageKey(scope));

        if (!rawValue) {
            return {};
        }

        const parsedValue = JSON.parse(rawValue);
        return parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue) ? parsedValue : {};
    } catch (error) {
        return {};
    }
};

const persistMerchantClaimedOrderChatSeenMap = (scope, nextValue) => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return;
    }

    try {
        window.localStorage.setItem(getMerchantClaimedOrderChatSeenStorageKey(scope), JSON.stringify(nextValue));
    } catch (error) {
        return;
    }
};

const getMerchantBootstrapRequestTimeout = url => (
    isLocalLikeHost(url) ? LOCAL_MERCHANT_BOOTSTRAP_TIMEOUT_MS : HOSTED_MERCHANT_BOOTSTRAP_TIMEOUT_MS
);

const isCustomerShipmentInDriverProgress = status =>
    status === 'shipment_assigned' || status === 'shipment_claimed' || status === 'shipment_enroute' || status === 'shipment_pending_review' || status === 'shipment_completed';

const isShipmentTrackingDisplayStatus = status =>
    status === 'shipment_assigned' || status === 'shipment_claimed' || status === 'shipment_enroute' || status === 'shipment_pending_review';

const hasOwn = (value, key) => Boolean(value && Object.prototype.hasOwnProperty.call(value, key));

const getPickupActionInstruction = order => {
    if (!order || !hasOwn(order, 'actions') || !order.actions || !hasOwn(order.actions, 'pick_up')) {
        return '';
    }

    const pickupAction = order.actions.pick_up;

    if (Array.isArray(pickupAction) && typeof pickupAction[0] === 'string') {
        return pickupAction[0];
    }

    if (typeof pickupAction === 'string') {
        return pickupAction;
    }

    return '';
};

const isPickupOrderForDisplay = order => {
    if (!order || typeof order !== 'object') {
        return false;
    }

    if (order.actual_action === 'pickup') {
        return true;
    }

    const pickupInstruction = getPickupActionInstruction(order);

    return hasOwn(order, 'actions') && hasOwn(order.actions, 'pick_up') && !pickupInstruction.includes('(drop)');
};

const isOrderCompletedForDisplay = order => {
    if (!order || typeof order.status !== 'string') {
        return false;
    }

    if (isPickupOrderForDisplay(order)) {
        return order.status === 'order_enroute' || order.status === 'order_completed';
    }

    return order.status === 'order_completed';
};

const getOrderCompletionTimestampForDisplay = order => {
    if (!order || typeof order !== 'object') {
        return null;
    }

    if (isPickupOrderForDisplay(order)) {
        return typeof order.arrived_at === 'string'
            ? order.arrived_at
            : order.arrival_validation && typeof order.arrival_validation.timestamp === 'string'
            ? order.arrival_validation.timestamp
            : typeof order.completed_at === 'string'
            ? order.completed_at
            : null;
    }

    return typeof order.completed_at === 'string' ? order.completed_at : null;
};

const isShipmentCompletedForDisplay = shipment => {
    if (!shipment || typeof shipment !== 'object') {
        return false;
    }

    const normalizedShipmentStatus = typeof shipment.status === 'string' ? shipment.status.trim().toLowerCase() : '';
    const normalizedRouteStatus = shipment && shipment.Route && typeof shipment.Route.status === 'string'
        ? shipment.Route.status.trim().toLowerCase()
        : '';

    if (normalizedShipmentStatus === 'shipment_pending_review') {
        return false;
    }

    if (normalizedShipmentStatus === 'shipment_completed' || normalizedShipmentStatus.includes('completed')) {
        return true;
    }

    if (normalizedRouteStatus === 'route_completed' || normalizedRouteStatus.includes('completed')) {
        return true;
    }

    if (typeof shipment.completed_at === 'string' && shipment.completed_at.trim() !== '') {
        return true;
    }

    return Array.isArray(shipment.Orders) && shipment.Orders.length > 0 && shipment.Orders.every(isOrderCompletedForDisplay);
};

const getShipmentCompletionTimestampForDisplay = shipment => {
    if (!shipment || typeof shipment !== 'object') {
        return null;
    }

    if (typeof shipment.completed_at === 'string' && shipment.completed_at.trim() !== '') {
        return shipment.completed_at;
    }

    if (!Array.isArray(shipment.Orders) || shipment.Orders.length === 0) {
        return null;
    }

    return shipment.Orders
        .map(getOrderCompletionTimestampForDisplay)
        .filter(Boolean)
        .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0] || null;
};

const formatOrderCompletionForDisplay = order => {
    const completionTimestamp = getOrderCompletionTimestampForDisplay(order);
    return completionTimestamp ? formatTimestamp(completionTimestamp) + '✅' : 'completed✅';
};

const enrichRouteDriverVehicleBlob = routeDriver => {
    if (!routeDriver || !routeDriver.vehicle_blob) {
        return routeDriver;
    }

    const vehicleBlob = routeDriver.vehicle_blob;
    const resolvedRenderUrl = getVehicleRenderUrl(vehicleBlob);
    const renderImageUrl =
        resolvedRenderUrl ||
        vehicleBlob.render_image_url ||
        vehicleBlob.vehicle_image_url ||
        vehicleBlob.image_url;
    const imageUrl =
        resolvedRenderUrl ||
        vehicleBlob.image_url ||
        vehicleBlob.render_image_url ||
        vehicleBlob.vehicle_image_url;

    return {
        ...routeDriver,
        render_image_url: routeDriver.render_image_url || renderImageUrl,
        image_url: routeDriver.image_url || imageUrl,
        vehicle_blob: {
            ...vehicleBlob,
            render_image_url: renderImageUrl,
            image_url: imageUrl,
            vehicle_image_url: vehicleBlob.vehicle_image_url || renderImageUrl,
        },
    };
};

const hasVisibleGophrCount = value => {
    if (value == null) {
        return false;
    }

    const normalizedValue = String(value).trim().toLowerCase();
    return normalizedValue !== '' && normalizedValue !== 'undefined' && normalizedValue !== 'null';
};

const getShipmentChangeToken = shipment => {
    if (!shipment || !shipment.shipment_id) {
        return '';
    }

    return JSON.stringify({
        shipmentId: shipment.shipment_id,
        status: shipment.status || '',
        updatedAt: shipment.updated_at || '',
        completedAt: shipment.completed_at || '',
        billingStatus: shipment.billing_status || '',
        billingDetails: shipment.billing_details || null,
        shippingFee: shipment.shipping_fee,
        orders: Array.isArray(shipment.Orders)
            ? shipment.Orders.map(order => ({
                orderId: order && order.order_id ? order.order_id : '',
                status: order && order.status ? order.status : '',
                updatedAt: order && order.updated_at ? order.updated_at : '',
                completedAt: order && order.completed_at ? order.completed_at : '',
                estimatedArrivalTime: order && order.estimated_arrival_time ? order.estimated_arrival_time : ''
            }))
            : []
    });
};

const getTrackedShipmentDisplayToken = shipment => {
    if (!shipment || !shipment.shipment_id) {
        return '';
    }

    const driverLocation = shipment.driver && shipment.driver.LastDriverActions && shipment.driver.LastDriverActions.location_blob
        ? shipment.driver.LastDriverActions.location_blob
        : null;
    const latestLocationUpdate = Array.isArray(shipment.LocationUpdates) && shipment.LocationUpdates.length > 0
        ? shipment.LocationUpdates[shipment.LocationUpdates.length - 1]
        : null;
    const latestTrackingDetail = shipment.Route && shipment.Route.RouteDrivers && shipment.Route.RouteDrivers[0] && shipment.Route.RouteDrivers[0].tracking_blob && Array.isArray(shipment.Route.RouteDrivers[0].tracking_blob.trackingDetails) && shipment.Route.RouteDrivers[0].tracking_blob.trackingDetails.length > 0
        ? shipment.Route.RouteDrivers[0].tracking_blob.trackingDetails[shipment.Route.RouteDrivers[0].tracking_blob.trackingDetails.length - 1]
        : null;

    return JSON.stringify({
        shipment: getShipmentChangeToken(shipment),
        driverId: shipment.driver && shipment.driver.driver_id ? shipment.driver.driver_id : '',
        driverImage: shipment.driver && shipment.driver.image_url ? shipment.driver.image_url : '',
        driverRenderImage: shipment.driver && shipment.driver.render_image_url ? shipment.driver.render_image_url : '',
        driverLocation: driverLocation
            ? { lat: driverLocation.lat, lng: driverLocation.lng, timestamp: driverLocation.timestamp || '' }
            : null,
        latestLocationUpdate: latestLocationUpdate
            ? { latitude: latestLocationUpdate.latitude, longitude: latestLocationUpdate.longitude, timestamp: latestLocationUpdate.timestamp || '' }
            : null,
        latestTrackingDetail: latestTrackingDetail
            ? { latitude: latestTrackingDetail.latitude, longitude: latestTrackingDetail.longitude, time: latestTrackingDetail.time || '' }
            : null,
    });
};

const parseLiveDemurrageTimestamp = value => {
    if (!value) {
        return null;
    }

    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeLiveFreeMinutes = value => {
    const parsed = Number(value);

    if (!Number.isFinite(parsed) || parsed <= 0) {
        return CUSTOMER_DEMURRAGE_FREE_MINUTES;
    }

    return Math.max(CUSTOMER_DEMURRAGE_FREE_MINUTES, parsed);
};

const getStageFreeMinutesFallback = (shipmentBlob, stopType) => {
    const candidates = stopType === 'pickup'
        ? [shipmentBlob && shipmentBlob.pickup_stop_time, shipmentBlob && shipmentBlob.pickupStopTime, shipmentBlob && shipmentBlob.load_time, shipmentBlob && shipmentBlob.loadTime, shipmentBlob && shipmentBlob.loading_time, shipmentBlob && shipmentBlob.loadingTime]
        : [shipmentBlob && shipmentBlob.dropoff_stop_time, shipmentBlob && shipmentBlob.dropoffStopTime, shipmentBlob && shipmentBlob.unload_time, shipmentBlob && shipmentBlob.unloadTime, shipmentBlob && shipmentBlob.unloading_time, shipmentBlob && shipmentBlob.unloadingTime];

    for (const candidate of candidates) {
        const parsed = Number(candidate);

        if (Number.isFinite(parsed) && parsed > 0) {
            return normalizeLiveFreeMinutes(parsed);
        }
    }

    return CUSTOMER_DEMURRAGE_FREE_MINUTES;
};

const formatCurrencyAmount = value => {
    const parsed = Number(value);
    return `$${(Number.isFinite(parsed) ? parsed : 0).toFixed(2)}`;
};

const getShipmentDeliveryFeeDisplay = shipment => {
    const parsedShippingFee = Number(shipment && shipment.shipping_fee);
    const shippingFeeRange = shipment && shipment.shipping_fee_blob && shipment.shipping_fee_blob.calculated && shipment.shipping_fee_blob.calculated.shipping_fee_range
        ? shipment.shipping_fee_blob.calculated.shipping_fee_range
        : null;
    const hasRange = shippingFeeRange && Number.isFinite(Number(shippingFeeRange.min)) && Number.isFinite(Number(shippingFeeRange.max));

    if (parsedShippingFee === 0 && shipment && shipment.shipment_type !== 'school_lunch' && hasRange) {
        return `${formatCurrencyAmount(shippingFeeRange.min)}--${formatCurrencyAmount(shippingFeeRange.max)}`;
    }

    if (Number.isFinite(parsedShippingFee)) {
        return formatCurrencyAmount(parsedShippingFee);
    }

    if (hasRange) {
        return `${formatCurrencyAmount(shippingFeeRange.min)}--${formatCurrencyAmount(shippingFeeRange.max)}`;
    }

    return formatCurrencyAmount(0);
};

const getCustomerLiveDemurrageClockContext = (shipment, demurrage, shipmentBlob) => {
    if (!shipment || !demurrage) {
        return null;
    }

    const demurrageStops = Array.isArray(demurrage.stops) ? demurrage.stops : [];
    const routePath = Array.isArray(shipment && shipment.Route && shipment.Route.path) ? shipment.Route.path : [];
    const shipmentOrders = Array.isArray(shipment.Orders) ? shipment.Orders : [];
    const getMatchedStop = ids => demurrageStops.find(stop => stop && ids.some(id => id && stop.stop_id === id)) || null;

    for (const waypoint of routePath) {
        const arrivalAt = waypoint && (waypoint.arrived_at || (waypoint.arrival_validation && waypoint.arrival_validation.timestamp));
        const completedAt = waypoint && waypoint.completed_at;
        const waypointMatchesShipment = waypoint && (
            waypoint.shipment_id === shipment.shipment_id
            || waypoint.waypoint_id === shipment.shipment_id
            || shipmentOrders.some(order => order && order.order_id && order.order_id === waypoint.waypoint_id)
            || shipmentOrders.some(order => order && order.order_id && order.order_id === waypoint.order_id)
        );

        if (!waypointMatchesShipment || !arrivalAt || completedAt || (waypoint && waypoint.is_waypoint_completed === true)) {
            continue;
        }

        const matchedStop = getMatchedStop([waypoint.order_id, waypoint.shipment_id, waypoint.waypoint_id]);
        const stopType = waypoint && waypoint.actions && waypoint.actions.pick_up ? 'pickup' : 'dropoff';

        return {
            arrivalAt,
            stopId: matchedStop && matchedStop.stop_id ? matchedStop.stop_id : (waypoint.order_id || waypoint.shipment_id || waypoint.waypoint_id || null),
            stopType,
            freeMinutes: normalizeLiveFreeMinutes(
                (waypoint && waypoint.stop_time)
                || (matchedStop && (matchedStop.minimum_free_minutes || matchedStop.free_minutes))
                || getStageFreeMinutesFallback(shipmentBlob, stopType)
            ),
        };
    }

    const activeOrder = shipmentOrders.find(order => {
        const arrivalAt = order && (order.arrived_at || (order.arrival_validation && order.arrival_validation.timestamp));
        return arrivalAt && !(order && order.completed_at);
    });

    if (!activeOrder) {
        const isAccruing = demurrage.status === 'accruing' || demurrage.status === 'review_required';
        const fallbackStop = [...demurrageStops]
            .reverse()
            .find(stop => stop && (Number(stop.wait_minutes || 0) > 0 || Number(stop.free_minutes || 0) > 0)) || null;
        const fallbackWaitMinutes = fallbackStop && fallbackStop.wait_minutes != null
            ? Number(fallbackStop.wait_minutes)
            : Number(demurrage.total_wait_minutes || 0);

        if (!isAccruing || !Number.isFinite(fallbackWaitMinutes) || fallbackWaitMinutes < 0) {
            return null;
        }

        const fallbackStopType = fallbackStop && fallbackStop.stop_type ? fallbackStop.stop_type : 'dropoff';

        return {
            arrivalAt: new Date(Date.now() - Math.max(0, fallbackWaitMinutes) * 60000).toISOString(),
            stopId: fallbackStop && fallbackStop.stop_id ? fallbackStop.stop_id : shipment.shipment_id,
            stopType: fallbackStopType,
            freeMinutes: normalizeLiveFreeMinutes(
                (fallbackStop && (fallbackStop.minimum_free_minutes || fallbackStop.free_minutes))
                || demurrage.total_free_minutes
                || getStageFreeMinutesFallback(shipmentBlob, fallbackStopType)
            ),
        };
    }

    const matchedStop = getMatchedStop([activeOrder.order_id, shipment.shipment_id]);
    const stopType = matchedStop && matchedStop.stop_type
        ? matchedStop.stop_type
        : (activeOrder.actions && activeOrder.actions.pick_up ? 'pickup' : 'dropoff');

    return {
        arrivalAt: activeOrder.arrived_at || (activeOrder.arrival_validation && activeOrder.arrival_validation.timestamp),
        stopId: matchedStop && matchedStop.stop_id ? matchedStop.stop_id : activeOrder.order_id,
        stopType,
        freeMinutes: normalizeLiveFreeMinutes(
            (matchedStop && (matchedStop.minimum_free_minutes || matchedStop.free_minutes))
            || getStageFreeMinutesFallback(shipmentBlob, stopType)
        ),
    };
};

const getDemurrageDisplayAmount = (demurrage, demurrageApplication, shippingFeeBlob = {}) => {
    return [
        demurrageApplication && demurrageApplication.applied_amount,
        demurrage && demurrage.total_amount,
        demurrage && demurrage.review_amount,
        demurrage && demurrage.preview_amount,
        shippingFeeBlob && shippingFeeBlob.demurrage_fee,
    ]
        .map(value => Number(value))
        .find(value => Number.isFinite(value) && value > 0) || 0;
};

const normalizeDemurrageIncrementMinutes = value => {
    const parsed = Number(value);

    if (!Number.isFinite(parsed) || parsed <= 0) {
        return DEFAULT_DEMURRAGE_INCREMENT_MINUTES;
    }

    return Math.floor(parsed);
};

const roundDemurrageCurrency = value => Math.round(Number(value || 0) * 100) / 100;

const getDemurrageAmountFromTiming = ({ hourlyRate, waitMinutes, freeMinutes, incrementMinutes }) => {
    const normalizedHourlyRate = Number(hourlyRate);

    if (!Number.isFinite(normalizedHourlyRate) || normalizedHourlyRate <= 0) {
        return 0;
    }

    const normalizedWaitMinutes = Math.max(0, Number(waitMinutes || 0));
    const normalizedFreeMinutes = Math.max(0, Number(freeMinutes || 0));
    const normalizedIncrementMinutes = normalizeDemurrageIncrementMinutes(incrementMinutes);
    const overageMinutes = Math.max(0, normalizedWaitMinutes - normalizedFreeMinutes);
    const billableMinutes = Math.floor(overageMinutes / normalizedIncrementMinutes) * normalizedIncrementMinutes;

    return roundDemurrageCurrency((billableMinutes / 60) * normalizedHourlyRate);
};

const getWaypointColumnKey = (waypoint, index) => {
    if (!waypoint) {
        return `waypoint-${index}`;
    }

    if (waypoint.type === 'merged') {
        return `merged-${waypoint.pickupOrder && waypoint.pickupOrder.order_id ? waypoint.pickupOrder.order_id : `pickup-${index}`}-${
            waypoint.dropoffOrder && waypoint.dropoffOrder.order_id ? waypoint.dropoffOrder.order_id : `dropoff-${index}`
        }`;
    }

    if (waypoint.order && waypoint.order.order_id) {
        return `${waypoint.type || 'waypoint'}-${waypoint.order.order_id}`;
    }

    return `${waypoint.type || 'waypoint'}-${index}`;
};

const getMarketplaceItemKey = (item, index) => item.item_id || item.id || `${item.product_blob && item.product_blob.sku ? item.product_blob.sku : 'item'}-${index}`;

const getRecipientLocationBlob = shipment => {
    const orders = Array.isArray(shipment && shipment.Orders) ? shipment.Orders : [];

    for (let index = orders.length - 1; index >= 0; index -= 1) {
        const candidateOrder = orders[index];

        if (candidateOrder && candidateOrder.location_blob) {
            return candidateOrder.location_blob;
        }
    }

    return null;
};

const getRecipientDisplayName = shipment => {
    const isDirectShipment = shipment && (shipment.shipment_type === 'direct' || shipment.shipment_type === 'routed_direct' || shipment.shipment_type === 'multidrop_direct');

    if (isDirectShipment) {
        const recipientLocation = getRecipientLocationBlob(shipment);
        return recipientLocation && recipientLocation.name ? recipientLocation.name : '';
    }

    const customer = shipment && shipment.customer_blob ? shipment.customer_blob : null;
    const firstName = customer && customer.first_name ? customer.first_name : '';
    const lastName = customer && customer.last_name ? customer.last_name : '';

    return `${firstName} ${lastName}`.trim();
};

const MerchantOrderTable = props => {
    const { isDarkMode } = useTheme();
    const storeIdOverride = props.storeIdOverride || '';
    const rowSurfaceBackground = isDarkMode ? '#27293d' : '#ffffff';
    const detailSurfaceBackground = isDarkMode ? '#17192D' : '#ffffff';
    const chatAlertRowBackground = isDarkMode ? '#4f3f00' : '#fff3bf';
    const chatAlertBorderColor = '#f4b400';
    const chatAlertTextColor = isDarkMode ? '#fff3bf' : '#7a5a00';

    const getFilterForTab = tab => {
        if (tab === 'pending') {
            return ['shipment_created', 'shipment_pending', 'shipment_pending_schedule'];
        }

        if (tab === 'completed') {
            return ['shipment_completed'];
        }

        if (tab === 'saved') {
            return ['shipment_scheduled', 'shipment_saved', 'shipment_pending_saved', 'shipment_saved_pending_payment_retry', 'shipment_pending_payment'];
        }

        return MERCHANT_IN_PROGRESS_SHIPMENT_STATUSES;
    };

    const resolveInitialSelectedTab = () => {
        if (MERCHANT_ORDER_TABS.includes(props.initialSelectedTab)) {
            return props.initialSelectedTab;
        }

        const storedTab = localStorage.getItem('merchant_orders_initial_tab');
        localStorage.removeItem('merchant_orders_initial_tab');

        if (MERCHANT_ORDER_TABS.includes(storedTab)) {
            return storedTab;
        }

        return 'inProgress';
    };

    const initialSelectedTab = resolveInitialSelectedTab();

    const isSavedDraftStatus = status =>
        status === 'shipment_saved' || status === 'shipment_pending_saved' || status === 'shipment_saved_pending_payment_retry';

    const isSavedDraftShipment = shipment => isSavedDraftStatus(shipment && shipment.status);
    const canShowSavedColumnActions = shipment => {
        if (!shipment) {
            return false;
        }

        return selected === 'saved' && (
            shipment.status === 'shipment_scheduled'
            || shipment.status === 'shipment_saved'
            || shipment.status === 'shipment_pending_saved'
            || shipment.status === 'shipment_saved_pending_payment_retry'
            || shipment.status === 'shipment_pending_payment'
        );
    };
    const hasInlinePendingShipmentActions = shipment => {
        if (!shipment) {
            return false;
        }

        return shipment.shipment_type === 'direct' || shipment.shipment_type === 'routed_direct' || shipment.shipment_type === 'multidrop_direct';
    };

    const canMerchantCancelShipment = shipment => {
        if (!shipment || isSavedDraftShipment(shipment)) {
            return false;
        }

        return shipment.status === 'shipment_ready_for_driver';
    };

    const canEditSavedDraftShipment = shipment => {
        if (!shipment || shipment.shipment_type !== 'direct') {
            return false;
        }

        const isEditableDraftStatus =
            isSavedDraftShipment(shipment) || shipment.status === 'shipment_pending_payment' || shipment.status === 'shipment_saved_pending_payment_retry';

        return isEditableDraftStatus && Array.isArray(shipment.Orders) && shipment.Orders.length >= 1;
    };

    const canSubmitSavedDraftForCheckout = shipment => {
        if (!shipment) {
            return false;
        }
        return (
            shipment.status === 'shipment_saved' || shipment.status === 'shipment_pending_saved'
        ) && ['direct', 'routed_direct', 'multidrop_direct'].includes(shipment.shipment_type);
    };

    const [submittingSavedDraftId, setSubmittingSavedDraftId] = useState(null);
    const [pendingCheckoutShipmentIds, setPendingCheckoutShipmentIds] = useState(new Set());

    const submitSavedDraftForCheckout = async shipment => {
        if (!shipment || !shipment.shipment_id) {
            return;
        }
        setSubmittingSavedDraftId(shipment.shipment_id);
        try {
            await axios({
                method: 'put',
                headers: {
                    Authorization: buildAuthorizationHeader(),
                    'content-type': 'application/json',
                },
                url: shipment_urls.checkoutSavedShipment(shipment.shipment_id),
                data: {},
            });
            setPendingCheckoutShipmentIds(prev => new Set([...prev, shipment.shipment_id]));
            setFilter(['shipment_ready_for_driver', 'shipment_assigned', 'shipment_claimed', 'shipment_enroute', 'shipment_pending_review']);
            setSelected('inProgress');
            props.notify('tc', 'Order submitted successfully.');
        } catch (error) {
            const msg = error && error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : 'Failed to submit order. Please try again.';
            props.notify('tc', msg, 'danger');
        } finally {
            setSubmittingSavedDraftId(null);
        }
    };

    const openSavedDraftEditor = shipment => {
        if (!shipment || !props.history || typeof props.history.push !== 'function') {
            return;
        }

        const draftStoreId =
            (shipment.customer_blob && shipment.customer_blob.store_id) ||
            (shipment.shipping_fee_blob && shipment.shipping_fee_blob.shipment && shipment.shipping_fee_blob.shipment.store_id) ||
            (Array.isArray(shipment.Orders) && shipment.Orders.find(order => order && order.store_id) && shipment.Orders.find(order => order && order.store_id).store_id) ||
            null;

        const savedDraftEdit = {
            shipment,
            storeId: draftStoreId,
        };
        saveSavedDraftEditState(savedDraftEdit);

        if (typeof props.onEditSavedDraft === 'function') {
            props.onEditSavedDraft(savedDraftEdit);
            return;
        }

        props.history.push({
            pathname: DRAFT_CREATION_PATH,
            state: {
                savedDraftEdit,
            },
        });
    };

    const [almostReady, setAlmostReady] = useState([]);
    const [expandedItems] = useState(props.expandedItems || createDefaultExpandedItems());
    const [filter, setFilter] = useState(getFilterForTab(initialSelectedTab));
    const [order] = useState([]);
    const [shipment, setShipment] = useState('');
    const [ready, setReady] = useState([]);
    // const [selected, setSelected] = useState('pending');
    const [selected, setSelected] = useState(initialSelectedTab);
    const [selectSavedAction, setSelectSavedAction] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [showAlert1, setShowAlert1] = useState(false);
    const [showDecline, setShowDecline] = useState(false);
    const [showDeclineQuoteShipmentAlert, setShowDeclineQuoteShipmentAlert] = useState(false);

    const [item, setItem] = useState(null);
    const [store, setStore] = useState(null);
    const [storeLoading, setStoreLoading] = useState(true);
    const [showSignature, setShowSignature] = useState(false);
    const [signatureUrl, setSignatureUrl] = useState('');

    const [amountPending, setAmountPending] = useState(0);
    const [amountApproval, setAmountApproval] = useState(0);
    const isLocalDashboardEnvironment = isDashboardLocalDebugMode();

    // Payment retry modal state
    const [paymentRetryModalOpen, setPaymentRetryModalOpen] = useState(false);
    const [selectedShipmentForPayment, setSelectedShipmentForPayment] = useState(null);
    const [paymentSuccessAlert, setPaymentSuccessAlert] = useState(false);

    //! MAP
    const [coordinates, setCoordinates] = useState({ latitude: 30.23119, longitude: -93.21163 });
    const [trafficLayer, setTrafficLayer] = useState(false);
    const [driversEnroute, setDriversEnroute] = useState([]);
    const [readyShipmentsCheck, setReadyShipmentsCheck] = useState(false);
    const [deliveringShipmentsCheck, setDeliveringShipmentsCheck] = useState(false);
    const [driversEnrouteCheck, setDriversEnrouteCheck] = useState(false);
    const [shipmentClick, setShipmentClick] = useState('');

    //! Market
    const [marketNote, setMarketNote] = useState('');

    //! Fedex Label
    const [showFedexLabel, setShowFedexLabel] = useState(false);
    const [showTracking, setShowTracking] = useState(false);
    const [selectedShipmentId, setSelectedShipmentId] = useState(null);
    const [liveSubscriptionsEnabled, setLiveSubscriptionsEnabled] = useState(false);
    const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
    const [showOrderChatModal, setShowOrderChatModal] = useState(false);
    const [orderChatMessage, setOrderChatMessage] = useState('');
    const [orderChatMessages, setOrderChatMessages] = useState([]);
    const [orderChatRecipientInfo, setOrderChatRecipientInfo] = useState({});
    const [claimedOrderChatSummariesByThreadId, setClaimedOrderChatSummariesByThreadId] = useState({});
    const [claimedOrderChatSeenByThreadId, setClaimedOrderChatSeenByThreadId] = useState({});
    const [claimedOrderChatUnreadCountsByThreadId, setClaimedOrderChatUnreadCountsByThreadId] = useState({});
    const [driverProfilePhotoOverridesById, setDriverProfilePhotoOverridesById] = useState({});
    const [pendingCustomerDriverReviews, setPendingCustomerDriverReviews] = useState([]);
    const [customerDriverReviewDrafts, setCustomerDriverReviewDrafts] = useState({});
    const [customerDriverReviewMessages, setCustomerDriverReviewMessages] = useState({});
    const [customerDriverReviewModalShipmentId, setCustomerDriverReviewModalShipmentId] = useState(null);
    const [submittingCustomerDriverReviewShipmentId, setSubmittingCustomerDriverReviewShipmentId] = useState('');
    const [customerDriverReviewsSupported, setCustomerDriverReviewsSupported] = useState(true);
    const [customerDriverReviewStatsByDriverKey, setCustomerDriverReviewStatsByDriverKey] = useState({});
    const [customerDriverReviewStatsSupported, setCustomerDriverReviewStatsSupported] = useState(true);
    const [customerDriverReviewStatsRefreshToken, setCustomerDriverReviewStatsRefreshToken] = useState(0);
    const [driverCompletedOrdersCountByDriverKey, setDriverCompletedOrdersCountByDriverKey] = useState({});
    const [driverAuth0IdOverridesByDriverKey, setDriverAuth0IdOverridesByDriverKey] = useState({});
    const [hiddenLocalTestCustomerDriverReviewShipmentIds, setHiddenLocalTestCustomerDriverReviewShipmentIds] = useState([]);
    const previousClaimedOrderChatMessageIdsRef = useRef({});
    const countedClaimedOrderChatMessageIdsRef = useRef({});
    const previousClaimedOrderChatUnreadCountsRef = useRef({});
    const hasHydratedClaimedOrderChatSummariesRef = useRef(false);
    const driverProfilePhotoOverridesRef = useRef({});
    const driverProfilePhotoLookupInFlightRef = useRef({});
    const claimedOrderChatSummariesUnavailableRef = useRef(false);
    const completedShipmentsRequestInFlightRef = useRef(false);
    const fallbackShipmentsRequestInFlightRef = useRef(false);
    const localHasuraLastRequestAtRef = useRef(0);
    const localHasuraRateLimitUntilRef = useRef(0);
    const autoPromptedCustomerDriverReviewShipmentIdsRef = useRef(new Set());
    const shouldUseScopedMerchantDashboardFeed = false;

    useEffect(() => {
        let timeoutId = null;
        let idleCallbackId = null;
        let cancelled = false;

        const enableSubscriptions = () => {
            if (!cancelled) {
                setLiveSubscriptionsEnabled(true);
            }
        };

        if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
            idleCallbackId = window.requestIdleCallback(enableSubscriptions, { timeout: 1200 });
        } else {
            timeoutId = setTimeout(enableSubscriptions, 250);
        }

        return () => {
            cancelled = true;
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
            if (idleCallbackId !== null && typeof window !== 'undefined' && typeof window.cancelIdleCallback === 'function') {
                window.cancelIdleCallback(idleCallbackId);
            }
        };
    }, []);

    const { loading, error, data } = useSubscription(SUBSCRIBE_TO_ORDERS, {
        skip: shouldUseScopedMerchantDashboardFeed || isLocalDashboardEnvironment || !liveSubscriptionsEnabled || documentsModalOpen,
    });
    const {
        setMarketIdMerchants,
        setRouteClick,
        setEnrouteClickDriver,
        setReadyRoutes,
        setDeliveringRoutes,
        setAvailableDrivers,
        setEnrouteDrivers,
        setOfflineDrivers,
    } = useGlobalContext();
    const hasMarkedMerchantDataReady = useRef(false);

    useEffect(() => {
        if (hasMarkedMerchantDataReady.current || loading || !data) {
            return;
        }

        hasMarkedMerchantDataReady.current = true;
        markDashboardPerformance(DASHBOARD_PERF_MARKS.MERCHANT_DATA_READY, {
            shipments: data && data.Shipments ? data.Shipments.length : 0,
        });
        measureDashboardPerformance('login_to_merchant_data_ready_ms', DASHBOARD_PERF_MARKS.LOGIN_AUTHENTICATED, DASHBOARD_PERF_MARKS.MERCHANT_DATA_READY);
        measureDashboardPerformance('admin_mount_to_merchant_data_ready_ms', DASHBOARD_PERF_MARKS.ADMIN_MOUNTED, DASHBOARD_PERF_MARKS.MERCHANT_DATA_READY);
    }, [loading, data]);

    const now = useMemo(() => moment(), []);
    const tomorrow = useMemo(() => moment().add(1, 'day'), []);

    const [play] = useSound(Sound);
    const [playChatNotification] = useSound(IncomingTextSound, { volume: 1, interrupt: true });
    const hasUserInteractedRef = useRef(false);
    const chatNotificationAudioRef = useRef(null);

    function playChatNotificationSound() {
        if (!hasUserInteractedRef.current) {
            return;
        }

        playChatNotification();

        if (chatNotificationAudioRef.current) {
            chatNotificationAudioRef.current.currentTime = 0;
            chatNotificationAudioRef.current.play().catch(() => {});
        }
    }

    useEffect(() => {
        chatNotificationAudioRef.current = new Audio(IncomingTextSound);
        chatNotificationAudioRef.current.preload = 'auto';
        chatNotificationAudioRef.current.volume = 1;

        const enableSound = () => {
            if (!hasUserInteractedRef.current && chatNotificationAudioRef.current) {
                chatNotificationAudioRef.current.muted = true;
                chatNotificationAudioRef.current.play()
                    .then(() => {
                        if (!chatNotificationAudioRef.current) {
                            return;
                        }

                        chatNotificationAudioRef.current.pause();
                        chatNotificationAudioRef.current.currentTime = 0;
                        chatNotificationAudioRef.current.muted = false;
                    })
                    .catch(() => {
                        if (chatNotificationAudioRef.current) {
                            chatNotificationAudioRef.current.muted = false;
                        }
                    });
            }

            hasUserInteractedRef.current = true;
        };

        window.addEventListener('pointerdown', enableSound, { passive: true });
        window.addEventListener('keydown', enableSound, { passive: true });

        return () => {
            window.removeEventListener('pointerdown', enableSound);
            window.removeEventListener('keydown', enableSound);
            chatNotificationAudioRef.current = null;
        };
    }, [playChatNotification]);

    const toggleSignature = () => {
        setShowSignature(!showSignature);
    };

    const selectedTrackingShipmentId = shipmentClick && shipmentClick.shipment_id ? shipmentClick.shipment_id : '';
    const selectedTrackingShipmentStatus = shipmentClick && shipmentClick.status ? shipmentClick.status : '';
    const merchantDisplayTimezone = (store && store.timezone) || 'America/Chicago';
    const formatShipmentTimestampForDisplay = value => formatTimestamp(tz(value).tz(merchantDisplayTimezone));

    const milesToMeters = miles => miles * 1609.344;

    const haversineDistanceMeters = (start, end) => {
        if (!start || !end) {
            return null;
        }

        const toRadians = value => (value * Math.PI) / 180;
        const earthRadiusMeters = 6371000;
        const latDelta = toRadians(end.lat - start.lat);
        const lngDelta = toRadians(end.lng - start.lng);
        const startLat = toRadians(start.lat);
        const endLat = toRadians(end.lat);

        const a =
            Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
            Math.cos(startLat) * Math.cos(endLat) * Math.sin(lngDelta / 2) * Math.sin(lngDelta / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return earthRadiusMeters * c;
    };

    const getTrackingRefreshMsForDistance = distanceMeters => {
        if (distanceMeters == null) {
            return 10 * 60 * 1000;
        }

        if (distanceMeters < milesToMeters(5)) {
            return 60 * 1000;
        }

        if (distanceMeters < milesToMeters(30)) {
            return 10 * 60 * 1000;
        }

        if (distanceMeters < milesToMeters(100)) {
            return 20 * 60 * 1000;
        }

        return 30 * 60 * 1000;
    };

    const isLiveTrackedShipmentStatus = status => {
        return status === 'shipment_assigned' || status === 'shipment_claimed' || status === 'shipment_enroute';
    };

    const hasTrackingRouteDetails = shipment => {
        const trackingDetails = shipment && shipment.Route && shipment.Route.RouteDrivers && shipment.Route.RouteDrivers[0] && shipment.Route.RouteDrivers[0].tracking_blob && Array.isArray(shipment.Route.RouteDrivers[0].tracking_blob.trackingDetails)
            ? shipment.Route.RouteDrivers[0].tracking_blob.trackingDetails
            : [];

        return trackingDetails.length > 0;
    };

    const hasLocationUpdates = shipment => Array.isArray(shipment && shipment.LocationUpdates) && shipment.LocationUpdates.length > 0;

    const mergeTrackedShipmentSelection = (currentShipment, latestShipment) => {
        if (!currentShipment) {
            return latestShipment;
        }

        if (!latestShipment) {
            return currentShipment;
        }

        const latestHasRouteDetails = hasTrackingRouteDetails(latestShipment);
        const currentHasRouteDetails = hasTrackingRouteDetails(currentShipment);
        const latestHasLocationDetails = hasLocationUpdates(latestShipment);
        const currentHasLocationDetails = hasLocationUpdates(currentShipment);
        const shouldFreezeCompletedTracking =
            currentShipment.status === 'shipment_completed' || latestShipment.status === 'shipment_completed';
        const latestDriver = latestShipment.driver || null;
        const currentDriver = currentShipment.driver || null;
        const mergedDriver = latestDriver || currentDriver
            ? {
                ...(currentDriver || {}),
                ...(latestDriver || {}),
                User: {
                    ...((currentDriver && currentDriver.User) || {}),
                    ...((latestDriver && latestDriver.User) || {}),
                },
                vehicle_blob:
                    (latestDriver && latestDriver.vehicle_blob) ||
                    (currentDriver && currentDriver.vehicle_blob) ||
                    null,
                render_image_url:
                    (latestDriver && latestDriver.render_image_url) ||
                    (currentDriver && currentDriver.render_image_url) ||
                    '',
                image_url:
                    (latestDriver && latestDriver.image_url) ||
                    (currentDriver && currentDriver.image_url) ||
                    '',
            }
            : null;

        return {
            ...latestShipment,
            driver: shouldFreezeCompletedTracking ? (currentShipment.driver || latestShipment.driver || mergedDriver) : mergedDriver,
            Route: shouldFreezeCompletedTracking
                ? latestHasRouteDetails && !currentHasRouteDetails
                    ? latestShipment.Route
                    : currentShipment.Route || latestShipment.Route
                : latestHasRouteDetails || !currentHasRouteDetails
                ? latestShipment.Route
                : currentShipment.Route,
            LocationUpdates: shouldFreezeCompletedTracking
                ? currentHasLocationDetails
                    ? currentShipment.LocationUpdates
                    : latestShipment.LocationUpdates
                : latestHasLocationDetails || !currentHasLocationDetails
                ? latestShipment.LocationUpdates
                : currentShipment.LocationUpdates,
        };
    };

    const getTrackingTargetOrder = shipment => {
        if (!shipment || !shipment.Orders || shipment.Orders.length === 0) {
            return null;
        }

        const pendingPickup = shipment.Orders.find(order => order.actions.hasOwnProperty('pick_up') && order.completed_at == null && !order.actions.pick_up[0].includes('(drop)'));
        if (pendingPickup) {
            return pendingPickup;
        }

        const pendingDropoff = shipment.Orders.find(order => (order.actions.hasOwnProperty('drop_off') || order.actual_action === 'dropoff') && order.completed_at == null);
        if (pendingDropoff) {
            return pendingDropoff;
        }

        return shipment.Orders[shipment.Orders.length - 1];
    };

    const getShipmentTargetLocation = shipment => {
        const targetOrder = getTrackingTargetOrder(shipment);

        if (targetOrder && targetOrder.location_blob && targetOrder.location_blob.lat != null && targetOrder.location_blob.lng != null) {
            return { lat: targetOrder.location_blob.lat, lng: targetOrder.location_blob.lng };
        }

        if (shipment && shipment.customer_blob && shipment.customer_blob.lat != null && shipment.customer_blob.lng != null) {
            return { lat: shipment.customer_blob.lat, lng: shipment.customer_blob.lng };
        }

        return null;
    };

    const getShipmentDriverLocation = shipment => {
        if (shipment && shipment.driver && shipment.driver.LastDriverActions && shipment.driver.LastDriverActions.location_blob) {
            return {
                lat: shipment.driver.LastDriverActions.location_blob.lat,
                lng: shipment.driver.LastDriverActions.location_blob.lng
            };
        }

        const matchingDriver = driversEnroute.find(driver => shipment && shipment.Route && shipment.Route.RouteDrivers && shipment.Route.RouteDrivers[0] && driver.driver_id === shipment.Route.RouteDrivers[0].driver_id);
        if (matchingDriver && matchingDriver.LastDriverActions && matchingDriver.LastDriverActions.location_blob) {
            return {
                lat: matchingDriver.LastDriverActions.location_blob.lat,
                lng: matchingDriver.LastDriverActions.location_blob.lng
            };
        }

        return null;
    };

    const getTrackingRefreshMsForShipment = shipment => {
        const driverLocation = getShipmentDriverLocation(shipment);
        const targetLocation = getShipmentTargetLocation(shipment);
        const distanceMeters = haversineDistanceMeters(driverLocation, targetLocation);

        return getTrackingRefreshMsForDistance(distanceMeters);
    };

    const getNextTrackingRefreshMs = () => {
        const intervals = [];

        if (driversEnrouteCheck) {
            visibleShipments.filter(isTrackedShipment).forEach(shipment => {
                intervals.push(getTrackingRefreshMsForShipment(shipment));
            });
        }

        if (selectedTrackingShipmentId !== '' && isShipmentTrackingDisplayStatus(selectedTrackingShipmentStatus)) {
            const selectedShipment = visibleShipments.find(shipment => shipment.shipment_id === selectedTrackingShipmentId) || (shipmentClick !== '' ? shipmentClick : null);
            if (selectedShipment) {
                intervals.push(getTrackingRefreshMsForShipment(selectedShipment));
            }
        }

        if (intervals.length === 0) {
            return 10 * 60 * 1000;
        }

        return Math.min(...intervals);
    };

    const isTrackedShipment = shipment => {
        if (!shipment || !shipment.Route || !shipment.Route.RouteDrivers || shipment.Route.RouteDrivers.length === 0) {
            return false;
        }

        if (shipment.shipping_fee_blob && shipment.shipping_fee_blob.hasOwnProperty('is_fedex') && shipment.shipping_fee_blob.is_fedex) {
            return false;
        }

        return isLiveTrackedShipmentStatus(shipment.status);
    };

    const isTrackingDisplayShipment = shipment => {
        if (!shipment || !shipment.Route || !shipment.Route.RouteDrivers || shipment.Route.RouteDrivers.length === 0) {
            return false;
        }

        if (shipment.shipping_fee_blob && shipment.shipping_fee_blob.hasOwnProperty('is_fedex') && shipment.shipping_fee_blob.is_fedex) {
            return false;
        }

        return isShipmentTrackingDisplayStatus(shipment.status);
    };

    const decodeJwtPayload = token => {
        if (!token) {
            return null;
        }

        try {
            const payloadSegment = token.split('.')[1];

            if (!payloadSegment) {
                return null;
            }

            const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
            const normalizedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
            const json = window.atob(normalizedBase64);

            return JSON.parse(json);
        } catch (_) {
            return null;
        }
    };

    const getCurrentAuth0Id = () => {
        const accessPayload = decodeJwtPayload(localStorage.getItem('access_token'));
        if (accessPayload && accessPayload.sub) {
            return accessPayload.sub;
        }

        const idPayload = decodeJwtPayload(localStorage.getItem('id_token'));
        return (idPayload && idPayload.sub) || '';
    };

    const resolveLastCreatedShipmentId = () => {
        const createdAtRaw = localStorage.getItem('merchant_orders_last_created_at');
        const createdAtMs = createdAtRaw ? Number(createdAtRaw) : 0;
        const maxAgeMs = 15 * 60 * 1000;

        if (!createdAtMs || Number.isNaN(createdAtMs) || Date.now() - createdAtMs > maxAgeMs) {
            localStorage.removeItem('merchant_orders_last_created_shipment_id');
            localStorage.removeItem('merchant_orders_last_created_at');
            return '';
        }

        return localStorage.getItem('merchant_orders_last_created_shipment_id') || '';
    };

    const sanitizeStoredLandingSnapshot = snapshot => {
        const shipmentId =
            (snapshot && snapshot.shipment_id) ||
            (snapshot && snapshot.shipment && snapshot.shipment.shipment_id) ||
            '';
        const createdAtMs = snapshot && snapshot.created_at ? Number(snapshot.created_at) : 0;

        if (!shipmentId || !createdAtMs || Number.isNaN(createdAtMs)) {
            return null;
        }

        if (Date.now() - createdAtMs > MERCHANT_RECENT_SHIPMENT_MAX_AGE_MS) {
            return null;
        }

        if (!snapshot.shipment || typeof snapshot.shipment !== 'object') {
            return null;
        }

        return {
            ...snapshot,
            shipment_id: shipmentId,
            created_at: createdAtMs,
            shipment: {
                ...snapshot.shipment,
                shipment_id: shipmentId,
            },
        };
    };

    const resolveLastCreatedSnapshot = () => {
        const createdAtRaw = localStorage.getItem('merchant_orders_last_created_at');
        const createdAtMs = createdAtRaw ? Number(createdAtRaw) : 0;
        const maxAgeMs = 15 * 60 * 1000;

        if (!createdAtMs || Number.isNaN(createdAtMs) || Date.now() - createdAtMs > maxAgeMs) {
            localStorage.removeItem('merchant_orders_last_created_snapshot');
            return null;
        }

        try {
            const raw = localStorage.getItem('merchant_orders_last_created_snapshot');
            const sanitizedSnapshot = sanitizeStoredLandingSnapshot(raw ? JSON.parse(raw) : null);

            if (!sanitizedSnapshot) {
                localStorage.removeItem('merchant_orders_last_created_snapshot');
                return null;
            }

            return sanitizedSnapshot;
        } catch (_) {
            return null;
        }
    };

    const resolveRecentCreatedSnapshots = () => {
        let parsedSnapshots = [];

        try {
            const raw = localStorage.getItem(MERCHANT_RECENT_SHIPMENT_QUEUE_KEY);
            parsedSnapshots = raw ? JSON.parse(raw) : [];
        } catch (_) {
            parsedSnapshots = [];
        }

        const sanitizedSnapshots = [];
        const seenShipmentIds = new Set();

        (Array.isArray(parsedSnapshots) ? parsedSnapshots : []).forEach(snapshot => {
            const sanitizedSnapshot = sanitizeStoredLandingSnapshot(snapshot);

            if (!sanitizedSnapshot || seenShipmentIds.has(sanitizedSnapshot.shipment_id)) {
                return;
            }

            seenShipmentIds.add(sanitizedSnapshot.shipment_id);
            sanitizedSnapshots.push(sanitizedSnapshot);
        });

        const boundedSnapshots = sanitizedSnapshots.slice(0, MERCHANT_RECENT_SHIPMENT_QUEUE_LIMIT);

        if (boundedSnapshots.length === 0) {
            localStorage.removeItem(MERCHANT_RECENT_SHIPMENT_QUEUE_KEY);
            return [];
        }

        localStorage.setItem(MERCHANT_RECENT_SHIPMENT_QUEUE_KEY, JSON.stringify(boundedSnapshots));
        return boundedSnapshots;
    };

    const isLocalShipmentSnapshot = snapshot => {
        if (!snapshot || !snapshot.shipment) {
            return false;
        }

        const actionHost = snapshot.shipment.__actionHost || '';
        return isLocalLikeHost(actionHost);
    };

    const forceScrollTop = () => {
        const scrollTargets = [
            window,
            document.documentElement,
            document.body,
            document.scrollingElement,
            document.querySelector('.main-panel'),
            document.querySelector('.content'),
            document.querySelector('.wrapper')
        ].filter(Boolean);

        scrollTargets.forEach(target => {
            if (target === window) {
                window.scrollTo({ top: 0, behavior: 'auto' });
                return;
            }

            if (typeof target.scrollTo === 'function') {
                target.scrollTo({ top: 0, behavior: 'auto' });
            }
            target.scrollTop = 0;
        });
    };

    const [lastCreatedShipmentId] = useState(resolveLastCreatedShipmentId);
    const [lastCreatedSnapshot] = useState(resolveLastCreatedSnapshot);
    const [recentCreatedSnapshots] = useState(resolveRecentCreatedSnapshots);
    const [hydratedLastCreatedShipment, setHydratedLastCreatedShipment] = useState(null);
    const [fallbackShipments, setFallbackShipments] = useState([]);
    const [completedSupplementShipments, setCompletedSupplementShipments] = useState([]);
    const [deletedShipmentIds, setDeletedShipmentIds] = useState([]);
    const [, setFallbackShipmentsLoading] = useState(false);
    const [accessibleStoreIds, setAccessibleStoreIds] = useState([]);
    const bootstrappedAuth0IdRef = useRef('');
    const feedDerivedStoreBootstrapKeyRef = useRef('');
    const lastCreatedVisibilityMissingLogRef = useRef('');
    const lastCreatedVisibilityCheckLogRef = useRef('');

    const appendMerchantSourceDebug = () => {};

    const areShipmentsEqualById = (previousList, nextList) => {
        if (previousList === nextList) {
            return true;
        }

        if (!Array.isArray(previousList) || !Array.isArray(nextList)) {
            return false;
        }

        if (previousList.length !== nextList.length) {
            return false;
        }

        for (let index = 0; index < previousList.length; index++) {
            const previousItem = previousList[index];
            const nextItem = nextList[index];

            if (!previousItem || !nextItem || previousItem.shipment_id !== nextItem.shipment_id) {
                return false;
            }

            if (getShipmentChangeToken(previousItem) !== getShipmentChangeToken(nextItem)) {
                return false;
            }
        }

        return true;
    };

    const areStringListsEqual = (previousList, nextList) => {
        if (previousList === nextList) {
            return true;
        }

        if (!Array.isArray(previousList) || !Array.isArray(nextList)) {
            return false;
        }

        if (previousList.length !== nextList.length) {
            return false;
        }

        for (let index = 0; index < previousList.length; index++) {
            if (previousList[index] !== nextList[index]) {
                return false;
            }
        }

        return true;
    };

    const hasCompletedBilling = shipment => {
        if (!shipment) {
            return false;
        }

        const billingDetails = shipment.billing_details && typeof shipment.billing_details === 'object'
            ? shipment.billing_details
            : null;
        const invoiceStatus = billingDetails && typeof billingDetails.status === 'string'
            ? billingDetails.status.trim().toLowerCase()
            : '';

        return (billingDetails && billingDetails.paid === true) || invoiceStatus === 'paid';
    };

    const getDisplayShipmentStatus = shipment => {
        const status = shipment && typeof shipment.status === 'string' ? shipment.status : '';

        if (status !== 'shipment_completed' && status !== 'shipment_pending_review' && isShipmentCompletedForDisplay(shipment)) {
            return 'shipment_completed';
        }

        if ((status === 'shipment_saved_pending_payment_retry' || status === 'shipment_pending_payment') && hasCompletedBilling(shipment)) {
            return 'shipment_ready_for_driver';
        }

        return status;
    };

    const normalizeShipmentForDisplay = shipment => {
        if (!shipment) {
            return shipment;
        }

        const displayStatus = getDisplayShipmentStatus(shipment);
        const actionHost = shipment.__actionHost || shipment_urls.shipmentHost;
        const normalizedOrders = Array.isArray(shipment.Orders)
            ? shipment.Orders.map(orderRecord => ({
                ...orderRecord,
                Items: Array.isArray(orderRecord && orderRecord.Items) ? orderRecord.Items : [],
                OrderHandshakes: Array.isArray(orderRecord && orderRecord.OrderHandshakes) ? orderRecord.OrderHandshakes : [],
            }))
            : [];
        const normalizedRoute = shipment.Route
            ? {
                ...shipment.Route,
                RouteDrivers: Array.isArray(shipment.Route.RouteDrivers)
                    ? shipment.Route.RouteDrivers.map(enrichRouteDriverVehicleBlob)
                    : [],
            }
            : { RouteDrivers: [] };
        const normalizedShipmentHandshakes = Array.isArray(shipment.ShipmentHandshakes) ? shipment.ShipmentHandshakes : [];

        if (displayStatus === shipment.status) {
            if (shipment.__actionHost) {
                return {
                    ...shipment,
                    Orders: normalizedOrders,
                    Route: normalizedRoute,
                    ShipmentHandshakes: normalizedShipmentHandshakes,
                };
            }

            return {
                ...shipment,
                Orders: normalizedOrders,
                Route: normalizedRoute,
                ShipmentHandshakes: normalizedShipmentHandshakes,
                __actionHost: actionHost,
            };
        }

        return {
            ...shipment,
            Orders: normalizedOrders,
            Route: normalizedRoute,
            ShipmentHandshakes: normalizedShipmentHandshakes,
            __actionHost: actionHost,
            raw_status: shipment.status,
            status: displayStatus,
        };
    };

    const withActionHost = (candidateShipment, actionHost) => {
        if (!candidateShipment) {
            return candidateShipment;
        }

        return {
            ...candidateShipment,
            __actionHost: actionHost || candidateShipment.__actionHost || shipment_urls.shipmentHost,
        };
    };

    const normalizeShipmentActionHost = actionHost => {
        const fallbackHost = shipment_urls.shipmentHost;

        if (typeof actionHost !== 'string' || actionHost.trim() === '') {
            return fallbackHost;
        }

        const trimmedHost = actionHost.trim();
        const shouldPreferProductionHostedHost = isLocalDashboardEnvironment
            && process.env.REACT_APP_LOCAL_DEBUG_HOSTED_STAGE !== 'development';

        if (shouldPreferProductionHostedHost && trimmedHost.includes(DEVELOPMENT_SHIPMENTS_ACTION_HOST)) {
            return trimmedHost.replace(DEVELOPMENT_SHIPMENTS_ACTION_HOST, PRODUCTION_SHIPMENTS_ACTION_HOST);
        }

        return trimmedHost;
    };

    const getShipmentActionHost = candidateShipment =>
        normalizeShipmentActionHost((candidateShipment && candidateShipment.__actionHost) || shipment_urls.shipmentHost);

    const getItemActionHost = candidateItem =>
        getShipmentActionHost(candidateItem && candidateItem.Order && candidateItem.Order.Shipment);

    const isLikelyUserIdentifier = value => {
        if (!value || typeof value !== 'string') {
            return false;
        }

        return value.includes('|') || /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(value);
    };

    const normalizeAuth0Candidate = value => {
        if (!value || typeof value !== 'string') {
            return '';
        }

        const trimmedValue = value.trim();
        const strippedValue = trimmedValue.replace(/\(behalf\)$/i, '').trim();

        return isLikelyUserIdentifier(strippedValue) ? strippedValue : '';
    };

    const resolveShipmentCustomerParticipantAuth0Id = candidateShipment => {
        if (!candidateShipment || typeof candidateShipment !== 'object') {
            return '';
        }

        const customerBlob = candidateShipment.customer_blob && typeof candidateShipment.customer_blob === 'object'
            ? candidateShipment.customer_blob
            : {};

        return normalizeAuth0Candidate(candidateShipment.cust_auth0_id)
            || normalizeAuth0Candidate(candidateShipment.created_by)
            || normalizeAuth0Candidate(candidateShipment.placed_by)
            || normalizeAuth0Candidate(customerBlob.auth0_id)
            || '';
    };

    const getShipmentCustomerDisplayName = candidateShipment => {
        const customerBlob = candidateShipment && candidateShipment.customer_blob && typeof candidateShipment.customer_blob === 'object'
            ? candidateShipment.customer_blob
            : {};
        const firstName = customerBlob.first_name || '';
        const lastName = customerBlob.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();

        return fullName || candidateShipment.placed_by || 'Customer';
    };

    const getPrimaryRouteDriver = shipment => {
        if (!shipment || !shipment.Route || !Array.isArray(shipment.Route.RouteDrivers)) {
            return null;
        }

        return shipment.Route.RouteDrivers[0] || null;
    };

    const buildDispatchRouteStatusFromShipment = useCallback(shipment => {
        const routeStatus = shipment && shipment.Route && shipment.Route.status ? String(shipment.Route.status).trim() : '';

        if (routeStatus) {
            return routeStatus;
        }

        const shipmentStatus = shipment && shipment.status ? String(shipment.status).trim() : '';

        if (shipmentStatus === 'shipment_completed' || shipmentStatus === 'shipment_pending_review') {
            return 'route_completed';
        }

        if (shipmentStatus === 'shipment_enroute') {
            return 'route_enroute';
        }

        if (shipmentStatus === 'shipment_claimed') {
            return 'route_claimed';
        }

        if (shipmentStatus === 'shipment_assigned' || shipmentStatus === 'shipment_ready_for_driver') {
            return 'route_assigned';
        }

        return 'route_assigned';
    }, []);

    const buildDispatchMapRouteFromShipment = useCallback(shipment => {
        if (!shipment || !Array.isArray(shipment.Orders) || shipment.Orders.length === 0) {
            return null;
        }

        const path = shipment.Orders.map((order, index) => {
            const location = order && order.location_blob ? order.location_blob : null;
            const latitude = location && location.lat != null ? Number(location.lat) : Number.NaN;
            const longitude = location && location.lng != null ? Number(location.lng) : Number.NaN;

            if (!location || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                return null;
            }

            const normalizedActions = {};

            if (order.actions && order.actions.pick_up) {
                normalizedActions.pick_up = {
                    item: Array.isArray(order.actions.pick_up) ? order.actions.pick_up : [order.actions.pick_up],
                };
            }

            if (order.actions && order.actions.drop_off) {
                normalizedActions.drop_off = {
                    item: Array.isArray(order.actions.drop_off) ? order.actions.drop_off : [order.actions.drop_off],
                };
            }

            return {
                waypoint_id: order.order_id || order.order_friendly_id || `${shipment.shipment_id || 'shipment'}-${index}`,
                stop_id: order.order_id || order.order_friendly_id || null,
                ship_id: shipment.shipment_friendly_id,
                shipment_id: shipment.shipment_id,
                completed_at: getOrderCompletionTimestampForDisplay(order),
                is_waypoint_completed: isOrderCompletedForDisplay(order),
                actions: normalizedActions,
                address: {
                    lat: latitude,
                    lng: longitude,
                    name: location.name || '',
                    address_1: location.address_1 || '',
                },
            };
        }).filter(Boolean);

        if (path.length === 0) {
            return null;
        }

        const coordinates = path.map(waypoint => waypoint.address);
        const latitudes = coordinates.map(point => point.lat);
        const longitudes = coordinates.map(point => point.lng);
        const routeDrivers = shipment.Route && Array.isArray(shipment.Route.RouteDrivers)
            ? shipment.Route.RouteDrivers.map(enrichRouteDriverVehicleBlob)
            : [];

        return {
            route_id: shipment.route_id || (shipment.Route && shipment.Route.route_id) || shipment.shipment_id,
            route_friendly_id: shipment.route_friendly_id || (shipment.Route && shipment.Route.route_friendly_id) || shipment.shipment_friendly_id,
            status: buildDispatchRouteStatusFromShipment(shipment),
            completed_at: getShipmentCompletionTimestampForDisplay(shipment),
            path,
            bounds: {
                min_lat: Math.min(...latitudes),
                min_lng: Math.min(...longitudes),
                max_lat: Math.max(...latitudes),
                max_lng: Math.max(...longitudes),
            },
            Shipments: [shipment],
            RouteDrivers: routeDrivers,
            LocationUpdates: Array.isArray(shipment.LocationUpdates) ? shipment.LocationUpdates : [],
            driven_route: shipment.Route && shipment.Route.driven_route ? shipment.Route.driven_route : null,
        };
    }, [buildDispatchRouteStatusFromShipment]);

    const merchantDispatchSelectedRoute = useMemo(
        () => buildDispatchMapRouteFromShipment(shipmentClick),
        [buildDispatchMapRouteFromShipment, shipmentClick]
    );

    const merchantDispatchFocusedDriver = useMemo(() => {
        if (merchantDispatchSelectedRoute && Array.isArray(merchantDispatchSelectedRoute.RouteDrivers) && merchantDispatchSelectedRoute.RouteDrivers.length > 0) {
            return merchantDispatchSelectedRoute.RouteDrivers[0];
        }

        return getPrimaryRouteDriver(shipmentClick);
    }, [merchantDispatchSelectedRoute, shipmentClick]);

    useEffect(() => {
        setReadyRoutes(currentRoutes => (currentRoutes.length === 0 ? currentRoutes : []));
        setDeliveringRoutes(currentRoutes => (currentRoutes.length === 0 ? currentRoutes : []));
        setAvailableDrivers(currentDrivers => (currentDrivers.length === 0 ? currentDrivers : []));
        setOfflineDrivers(currentDrivers => (currentDrivers.length === 0 ? currentDrivers : []));
        setEnrouteDrivers(currentDrivers => {
            const nextDrivers = Array.isArray(driversEnroute) ? driversEnroute : [];
            return areDriversEnrouteEqual(currentDrivers, nextDrivers) ? currentDrivers : nextDrivers;
        });

        if (!merchantDispatchSelectedRoute) {
            setRouteClick(null);
            setEnrouteClickDriver(null);
            return;
        }

        setRouteClick(merchantDispatchSelectedRoute);
        setEnrouteClickDriver(merchantDispatchFocusedDriver || null);
    }, [
        driversEnroute,
        merchantDispatchFocusedDriver,
        merchantDispatchSelectedRoute,
        setAvailableDrivers,
        setDeliveringRoutes,
        setEnrouteClickDriver,
        setEnrouteDrivers,
        setOfflineDrivers,
        setReadyRoutes,
        setRouteClick,
    ]);

    useEffect(() => {
        return () => {
            setRouteClick(null);
            setEnrouteClickDriver(null);
            setReadyRoutes(currentRoutes => (currentRoutes.length === 0 ? currentRoutes : []));
            setDeliveringRoutes(currentRoutes => (currentRoutes.length === 0 ? currentRoutes : []));
            setAvailableDrivers(currentDrivers => (currentDrivers.length === 0 ? currentDrivers : []));
            setEnrouteDrivers(currentDrivers => (currentDrivers.length === 0 ? currentDrivers : []));
            setOfflineDrivers(currentDrivers => (currentDrivers.length === 0 ? currentDrivers : []));
        };
    }, [
        setAvailableDrivers,
        setDeliveringRoutes,
        setEnrouteClickDriver,
        setEnrouteDrivers,
        setOfflineDrivers,
        setReadyRoutes,
        setRouteClick,
    ]);

    const getShipmentDriverDisplayName = candidateShipment => {
        const routeDriver = getPrimaryRouteDriver(candidateShipment);
        const driverBlob = routeDriver && routeDriver.driver_blob ? routeDriver.driver_blob : null;

        if (!driverBlob) {
            return 'Driver';
        }

        return `${driverBlob.first_name || ''} ${driverBlob.last_name || ''}`.trim() || 'Driver';
    };

    const getMerchantChatDisplayName = contactInfo => {
        if (!contactInfo) {
            return 'Unknown Contact';
        }

        if (contactInfo.chatDisplayName) {
            return contactInfo.chatDisplayName;
        }

        const fallbackName = `${contactInfo.firstname || ''} ${contactInfo.lastname || ''}`.trim();
        return fallbackName || 'Unknown Contact';
    };

    const getMerchantChatNameInitials = displayName => String(displayName || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(namePart => namePart.charAt(0).toUpperCase())
        .join('') || 'C';

    const getMerchantChatTitle = contactInfo => {
        const role = contactInfo && contactInfo.chatRole ? contactInfo.chatRole : '';

        if (role === 'Dispatch Chat') {
            return 'Dispatch Chat';
        }

        if (role === 'Customer Driver') {
            return 'Customer / Driver Chat';
        }

        return 'Chat';
    };

    const resetOrderChatComposer = () => {
        setOrderChatMessage('');
        setOrderChatMessages([]);
        setOrderChatRecipientInfo({});
    };

    const markClaimedOrderChatSeen = (threadKey, messageId) => {
        if (!threadKey || !messageId) {
            return;
        }

        setClaimedOrderChatUnreadCountsByThreadId(currentValue => {
            if (!currentValue[threadKey]) {
                return currentValue;
            }

            return {
                ...currentValue,
                [threadKey]: 0,
            };
        });

        setClaimedOrderChatSeenByThreadId(currentValue => {
            if (currentValue[threadKey] === messageId) {
                return currentValue;
            }

            const nextValue = {
                ...currentValue,
                [threadKey]: messageId,
            };

            persistMerchantClaimedOrderChatSeenMap(merchantClaimedOrderChatSeenScope, nextValue);
            return nextValue;
        });
    };

    const toggleOrderChatModal = () => {
        if (showOrderChatModal) {
            resetOrderChatComposer();
        }

        setShowOrderChatModal(!showOrderChatModal);
    };

    const retrieveOrderChatMessages = recipientInfo => {
        if (!(recipientInfo && recipientInfo.chatShipmentId)) {
            setOrderChatMessages([]);
            return;
        }

        axios({
            method: 'get',
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                'content-type': 'application/json'
            },
            url: `${claimedOrderChatEndpoint}?shipment_id=${encodeURIComponent(recipientInfo.chatShipmentId)}${getClaimedOrderChatThreadTypeParam(recipientInfo.chatThreadType) ? `&thread_type=${encodeURIComponent(getClaimedOrderChatThreadTypeParam(recipientInfo.chatThreadType))}` : ''}${recipientInfo.chatParticipantAuth0Id ? `&participant_auth0_id=${encodeURIComponent(recipientInfo.chatParticipantAuth0Id)}` : ''}`
        })
            .then(response => {
                if (response.data.status === 'successful') {
                    const payload = response.data.payload || {};
                    const messages = Array.isArray(payload.messages) ? payload.messages : [];
                    const latestMessage = messages.length > 0 ? messages[messages.length - 1] : null;
                    const payloadThread = payload && payload.thread ? payload.thread : null;
                    const threadKey = recipientInfo.chatThreadId || buildClaimedOrderThreadKey(payloadThread || {
                        shipment_id: recipientInfo.chatShipmentId,
                        thread_type: recipientInfo.chatThreadType,
                        participant_auth0_id: recipientInfo.chatParticipantAuth0Id,
                    });

                    if (latestMessage && latestMessage.message_id && threadKey) {
                        markClaimedOrderChatSeen(threadKey, latestMessage.message_id);
                    }

                    setOrderChatMessages(currentMessages => (
                        buildChatThreadSignature(currentMessages) === buildChatThreadSignature(messages)
                            ? currentMessages
                            : messages
                    ));
                } else {
                    setOrderChatMessages([]);
                }
            })
            .catch(() => {
                setOrderChatMessages([]);
            });
    };

    const sendOrderChatMessage = recipientInfo => {
        const trimmedMessage = String(orderChatMessage || '').trim();

        if (!(recipientInfo && recipientInfo.chatShipmentId)) {
            props.notify('tc', 'Failed to load the chat thread.', 'danger');
            return;
        }

        if (!trimmedMessage) {
            props.notify('tc', 'Message is required');
            return;
        }

        axios({
            method: 'post',
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                'content-type': 'application/json'
            },
            url: claimedOrderChatEndpoint,
            data: {
                shipment_id: recipientInfo.chatShipmentId,
                ...(getClaimedOrderChatThreadTypeParam(recipientInfo.chatThreadType) ? { thread_type: getClaimedOrderChatThreadTypeParam(recipientInfo.chatThreadType) } : {}),
                ...(recipientInfo.chatParticipantAuth0Id ? { participant_auth0_id: recipientInfo.chatParticipantAuth0Id } : {}),
                body: trimmedMessage,
            }
        })
            .then(response => {
                const createdMessage = response && response.data && response.data.payload ? response.data.payload : null;

                if (!createdMessage) {
                    props.notify('tc', 'Failed to send the message');
                    return;
                }

                setOrderChatMessages(currentMessages => [...currentMessages, createdMessage]);
                setOrderChatMessage('');
            })
            .catch(error => {
                const errorMessage =
                    (error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
                    (error && error.message) ||
                    'Failed to send the message';
                props.notify('tc', errorMessage, 'danger');
            });
    };

    const openShipmentThreadChat = (candidateShipment, chatThreadType) => {
        if (!(candidateShipment && candidateShipment.shipment_id) || !isCustomerVisibleClaimedOrderThreadType(chatThreadType)) {
            return;
        }

        const customerDisplayName = getShipmentCustomerDisplayName(candidateShipment);
        const driverDisplayName = getShipmentDriverDisplayName(candidateShipment);
        const chatParticipantAuth0Id = resolveShipmentCustomerParticipantAuth0Id(candidateShipment);
        const nextRecipientInfo = {
            chatMode: 'order-thread',
            chatDisplayName: chatThreadType === 'customer_driver' ? driverDisplayName : 'Dispatch',
            chatRole: chatThreadType === 'customer_driver' ? 'Customer Driver' : 'Dispatch Chat',
            chatShipmentId: candidateShipment.shipment_id,
            chatShipmentFriendlyId: candidateShipment.shipment_friendly_id || '',
            chatThreadType,
            chatThreadId: buildClaimedOrderThreadKey({
                shipment_id: candidateShipment.shipment_id,
                thread_type: chatThreadType,
                participant_auth0_id: chatParticipantAuth0Id,
            }),
            chatParticipantAuth0Id,
            chatAdminDisplayName: 'Dispatch',
            chatCustomerDisplayName: customerDisplayName,
            chatDriverDisplayName: driverDisplayName,
        };

        setOrderChatRecipientInfo(nextRecipientInfo);
        setShowOrderChatModal(true);
        retrieveOrderChatMessages(nextRecipientInfo);
    };

    const getClaimedOrderChatUnreadCountForThread = ({ shipmentId = '', threadType = 'customer_driver', participantAuth0Id = '' } = {}) => {
        const threadKey = buildClaimedOrderThreadKey({
            shipment_id: shipmentId,
            thread_type: threadType,
            participant_auth0_id: participantAuth0Id,
        });

        if (!threadKey) {
            return 0;
        }

        return Number(claimedOrderChatUnreadCountsByThreadId[threadKey]) || 0;
    };

    const getClaimedOrderChatUnreadCountForShipment = shipment => {
        if (!shipment || !shipment.shipment_id) {
            return 0;
        }

        const participantAuth0Id = resolveShipmentCustomerParticipantAuth0Id(shipment);

        return CUSTOMER_VISIBLE_CLAIMED_ORDER_CHAT_THREAD_TYPES.reduce(
            (total, threadType) => total + getClaimedOrderChatUnreadCountForThread({
                shipmentId: shipment.shipment_id,
                threadType,
                participantAuth0Id,
            }),
            0
        );
    };

    const hasUnreadClaimedOrderChatForShipment = shipment => (
        Boolean(shipment)
        && MERCHANT_IN_PROGRESS_SHIPMENT_STATUSES.includes(shipment.status)
        && getClaimedOrderChatUnreadCountForShipment(shipment) > 0
    );

    const shipmentBelongsToStore = (candidateShipment, storeIds, auth0Id, userEmail, pinnedShipmentId) => {
        if (!candidateShipment) {
            return false;
        }

        if (pinnedShipmentId && candidateShipment.shipment_id === pinnedShipmentId) {
            return true;
        }

        const normalizedStoreIds = normalizeStoreIds(storeIds);
        const shipmentLevelStoreId =
            (candidateShipment.customer_blob && candidateShipment.customer_blob.store_id != null ? String(candidateShipment.customer_blob.store_id) : '') ||
            (candidateShipment.shipping_fee_blob && candidateShipment.shipping_fee_blob.shipment && candidateShipment.shipping_fee_blob.shipment.store_id != null
                ? String(candidateShipment.shipping_fee_blob.shipment.store_id)
                : '');

        if (normalizedStoreIds.length > 0 && shipmentLevelStoreId && normalizedStoreIds.includes(shipmentLevelStoreId)) {
            return true;
        }

        const belongsToStore =
            normalizedStoreIds.length > 0 &&
            Array.isArray(candidateShipment.Orders) &&
            candidateShipment.Orders.some(orderRecord =>
                orderRecord &&
                orderRecord.store_id != null &&
                normalizedStoreIds.includes(String(orderRecord.store_id))
            );

        if (belongsToStore) {
            return true;
        }

        // When store scope is known, keep filtering strictly by store membership.
        // Falling back to user identity here causes associate users to see different
        // subsets of the same customer store's shipments.
        if (normalizedStoreIds.length > 0) {
            return false;
        }

        const customerBlob = candidateShipment.customer_blob && typeof candidateShipment.customer_blob === 'object'
            ? candidateShipment.customer_blob
            : {};

        const resolvedShipmentAuth0Id = normalizeAuth0Candidate(candidateShipment.cust_auth0_id)
            || normalizeAuth0Candidate(candidateShipment.created_by)
            || normalizeAuth0Candidate(candidateShipment.placed_by)
            || normalizeAuth0Candidate(customerBlob.auth0_id)
            || '';

        if (Boolean(auth0Id) && normalizeAuth0Candidate(candidateShipment.created_by) === auth0Id) {
            return true;
        }

        if (Boolean(auth0Id) && normalizeAuth0Candidate(candidateShipment.cust_auth0_id) === auth0Id) {
            return true;
        }

        if (Boolean(auth0Id) && resolvedShipmentAuth0Id === auth0Id) {
            return true;
        }

        const normalizedUserEmail = normalizeEmailCandidate(userEmail);
        if (
            normalizedUserEmail &&
            (
                normalizeEmailCandidate(candidateShipment.placed_by) === normalizedUserEmail ||
                normalizeEmailCandidate(candidateShipment.created_by) === normalizedUserEmail
            )
        ) {
            return true;
        }

        return false;
    };

    const getCurrentUserEmail = () => {
        const accessPayload = decodeJwtPayload(localStorage.getItem('access_token'));
        if (accessPayload && accessPayload.email) {
            return normalizeEmailCandidate(accessPayload.email);
        }

        const idPayload = decodeJwtPayload(localStorage.getItem('id_token'));
        return normalizeEmailCandidate(idPayload && idPayload.email);
    };

    const resolveStoreIdsFromJwtPayload = payload => {
        if (!payload || typeof payload !== 'object') {
            return [];
        }

        const directCandidates = [
            payload.store_id,
            payload.storeId,
            payload.merchant_store_id,
            payload.merchantStoreId,
            ...(Array.isArray(payload.store_ids) ? payload.store_ids : []),
            ...(Array.isArray(payload.storeIds) ? payload.storeIds : []),
            ...(Array.isArray(payload.merchant_store_ids) ? payload.merchant_store_ids : []),
            ...(Array.isArray(payload.merchantStoreIds) ? payload.merchantStoreIds : []),
        ];

        const namespacedCandidates = Object.keys(payload)
            .filter(key => key.includes('store_id') || key.includes('store_ids') || key.includes('merchant_store'))
            .flatMap(key => {
                const value = payload[key];
                return Array.isArray(value) ? value : [value];
            });

        return normalizeStoreIds([...directCandidates, ...namespacedCandidates]);
    };

    useEffect(() => {
        if (!showOrderChatModal || !orderChatRecipientInfo || !orderChatRecipientInfo.chatShipmentId) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            retrieveOrderChatMessages(orderChatRecipientInfo);
        }, OPEN_CHAT_THREAD_POLL_INTERVAL_MS);

        return () => window.clearInterval(intervalId);
    }, [showOrderChatModal, orderChatRecipientInfo]);

    const renderTabCountChip = (count, options = {}) => {
        if (!count || count <= 0) {
            return null;
        }

        const containerClassName = options.warning ? 'approve-amount-container' : 'amount-container';
        const labelClassName = options.warning ? 'approval-amount' : 'total-amount';
        const containerStyle = options.style || undefined;

        return (
            <div className={containerClassName} style={containerStyle}>
                <p className={labelClassName}>{count}</p>
            </div>
        );
    };

    const renderMerchantChatUnreadBadge = (unreadCount, options = {}) => {
        if (!unreadCount || unreadCount <= 0) {
            return null;
        }

        return (
            <span
                style={{
                    position: 'absolute',
                    top: options.top || '-8px',
                    right: options.right || '-10px',
                    minWidth: '18px',
                    height: '18px',
                    borderRadius: '999px',
                    backgroundColor: options.backgroundColor || '#D62828',
                    border: `2px solid ${options.borderColor || '#FFFFFF'}`,
                    color: options.color || '#FFFFFF',
                    fontSize: '10px',
                    fontWeight: 700,
                    lineHeight: '14px',
                    padding: '0 4px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                }}
            >
                {unreadCount > 9 ? '9+' : unreadCount}
            </span>
        );
    };

    const filterShipmentsForStore = (candidateShipments, storeIds, auth0Id, userEmail, pinnedShipmentId) => {
        if (!Array.isArray(candidateShipments)) {
            return [];
        }

        const normalizedStoreIds = normalizeStoreIds(storeIds);

        // If store scope has not resolved yet, keep the server-scoped feed intact.
        // Falling back to user-level checks here hides shared company orders,
        // especially in Completed for associate users.
        if (normalizedStoreIds.length === 0) {
            return candidateShipments;
        }

        return candidateShipments.filter(candidateShipment => shipmentBelongsToStore(candidateShipment, normalizedStoreIds, auth0Id, userEmail, pinnedShipmentId));
    };

    const currentStoreId = storeIdOverride || (store && store.store_id ? store.store_id : '');
    const currentAuth0Id = getCurrentAuth0Id();
    const currentUserEmail = getCurrentUserEmail();
    const currentStoreScopeIds = useMemo(() => normalizeStoreIds([currentStoreId, ...accessibleStoreIds]), [accessibleStoreIds, currentStoreId]);
    const currentStoreScopeIdsKey = useMemo(() => currentStoreScopeIds.join(','), [currentStoreScopeIds]);
    const merchantClaimedOrderChatSeenScope = currentAuth0Id || currentStoreId || 'anonymous';
    const merchantAccessToken = localStorage.getItem('access_token') || '';
    const rawShipments = useMemo(() => (data && Array.isArray(data.Shipments) ? data.Shipments : EMPTY_SHIPMENTS), [data]);
    const rawShipmentCount = rawShipments.length;
    // Only run merchant shipment/bootstrap queries when we have merchant context.
    // Generic customer sessions can still have auth tokens, but should not hit
    // merchant-dashboard endpoints until store scope (or prior merchant shipment
    // context) is present.
    const hasMerchantQueryScope = Boolean(
        currentStoreScopeIds.length > 0
        || currentStoreId
        || (isLocalDashboardEnvironment && (currentAuth0Id || currentUserEmail || merchantAccessToken))
        || lastCreatedShipmentId
        || rawShipments.length > 0
        || fallbackShipments.length > 0
    );
    const hasMerchantShipmentScope = Boolean(
        currentStoreScopeIds.length > 0 ||
        currentStoreId ||
        currentAuth0Id ||
        merchantAccessToken ||
        lastCreatedShipmentId ||
        rawShipments.length > 0 ||
        fallbackShipments.length > 0
    );

    useEffect(() => {
        let cancelled = false;

        if (isLocalDashboardEnvironment) {
            setCompletedSupplementShipments([]);
            return () => {
                cancelled = true;
            };
        }
        const shouldSkipBrowserStoreAccessLookup = process.env.REACT_APP_ENABLE_BROWSER_STORE_ACCESS_LOOKUP !== 'true';

        const resolveAccessibleStoreIds = async () => {
            const fallbackStoreIds = normalizeStoreIds(currentStoreId);
            const accessPayload = decodeJwtPayload(localStorage.getItem('access_token'));
            const idPayload = decodeJwtPayload(localStorage.getItem('id_token'));
            const tokenStoreIds = normalizeStoreIds([
                ...resolveStoreIdsFromJwtPayload(accessPayload),
                ...resolveStoreIdsFromJwtPayload(idPayload),
            ]);
            const fallbackWithTokenStores = normalizeStoreIds([...fallbackStoreIds, ...tokenStoreIds]);

            const applyResolvedStoreIds = nextStoreIds => {
                if (!cancelled) {
                    setAccessibleStoreIds(previousStoreIds => {
                        const normalizedNextStoreIds = normalizeStoreIds(nextStoreIds);

                        return areStoreIdSetsEqual(previousStoreIds, normalizedNextStoreIds)
                            ? previousStoreIds
                            : normalizedNextStoreIds;
                    });
                }
            };

            if (!currentAuth0Id || typeof user_urls.merchantStoreAccess !== 'function') {
                applyResolvedStoreIds(fallbackWithTokenStores);

                return;
            }

            if (shouldSkipBrowserStoreAccessLookup) {
                const hasuraFallbackStoreIds = await resolveStoreIdsFromUsersHasura().catch(() => fallbackWithTokenStores);
                applyResolvedStoreIds(hasuraFallbackStoreIds);
                return;
            }

            async function resolveStoreIdsFromUsersHasura() {
                const response = await axios({
                    method: 'post',
                    url: user_urls.hasuraHost,
                    timeout: getMerchantBootstrapRequestTimeout(user_urls.hasuraHost),
                    headers: {
                        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                        'content-type': 'application/json'
                    },
                    data: {
                        query: `query ResolveMerchantStoreScope($auth0Id: String!, $userEmail: String!) {
                            Users(where: {_or: [{auth0_id: {_eq: $auth0Id}}, {email: {_eq: $userEmail}}]}, limit: 50) {
                                store_id
                            }
                        }`,
                        variables: {
                            auth0Id: currentAuth0Id,
                            userEmail: currentUserEmail || '',
                        }
                    }
                });

                const userRecords = response && response.data && response.data.data && Array.isArray(response.data.data.Users)
                    ? response.data.data.Users
                    : [];
                const storeIdsFromUsers = normalizeStoreIds(userRecords.map(userRecord => userRecord && userRecord.store_id));

                return normalizeStoreIds([...fallbackWithTokenStores, ...storeIdsFromUsers]);
            }

            try {
                const response = await axios({
                    method: 'get',
                    url: user_urls.merchantStoreAccess(currentAuth0Id),
                    withCredentials: true,
                    headers: {
                        'content-type': 'application/json',
                        Authorization: 'Bearer ' + localStorage.getItem('access_token')
                    }
                });

                const payload = response && response.data && response.data.payload ? response.data.payload : {};
                const nextStoreIds = normalizeStoreIds([
                    ...fallbackWithTokenStores,
                    payload && payload.store_id,
                    ...(payload && Array.isArray(payload.store_ids) ? payload.store_ids : []),
                ]);

                if (nextStoreIds.length > 0) {
                    applyResolvedStoreIds(nextStoreIds);
                    return;
                }

                const hasuraFallbackStoreIds = await resolveStoreIdsFromUsersHasura().catch(() => fallbackWithTokenStores);
                applyResolvedStoreIds(hasuraFallbackStoreIds);
            } catch (error) {
                const hasuraFallbackStoreIds = await resolveStoreIdsFromUsersHasura().catch(() => fallbackWithTokenStores);
                applyResolvedStoreIds(hasuraFallbackStoreIds);
            }
        };

        resolveAccessibleStoreIds();

        return () => {
            cancelled = true;
        };
    }, [currentAuth0Id, currentStoreId, currentUserEmail]);

    useEffect(() => {
        const shouldFetchCompletedSupplement = !isLocalDashboardEnvironment || process.env.REACT_APP_ENABLE_LOCAL_COMPLETED_SUPPLEMENT === 'true';

        if (!hasMerchantQueryScope || !shouldFetchCompletedSupplement) {
            if (!shouldFetchCompletedSupplement) {
                setCompletedSupplementShipments([]);
            }
            return;
        }

        let cancelled = false;

        const runCompletedByStoreQuery = async (requestOptions, sourceLabel = 'completed.unknown') => {
            let response = null;

            try {
                response = await axios(requestOptions);

                if (response && response.data && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
                    const firstError = response.data.errors[0];
                    appendMerchantSourceDebug('completed', {
                        source: sourceLabel,
                        url: requestOptions && requestOptions.url,
                        ok: false,
                        message: firstError && firstError.message ? firstError.message : 'GraphQL error',
                    });
                    throw new Error(firstError && firstError.message ? firstError.message : 'Merchant completed query failed');
                }

                const shipments = response && response.data && response.data.data && Array.isArray(response.data.data.Shipments)
                    ? response.data.data.Shipments
                    : [];

                appendMerchantSourceDebug('completed', {
                    source: sourceLabel,
                    url: requestOptions && requestOptions.url,
                    ok: true,
                    count: shipments.length,
                });

                return shipments;
            } catch (error) {
                if (!(response && response.data && Array.isArray(response.data.errors) && response.data.errors.length > 0)) {
                    appendMerchantSourceDebug('completed', {
                        source: sourceLabel,
                        url: requestOptions && requestOptions.url,
                        ok: false,
                        message: error && error.message ? error.message : 'request failed',
                    });
                }

                throw error;
            }
        };

        const fetchCompletedByStore = () => {
            if (completedShipmentsRequestInFlightRef.current) {
                return;
            }

            completedShipmentsRequestInFlightRef.current = true;

            const query = `query MerchantCompletedByStore($storeIds:[uuid!], $auth0Id:String, $userEmail:String, $userEmailPattern:String) {
                        Shipments(
                            order_by: {updated_at: desc}
                            where: {
                                _or: [
                                    {Orders: {store_id: {_in: $storeIds}}}
                                    {cust_auth0_id: {_eq: $auth0Id}}
                                    {placed_by: {_eq: $auth0Id}}
                                    {placed_by: {_eq: $userEmail}}
                                    {placed_by: {_ilike: $userEmailPattern}}
                                ]
                                status: {_eq: "shipment_completed"}
                            }
                            limit: 1000
                        ) {
                            shipment_id
                            shipment_friendly_id
                            cust_auth0_id
                            customer_blob
                            distance
                            shipment_total
                            contains_alcohol
                            shipping_fee
                            sum_of_orders_total
                            weight
                            created_at
                            updated_at
                            description
                            drop_off_instructions
                            pick_up_instructions
                            scheduled_for
                            shipment_type
                            status
                            billing_status
                            vehicle_type
                            is_roundtrip
                            merchant_order_id
                            shipping_fee_blob
                            placed_by
                            completed_at
                            fedex_data
                            ai_recommendation
                            billing_details
                            Orders(order_by: { order_friendly_id: asc }) {
                                order_id
                                shipment_id
                                store_id
                                location_blob
                                order_friendly_id
                                actions
                                status
                                subtotal
                                sum_tax
                                total
                                created_at
                                updated_at
                                pick_up_instructions
                                description
                                weight
                                drop_off_instructions
                                estimated_arrival_time
                                completed_at
                                photos
                                actual_action
                                Items {
                                    item_id
                                    product_blob
                                    quantity
                                    product_price
                                    status
                                    product_id
                                    ItemOptions {
                                        option_name
                                        option_price
                                        variant_name
                                        variant_blob
                                        option_blob
                                    }
                                }
                            }
                            LocationUpdates(order_by: { timestamp: asc }) {
                                latitude
                                longitude
                                timestamp
                            }
                            Route {
                                RouteDrivers(where: { status: { _eq: "driver" }, deleted_at: { _is_null: true } }) {
                                    driver_blob
                                    driver_id
                                    vehicle_blob
                                    tracking_blob
                                }
                            }
                            ShipmentHandshakes {
                                url
                                type
                            }
                        }
                    }`;

            const variables = {
                storeIds: currentStoreScopeIds.length > 0 ? currentStoreScopeIds : [],
                auth0Id: currentAuth0Id || null,
                userEmail: currentUserEmail || null,
                userEmailPattern: currentUserEmail ? `%${currentUserEmail}%` : null,
            };

            const isLocalBrowser = typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost';
            const configuredSecret =
                dashboardConfig &&
                dashboardConfig.config &&
                dashboardConfig.config.api_shipments &&
                dashboardConfig.config.api_shipments.admin &&
                dashboardConfig.config.api_shipments.admin.shipmentsHasuraAdminSecret
                    ? String(dashboardConfig.config.api_shipments.admin.shipmentsHasuraAdminSecret)
                    : '';
            const envSecret =
                typeof process !== 'undefined' && process.env && process.env.REACT_APP_SHIPMENTS_HASURA_ADMIN_SECRET
                    ? String(process.env.REACT_APP_SHIPMENTS_HASURA_ADMIN_SECRET)
                    : '';
            const localShipmentsHasuraAdminSecret = (configuredSecret || envSecret).trim().replace(/^"|"$/g, '');
            const productionShipmentsHasuraHost = 'https://api-shipments.hasura.gophr.app/v1/graphql';

            const bearerRequest = {
                method: 'post',
                url: shipment_urls.hasuraHost,
                timeout: getMerchantBootstrapRequestTimeout(shipment_urls.hasuraHost),
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                    'content-type': 'application/json'
                },
                data: {
                    query,
                    variables,
                }
            };

            const bearerRequestProduction = {
                method: 'post',
                url: productionShipmentsHasuraHost,
                timeout: getMerchantBootstrapRequestTimeout(productionShipmentsHasuraHost),
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                    'content-type': 'application/json'
                },
                data: {
                    query,
                    variables,
                }
            };

            const adminSecretRequest = {
                method: 'post',
                url: shipment_urls.hasuraHost,
                timeout: getMerchantBootstrapRequestTimeout(shipment_urls.hasuraHost),
                headers: {
                    'x-hasura-admin-secret': localShipmentsHasuraAdminSecret,
                    'content-type': 'application/json',
                },
                data: {
                    query,
                    variables,
                }
            };

            const proxyRequest = {
                method: 'post',
                url: '/__local/shipments-hasura',
                timeout: 2000,
                headers: {
                    'content-type': 'application/json'
                },
                data: {
                    query,
                    variables,
                }
            };

            const runBearerWithProductionFallback = () =>
                runCompletedByStoreQuery(bearerRequest, 'completed.bearer.configured')
                    .then(shipments => {
                        if (Array.isArray(shipments) && shipments.length > 0) {
                            return shipments;
                        }

                        if (bearerRequest.url === bearerRequestProduction.url) {
                            return shipments;
                        }

                        return runCompletedByStoreQuery(bearerRequestProduction, 'completed.bearer.production').catch(() => shipments);
                    })
                    .catch(() => runCompletedByStoreQuery(bearerRequestProduction, 'completed.bearer.production'));

            const runAdminSecretWithBearerFallback = () =>
                runCompletedByStoreQuery(adminSecretRequest, 'completed.admin-secret.configured')
                    .then(shipments => {
                        if (Array.isArray(shipments) && shipments.length > 0) {
                            return shipments;
                        }

                        return runBearerWithProductionFallback();
                    })
                    .catch(() => runBearerWithProductionFallback());

            const requestPromise = isLocalBrowser
                ? (localShipmentsHasuraAdminSecret
                    ? runAdminSecretWithBearerFallback()
                    : runBearerWithProductionFallback())
                    .then(shipments => {
                        if (Array.isArray(shipments) && shipments.length > 0) {
                            return shipments;
                        }

                        return runCompletedByStoreQuery(proxyRequest, 'completed.proxy.local').catch(() => shipments);
                    })
                    .catch(() => runCompletedByStoreQuery(proxyRequest, 'completed.proxy.local').catch(() => []))
                : runBearerWithProductionFallback();

            requestPromise
                .then(shipments => {
                    if (cancelled) {
                        return;
                    }

                    setCompletedSupplementShipments(current => {
                        const nextShipments = Array.isArray(shipments) ? shipments : [];

                        if (nextShipments.length === 0 && Array.isArray(current) && current.length > 0) {
                            return current;
                        }

                        return areShipmentsEqualById(current, nextShipments) ? current : nextShipments;
                    });
                })
                .catch(() => {
                    if (!cancelled) {
                        setCompletedSupplementShipments(current => (Array.isArray(current) && current.length > 0 ? current : []));
                    }
                })
                .finally(() => {
                    completedShipmentsRequestInFlightRef.current = false;
                });
        };

        fetchCompletedByStore();

        return () => {
            cancelled = true;
            completedShipmentsRequestInFlightRef.current = false;
        };
    }, [currentStoreScopeIdsKey, currentAuth0Id, currentUserEmail, hasMerchantQueryScope, isLocalDashboardEnvironment]);
    const effectiveRawShipments = useMemo(() => {
        return rawShipments.length > 0 ? rawShipments : fallbackShipments;
    }, [fallbackShipments, rawShipments]);
    const merchantScopedRawShipments = useMemo(() => {
        return isLocalDashboardEnvironment
            ? rawShipments
            : filterShipmentsForStore(rawShipments, currentStoreScopeIds, currentAuth0Id, currentUserEmail, lastCreatedShipmentId);
    }, [currentAuth0Id, currentStoreScopeIds, currentUserEmail, isLocalDashboardEnvironment, lastCreatedShipmentId, rawShipments]);
    const merchantScopedFallbackShipments = useMemo(() => {
        // The merchant-dashboard fallback endpoint is already store-scoped server-side.
        // Re-filtering it client-side drops valid company shipments when legacy order
        // rows still carry stale store ids or were placed by a sibling merchant account.
        return fallbackShipments;
    }, [fallbackShipments]);
    const effectiveMerchantScopedShipments = useMemo(() => {
        if (merchantScopedRawShipments.length === 0) {
            const mergedWithCompleted = [...merchantScopedFallbackShipments];

            completedSupplementShipments.forEach(candidateShipment => {
                if (!candidateShipment || !candidateShipment.shipment_id) {
                    return;
                }

                if (mergedWithCompleted.some(existingShipment => existingShipment && existingShipment.shipment_id === candidateShipment.shipment_id)) {
                    return;
                }

                mergedWithCompleted.push(candidateShipment);
            });

            return mergedWithCompleted;
        }
        const mergedShipments = [...merchantScopedRawShipments];

        if (merchantScopedFallbackShipments.length > 0) {
            merchantScopedFallbackShipments.forEach(candidateShipment => {
                if (!candidateShipment || !candidateShipment.shipment_id) {
                    return;
                }

                if (mergedShipments.some(existingShipment => existingShipment && existingShipment.shipment_id === candidateShipment.shipment_id)) {
                    return;
                }

                mergedShipments.push(candidateShipment);
            });
        }

        completedSupplementShipments.forEach(candidateShipment => {
            if (!candidateShipment || !candidateShipment.shipment_id) {
                return;
            }

            if (mergedShipments.some(existingShipment => existingShipment && existingShipment.shipment_id === candidateShipment.shipment_id)) {
                return;
            }

            mergedShipments.push(candidateShipment);
        });

        return mergedShipments;
    }, [completedSupplementShipments, merchantScopedFallbackShipments, merchantScopedRawShipments]);

    const recentCreatedShipments = useMemo(() => {
        return recentCreatedSnapshots
            .map(snapshot => {
                const shipment = snapshot && snapshot.shipment ? snapshot.shipment : null;

                if (!shipment) {
                    return null;
                }

                return withActionHost(
                    normalizeShipmentForDisplay(shipment),
                    (shipment && shipment.__actionHost) || shipment_urls.shipmentHost
                );
            })
            .filter(candidateShipment => {
                if (!candidateShipment) {
                    return false;
                }

                if (deletedShipmentIds.includes(candidateShipment.shipment_id)) {
                    return false;
                }

                return shipmentBelongsToStore(candidateShipment, currentStoreScopeIds, currentAuth0Id, currentUserEmail, candidateShipment.shipment_id);
            });
    }, [currentAuth0Id, currentStoreScopeIds, currentUserEmail, deletedShipmentIds, recentCreatedSnapshots]);

    const visibleShipments = useMemo(() => {
        const merchantScopedShipments = effectiveMerchantScopedShipments;

        const dedupedShipmentsById = new Map();

        merchantScopedShipments.forEach(candidateShipment => {
            if (!candidateShipment || !candidateShipment.shipment_id) {
                return;
            }

            const existingShipment = dedupedShipmentsById.get(candidateShipment.shipment_id);
            dedupedShipmentsById.set(
                candidateShipment.shipment_id,
                preferFreshShipmentWithTrackingHistory(existingShipment, candidateShipment)
            );
        });

        const filtered = Array.from(dedupedShipmentsById.values())
            .map(normalizeShipmentForDisplay)
            .filter(candidateShipment => !deletedShipmentIds.includes(candidateShipment.shipment_id));

        const mergedShipments = [...filtered];
        const queuedShipments = [];

        if (hydratedLastCreatedShipment && !deletedShipmentIds.includes(hydratedLastCreatedShipment.shipment_id)) {
            queuedShipments.push(normalizeShipmentForDisplay(hydratedLastCreatedShipment));
        }

        recentCreatedShipments.forEach(candidateShipment => {
            queuedShipments.push(candidateShipment);
        });

        queuedShipments.forEach(candidateShipment => {
            if (!candidateShipment || !candidateShipment.shipment_id) {
                return;
            }

            if (mergedShipments.some(existingShipment => existingShipment.shipment_id === candidateShipment.shipment_id)) {
                return;
            }

            mergedShipments.unshift(candidateShipment);
        });

        return mergedShipments;
    }, [effectiveMerchantScopedShipments, hydratedLastCreatedShipment, recentCreatedShipments, deletedShipmentIds]);

    const completedShipmentReviewScopeKey = useMemo(() => {
        return visibleShipments
            .filter(candidateShipment => {
                if (!candidateShipment) {
                    return false;
                }
                if (isShipmentCompletedForDisplay(candidateShipment)) {
                    return true;
                }
                // shipment_pending_review is reviewable (in CUSTOMER_DRIVER_REVIEWABLE_SHIPMENT_STATUSES)
                // and remains in the subscription, so include it to trigger the pending reviews fetch.
                const normalizedStatus = typeof candidateShipment.status === 'string' ? candidateShipment.status.trim().toLowerCase() : '';
                return normalizedStatus === 'shipment_pending_review';
            })
            .map(candidateShipment => candidateShipment.shipment_id)
            .filter(Boolean)
            .sort()
            .join(',');
    }, [visibleShipments]);
    const shouldForceLocalTestCustomerDriverReview = isLocalDashboardEnvironment || isLocalhostBrowser();

    // Paid-feature gates for this merchant: volume pricing is Enterprise-only;
    // Go-Pilot reports are Pro+.
    const merchantEntitlements = useEntitlements();
    const volumePricingUnlocked = Boolean(merchantEntitlements && merchantEntitlements.features && merchantEntitlements.features.volume_pricing);
    const goPilotReportLocked = Boolean(merchantEntitlements && merchantEntitlements.features && merchantEntitlements.features.go_pilot_report === false);

    // Compute current-month spend from shipments visible in the table (no extra API call).
    // Uses completed_at when available so cross-month orders count correctly.
    const volumeDiscountMonthlySpend = useMemo(
        () => computeMonthlySpendFromShipments(visibleShipments),
        [visibleShipments]
    );

    const localTestCustomerDriverReview = useMemo(() => {
        if (!shouldForceLocalTestCustomerDriverReview) {
            return null;
        }

        const targetShipment = visibleShipments.find(candidateShipment => (
            candidateShipment
            && candidateShipment.shipment_id
            && Number(candidateShipment.shipment_friendly_id) === LOCAL_TEST_CUSTOMER_DRIVER_REVIEW_FRIENDLY_ID
            && !hiddenLocalTestCustomerDriverReviewShipmentIds.includes(candidateShipment.shipment_id)
        ));

        if (!targetShipment) {
            return null;
        }

        return normalizeCustomerDriverReviewPrompt({
            shipment_id: targetShipment.shipment_id,
            shipment_friendly_id: targetShipment.shipment_friendly_id,
            route_id: targetShipment.route_id || null,
            completed_at: targetShipment.completed_at || null,
            description: targetShipment.description || 'Integrity Holdings test review prompt',
            driver_name: getShipmentDriverDisplayName(targetShipment),
            customer_blob: targetShipment.customer_blob || null,
            driver_blob: (getPrimaryRouteDriver(targetShipment) && getPrimaryRouteDriver(targetShipment).driver_blob) || null,
            driver_auth0_id: (getPrimaryRouteDriver(targetShipment) && getPrimaryRouteDriver(targetShipment).driver_auth0_id) || null,
            driver_id: (getPrimaryRouteDriver(targetShipment) && getPrimaryRouteDriver(targetShipment).driver_id) || null,
        });
    }, [hiddenLocalTestCustomerDriverReviewShipmentIds, shouldForceLocalTestCustomerDriverReview, visibleShipments]);
    const effectivePendingCustomerDriverReviews = useMemo(() => {
        if (!localTestCustomerDriverReview) {
            return pendingCustomerDriverReviews;
        }

        if (pendingCustomerDriverReviews.some(review => review && review.shipment_id === localTestCustomerDriverReview.shipment_id)) {
            return pendingCustomerDriverReviews;
        }

        return [localTestCustomerDriverReview, ...pendingCustomerDriverReviews];
    }, [localTestCustomerDriverReview, pendingCustomerDriverReviews]);
    const displayedPendingCustomerDriverReviews = useMemo(() => effectivePendingCustomerDriverReviews.slice(0, 1), [effectivePendingCustomerDriverReviews]);
    const activeCustomerDriverReview = useMemo(() => {
        if (!customerDriverReviewModalShipmentId) {
            return null;
        }

        return effectivePendingCustomerDriverReviews.find(review => review && review.shipment_id === customerDriverReviewModalShipmentId) || null;
    }, [customerDriverReviewModalShipmentId, effectivePendingCustomerDriverReviews]);

    const getCustomerDriverReviewDraft = useCallback(
        shipmentId => customerDriverReviewDrafts[shipmentId] || createEmptyCustomerDriverReviewDraft(),
        [customerDriverReviewDrafts]
    );

    const setCustomerDriverReviewMessage = useCallback((shipmentId, message) => {
        setCustomerDriverReviewMessages(currentMessages => {
            if (!shipmentId) {
                return currentMessages;
            }

            if (!message) {
                if (!Object.prototype.hasOwnProperty.call(currentMessages, shipmentId)) {
                    return currentMessages;
                }

                const nextMessages = { ...currentMessages };
                delete nextMessages[shipmentId];
                return nextMessages;
            }

            if (currentMessages[shipmentId] === message) {
                return currentMessages;
            }

            return {
                ...currentMessages,
                [shipmentId]: message,
            };
        });
    }, []);

    const updateCustomerDriverReviewDraft = useCallback((shipmentId, updates) => {
        if (!shipmentId) {
            return;
        }

        setCustomerDriverReviewDrafts(currentDrafts => {
            const previousDraft = currentDrafts[shipmentId] || createEmptyCustomerDriverReviewDraft();
            const nextDraft = {
                ...previousDraft,
                ...updates,
            };

            if (previousDraft.rating === nextDraft.rating && previousDraft.reviewText === nextDraft.reviewText) {
                return currentDrafts;
            }

            return {
                ...currentDrafts,
                [shipmentId]: nextDraft,
            };
        });

        setCustomerDriverReviewMessage(shipmentId, '');
    }, [setCustomerDriverReviewMessage]);

    const dismissCustomerDriverReviewModal = useCallback(shipmentId => {
        if (shipmentId) {
            autoPromptedCustomerDriverReviewShipmentIdsRef.current.add(shipmentId);
        }

        setCustomerDriverReviewModalShipmentId(null);
    }, []);

    const openCustomerDriverReviewModal = useCallback(shipmentId => {
        if (!shipmentId) {
            return;
        }

        autoPromptedCustomerDriverReviewShipmentIdsRef.current.add(shipmentId);
        setCustomerDriverReviewModalShipmentId(shipmentId);
    }, []);

    const submitCustomerDriverReview = useCallback(async shipmentId => {
        const currentDraft = getCustomerDriverReviewDraft(shipmentId);
        const rating = Number(currentDraft.rating || 0);
        const activeReview = effectivePendingCustomerDriverReviews.find(review => review && review.shipment_id === shipmentId) || null;
        const submittedReviewSnapshot = buildSubmittedCustomerDriverReviewSnapshot({
            shipmentId,
            shipmentFriendlyId: activeReview && activeReview.shipment_friendly_id,
            driverAuth0Id: activeReview && activeReview.driver_auth0_id,
            driverName: activeReview && activeReview.driver_name,
            rating,
            reviewText: currentDraft.reviewText.trim().slice(0, CUSTOMER_DRIVER_REVIEW_MAX_LENGTH),
        });

        if (!rating) {
            setCustomerDriverReviewMessage(shipmentId, 'Please select a star rating before submitting your review.');
            return;
        }

        setSubmittingCustomerDriverReviewShipmentId(shipmentId);
        setCustomerDriverReviewMessage(shipmentId, '');

        try {
            await runMerchantRequestWithSessionRecovery(() =>
                axios({
                    method: 'post',
                    url: shipment_urls.submitCustomerDriverReview(shipmentId),
                    headers: {
                        Authorization: buildAuthorizationHeader(),
                        'content-type': 'application/json',
                    },
                    data: {
                        rating,
                        review_text: submittedReviewSnapshot.reviewText,
                    },
                })
            );

            if (isLocalhostBrowser()) {
                saveStoredCustomerDriverReview(submittedReviewSnapshot);
                setCustomerDriverReviewStatsRefreshToken(currentValue => currentValue + 1);
            }

            setPendingCustomerDriverReviews(currentReviews => currentReviews.filter(review => review && review.shipment_id !== shipmentId));
            setCustomerDriverReviewDrafts(currentDrafts => {
                if (!Object.prototype.hasOwnProperty.call(currentDrafts, shipmentId)) {
                    return currentDrafts;
                }

                const nextDrafts = { ...currentDrafts };
                delete nextDrafts[shipmentId];
                return nextDrafts;
            });
            setCustomerDriverReviewMessages(currentMessages => {
                if (!Object.prototype.hasOwnProperty.call(currentMessages, shipmentId)) {
                    return currentMessages;
                }

                const nextMessages = { ...currentMessages };
                delete nextMessages[shipmentId];
                return nextMessages;
            });
            setHiddenLocalTestCustomerDriverReviewShipmentIds(currentShipmentIds => (
                currentShipmentIds.includes(shipmentId) ? currentShipmentIds : [...currentShipmentIds, shipmentId]
            ));
            setCustomerDriverReviewModalShipmentId(currentShipmentId => (currentShipmentId === shipmentId ? null : currentShipmentId));
            props.notify('tc', 'Thanks for reviewing your driver.');
        } catch (error) {
            const serverMessage =
                error && error.response && error.response.data && typeof error.response.data.message === 'string'
                    ? error.response.data.message
                    : 'We could not save your review right now. Please try again.';

            if (isLocalhostBrowser()) {
                saveStoredCustomerDriverReview(submittedReviewSnapshot);
                setCustomerDriverReviewStatsRefreshToken(currentValue => currentValue + 1);
                setPendingCustomerDriverReviews(currentReviews => currentReviews.filter(review => review && review.shipment_id !== shipmentId));
                setCustomerDriverReviewDrafts(currentDrafts => {
                    if (!Object.prototype.hasOwnProperty.call(currentDrafts, shipmentId)) {
                        return currentDrafts;
                    }

                    const nextDrafts = { ...currentDrafts };
                    delete nextDrafts[shipmentId];
                    return nextDrafts;
                });
                setCustomerDriverReviewMessages(currentMessages => {
                    if (!Object.prototype.hasOwnProperty.call(currentMessages, shipmentId)) {
                        return currentMessages;
                    }

                    const nextMessages = { ...currentMessages };
                    delete nextMessages[shipmentId];
                    return nextMessages;
                });
                setHiddenLocalTestCustomerDriverReviewShipmentIds(currentShipmentIds => (
                    currentShipmentIds.includes(shipmentId) ? currentShipmentIds : [...currentShipmentIds, shipmentId]
                ));
                setCustomerDriverReviewModalShipmentId(currentShipmentId => (currentShipmentId === shipmentId ? null : currentShipmentId));
                props.notify('tc', 'Review saved locally for testing.');
                return;
            }

            setCustomerDriverReviewMessage(shipmentId, serverMessage);
        } finally {
            setSubmittingCustomerDriverReviewShipmentId('');
        }
    }, [effectivePendingCustomerDriverReviews, getCustomerDriverReviewDraft, props, setCustomerDriverReviewMessage]);

    useEffect(() => {
        if (!customerDriverReviewsSupported || !hasMerchantShipmentScope || !merchantAccessToken) {
            setPendingCustomerDriverReviews(currentReviews => (currentReviews.length === 0 ? currentReviews : []));
            setCustomerDriverReviewModalShipmentId(null);
            return;
        }

        let cancelled = false;

        runMerchantRequestWithSessionRecovery(() =>
            axios({
                method: 'get',
                url: shipment_urls.pendingCustomerDriverReviews,
                timeout: getMerchantBootstrapRequestTimeout(shipment_urls.pendingCustomerDriverReviews),
                headers: {
                    Authorization: buildAuthorizationHeader(),
                    'content-type': 'application/json',
                },
            })
        )
            .then(response => {
                if (cancelled) {
                    return;
                }

                const payload = response && response.data && Array.isArray(response.data.payload)
                    ? response.data.payload.map(normalizeCustomerDriverReviewPrompt).filter(Boolean)
                    : [];
                const pendingReviewIds = new Set(payload.map(review => review.shipment_id));

                setCustomerDriverReviewsSupported(true);
                setPendingCustomerDriverReviews(payload);
                setCustomerDriverReviewDrafts(currentDrafts => filterReviewStateByShipmentIds(currentDrafts, pendingReviewIds));
                setCustomerDriverReviewMessages(currentMessages => filterReviewStateByShipmentIds(currentMessages, pendingReviewIds));
                setCustomerDriverReviewModalShipmentId(currentShipmentId => (currentShipmentId && pendingReviewIds.has(currentShipmentId) ? currentShipmentId : null));
            })
            .catch(error => {
                if (cancelled) {
                    return;
                }

                const status = error && error.response ? error.response.status : null;

                if (status === 404) {
                    setCustomerDriverReviewsSupported(false);
                    setPendingCustomerDriverReviews([]);
                    setCustomerDriverReviewModalShipmentId(null);
                    return;
                }

                trySilentRenewForMerchantAuthError();
            });

        return () => {
            cancelled = true;
        };
    }, [completedShipmentReviewScopeKey, currentAuth0Id, currentStoreScopeIdsKey, customerDriverReviewsSupported, hasMerchantShipmentScope, merchantAccessToken]);

    useEffect(() => {
        if (activeCustomerDriverReview || effectivePendingCustomerDriverReviews.length === 0) {
            return;
        }

        const nextPendingReview = effectivePendingCustomerDriverReviews.find(review => review && !autoPromptedCustomerDriverReviewShipmentIdsRef.current.has(review.shipment_id));

        if (nextPendingReview) {
            if (shouldForceLocalTestCustomerDriverReview && localTestCustomerDriverReview && nextPendingReview.shipment_id === localTestCustomerDriverReview.shipment_id) {
                return;
            }

            autoPromptedCustomerDriverReviewShipmentIdsRef.current.add(nextPendingReview.shipment_id);
            setCustomerDriverReviewModalShipmentId(nextPendingReview.shipment_id);
        }
    }, [activeCustomerDriverReview, effectivePendingCustomerDriverReviews, localTestCustomerDriverReview, shouldForceLocalTestCustomerDriverReview]);

    const getResolvedDriverAuth0Id = useCallback((routeDriver, shipment = null) => {
        const directAuth0Id = getRouteDriverAuth0Id(routeDriver, shipment);

        if (directAuth0Id) {
            return directAuth0Id;
        }

        const fallbackDriverKey = getDriverReviewStatsKey(routeDriver, shipment);
        return fallbackDriverKey ? driverAuth0IdOverridesByDriverKey[fallbackDriverKey] || '' : '';
    }, [driverAuth0IdOverridesByDriverKey]);

    const getResolvedDriverReviewStatsKey = useCallback((routeDriver, shipment = null) => {
        const resolvedAuth0Id = getResolvedDriverAuth0Id(routeDriver, shipment);

        if (resolvedAuth0Id) {
            return `auth0:${resolvedAuth0Id}`;
        }

        return getDriverReviewStatsKey(routeDriver, shipment);
    }, [getResolvedDriverAuth0Id]);

    const getResolvableDriverId = useCallback((routeDriver, shipment = null) => {
        const shipmentDriver = shipment && typeof shipment.driver === 'object' ? shipment.driver : null;
        const listedDriver = Array.isArray(shipment && shipment.Drivers) && shipment.Drivers.length > 0 ? shipment.Drivers[0] : null;
        const listedDriverBlob = listedDriver && typeof listedDriver.driver_blob === 'object' ? listedDriver.driver_blob : null;
        const routeDriverDriver = routeDriver && routeDriver.Driver && typeof routeDriver.Driver === 'object' ? routeDriver.Driver : null;
        const routeDriverUser = routeDriver && routeDriver.User && typeof routeDriver.User === 'object' ? routeDriver.User : null;
        const driverIdCandidates = [
            routeDriver && routeDriver.driver_id,
            routeDriver && routeDriver.user_id,
            routeDriverDriver && routeDriverDriver.driver_id,
            routeDriverDriver && routeDriverDriver.user_id,
            routeDriverUser && routeDriverUser.driver_id,
            routeDriverUser && routeDriverUser.user_id,
            shipmentDriver && shipmentDriver.driver_id,
            shipmentDriver && shipmentDriver.user_id,
            shipmentDriver && shipmentDriver.Driver && shipmentDriver.Driver.driver_id,
            shipmentDriver && shipmentDriver.Driver && shipmentDriver.Driver.user_id,
            shipmentDriver && shipmentDriver.User && shipmentDriver.User.driver_id,
            shipmentDriver && shipmentDriver.User && shipmentDriver.User.user_id,
            listedDriver && listedDriver.driver_id,
            listedDriver && listedDriver.user_id,
            listedDriverBlob && listedDriverBlob.driver_id,
            listedDriverBlob && listedDriverBlob.user_id,
        ];

        return driverIdCandidates
            .map(candidate => (candidate != null ? String(candidate).trim() : ''))
            .find(candidate => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(candidate)) || '';
    }, []);

    useEffect(() => {
        if (!hasMerchantShipmentScope || !merchantAccessToken) {
            setDriverAuth0IdOverridesByDriverKey(currentValue => (Object.keys(currentValue).length === 0 ? currentValue : {}));
            return;
        }

        const unresolvedDrivers = visibleShipments.reduce((accumulator, candidateShipment) => {
            const routeDriver = getPrimaryRouteDriver(candidateShipment);
            const fallbackDriverKey = getDriverReviewStatsKey(routeDriver, candidateShipment);
            const directAuth0Id = getRouteDriverAuth0Id(routeDriver, candidateShipment);
            const driverId = getResolvableDriverId(routeDriver, candidateShipment);

            if (!fallbackDriverKey || directAuth0Id || !driverId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(driverId)) {
                return accumulator;
            }

            accumulator.push({ driverId, fallbackDriverKey });
            return accumulator;
        }, []);

        if (unresolvedDrivers.length === 0) {
            setDriverAuth0IdOverridesByDriverKey(currentValue => (Object.keys(currentValue).length === 0 ? currentValue : {}));
            return;
        }

        let cancelled = false;
        const driverIds = Array.from(new Set(unresolvedDrivers.map(driver => driver.driverId)));

        runMerchantRequestWithSessionRecovery(() =>
            axios({
                method: 'post',
                url: user_urls.hasuraHost,
                timeout: getMerchantBootstrapRequestTimeout(user_urls.hasuraHost),
                headers: {
                    Authorization: buildAuthorizationHeader(),
                    'content-type': 'application/json',
                },
                data: {
                    query: `query DriversByIdForStats($driverIDs: [uuid!]) {
                      Drivers(where: {driver_id: {_in: $driverIDs}}) {
                        driver_id
                        auth0_id
                        User {
                          auth0_id
                        }
                      }
                    }`,
                    variables: { driverIDs: driverIds }
                }
            })
        )
            .then(response => {
                if (cancelled) {
                    return;
                }

                const drivers = response && response.data && response.data.data && Array.isArray(response.data.data.Drivers)
                    ? response.data.data.Drivers
                    : [];
                const auth0ByDriverId = drivers.reduce((accumulator, driverRecord) => {
                    const normalizedDriverId = driverRecord && driverRecord.driver_id ? String(driverRecord.driver_id).trim() : '';
                    const normalizedAuth0Id = driverRecord && (driverRecord.auth0_id || (driverRecord.User && driverRecord.User.auth0_id))
                        ? String(driverRecord.auth0_id || driverRecord.User.auth0_id).trim()
                        : '';

                    if (normalizedDriverId && normalizedAuth0Id) {
                        accumulator[normalizedDriverId] = normalizedAuth0Id;
                    }

                    return accumulator;
                }, {});

                const nextOverrides = unresolvedDrivers.reduce((accumulator, driver) => {
                    const resolvedAuth0Id = auth0ByDriverId[driver.driverId] || '';

                    if (resolvedAuth0Id) {
                        accumulator[driver.fallbackDriverKey] = resolvedAuth0Id;
                    }

                    return accumulator;
                }, {});

                setDriverAuth0IdOverridesByDriverKey(currentValue => {
                    const currentKeys = Object.keys(currentValue);
                    const nextKeys = Object.keys(nextOverrides);

                    if (currentKeys.length === nextKeys.length && currentKeys.every(key => currentValue[key] === nextOverrides[key])) {
                        return currentValue;
                    }

                    return nextOverrides;
                });
            })
            .catch(() => {
                if (!cancelled) {
                    setDriverAuth0IdOverridesByDriverKey(currentValue => (Object.keys(currentValue).length === 0 ? currentValue : {}));
                }
            });

        return () => {
            cancelled = true;
        };
    }, [getResolvableDriverId, hasMerchantShipmentScope, merchantAccessToken, visibleShipments]);

    const driverReviewStatsRequestTargets = useMemo(() => {
        const targetsByIdentity = new Map();

        visibleShipments.forEach(candidateShipment => {
            const routeDriver = getPrimaryRouteDriver(candidateShipment);
            const driverKey = getResolvedDriverReviewStatsKey(routeDriver, candidateShipment);
            const driverAuth0Id = getResolvedDriverAuth0Id(routeDriver, candidateShipment) || '';
            const driverId = getResolvableDriverId(routeDriver, candidateShipment);
            const targetIdentity = driverId || driverAuth0Id || driverKey;

            if (!driverKey || !targetIdentity) {
                return;
            }

            const existingTarget = targetsByIdentity.get(targetIdentity);

            if (existingTarget) {
                const nextDriverKeys = Array.from(new Set([...(existingTarget.driverKeys || []), driverKey]));
                targetsByIdentity.set(targetIdentity, {
                    ...existingTarget,
                    driverAuth0Id: existingTarget.driverAuth0Id || driverAuth0Id,
                    driverId: existingTarget.driverId || driverId,
                    driverKeys: nextDriverKeys,
                });
                return;
            }

            targetsByIdentity.set(targetIdentity, {
                driverKey,
                driverAuth0Id,
                driverId,
                driverKeys: [driverKey],
            });
        });

        return Array.from(targetsByIdentity.values());
    }, [getResolvedDriverAuth0Id, getResolvedDriverReviewStatsKey, visibleShipments]);
    const driverReviewStatsRequestDriverAuth0Ids = useMemo(() => driverReviewStatsRequestTargets.map(target => target.driverAuth0Id).filter(Boolean), [driverReviewStatsRequestTargets]);
    const driverReviewStatsScopeKey = useMemo(() => driverReviewStatsRequestTargets.map(target => `${target.driverKey}:${target.driverAuth0Id}:${target.driverId}`).sort().join(','), [driverReviewStatsRequestTargets]);
    const localCustomerDriverReviewStatsByDriverKey = useMemo(() => {
        const statsByDriverKey = {};
        const shipmentDriverKeyByShipmentId = {};
        const shipmentDriverAuth0IdByShipmentId = {};
        const shouldCountLocalCompletedOrders = isLocalhostBrowser();

        visibleShipments.forEach(candidateShipment => {
            const routeDriver = getPrimaryRouteDriver(candidateShipment);
            const driverKey = getResolvedDriverReviewStatsKey(routeDriver, candidateShipment);

            if (!driverKey) {
                return;
            }

            if (!statsByDriverKey[driverKey]) {
                statsByDriverKey[driverKey] = {
                    driver_key: driverKey,
                    driver_auth0_id: getResolvedDriverAuth0Id(routeDriver, candidateShipment) || null,
                    average_rating: 0,
                    review_count: 0,
                    completed_orders_count: 0,
                };
            }

            if (candidateShipment && candidateShipment.shipment_id) {
                shipmentDriverKeyByShipmentId[String(candidateShipment.shipment_id).trim()] = driverKey;
                shipmentDriverAuth0IdByShipmentId[String(candidateShipment.shipment_id).trim()] = getResolvedDriverAuth0Id(routeDriver, candidateShipment);
            }

            if (shouldCountLocalCompletedOrders && isShipmentCompletedForDisplay(candidateShipment)) {
                statsByDriverKey[driverKey].completed_orders_count += 1;
            }
        });

        getStoredCustomerDriverReviews().forEach(review => {
            if (!review || !review.shipmentId) {
                return;
            }

            const normalizedShipmentId = String(review.shipmentId).trim();
            const driverAuth0Id = review.driverAuth0Id ? String(review.driverAuth0Id).trim() : shipmentDriverAuth0IdByShipmentId[normalizedShipmentId] || '';
            const driverKey = driverAuth0Id ? `auth0:${driverAuth0Id}` : shipmentDriverKeyByShipmentId[normalizedShipmentId] || '';
            const normalizedRating = Number(review.rating || 0);

            if (!driverKey || !Number.isFinite(normalizedRating) || normalizedRating <= 0) {
                return;
            }

            if (!statsByDriverKey[driverKey]) {
                statsByDriverKey[driverKey] = {
                    driver_key: driverKey,
                    driver_auth0_id: driverAuth0Id || null,
                    average_rating: 0,
                    review_count: 0,
                    completed_orders_count: 0,
                };
            }

            const currentStats = statsByDriverKey[driverKey];
            const nextReviewCount = currentStats.review_count + 1;
            const cumulativeRating = (currentStats.average_rating * currentStats.review_count) + normalizedRating;

            currentStats.review_count = nextReviewCount;
            currentStats.average_rating = Number((cumulativeRating / nextReviewCount).toFixed(1));
        });

        return statsByDriverKey;
    }, [customerDriverReviewStatsRefreshToken, getResolvedDriverAuth0Id, getResolvedDriverReviewStatsKey, visibleShipments]);
    const effectiveCustomerDriverReviewStatsByDriverKey = useMemo(() => {
        const mergedStats = { ...localCustomerDriverReviewStatsByDriverKey };

        Object.keys(customerDriverReviewStatsByDriverKey).forEach(driverKey => {
            const remoteStats = customerDriverReviewStatsByDriverKey[driverKey];
            const localStats = mergedStats[driverKey] || null;
            const normalizedRemoteStats = normalizeDriverReviewStats(remoteStats);

            if (!normalizedRemoteStats) {
                return;
            }

            const localReviewCount = localStats ? Number(localStats.review_count || 0) : 0;
            const remoteReviewCount = Number(normalizedRemoteStats.review_count || 0);
            const shouldUseRemoteRating = remoteReviewCount >= localReviewCount && remoteReviewCount > 0;
            const remoteCompletedOrdersCount = normalizeDriverCompletedCount(normalizedRemoteStats.completed_orders_count);

            mergedStats[driverKey] = {
                ...(localStats || {}),
                ...normalizedRemoteStats,
                average_rating: shouldUseRemoteRating
                    ? normalizedRemoteStats.average_rating
                    : Number((localStats && localStats.average_rating) || 0),
                review_count: Math.max(remoteReviewCount, localReviewCount),
                completed_orders_count: remoteCompletedOrdersCount,
            };
        });

        Object.keys(localCustomerDriverReviewStatsByDriverKey).forEach(driverKey => {
            const localStats = localCustomerDriverReviewStatsByDriverKey[driverKey];
            const hasDirectCompletedOrdersCount = Object.prototype.hasOwnProperty.call(driverCompletedOrdersCountByDriverKey, driverKey);
            const directCompletedOrdersCount = normalizeDriverCompletedCount(driverCompletedOrdersCountByDriverKey[driverKey]);
            const existingStats = mergedStats[driverKey] || localStats;
            const hasRemoteCompletedOrdersCount = Object.prototype.hasOwnProperty.call(customerDriverReviewStatsByDriverKey, driverKey)
                && Number.isFinite(Number(existingStats && existingStats.completed_orders_count));

            if (hasRemoteCompletedOrdersCount) {
                return;
            }

            if (hasDirectCompletedOrdersCount) {
                mergedStats[driverKey] = {
                    ...existingStats,
                    completed_orders_count: directCompletedOrdersCount,
                };
            }
        });

        return mergedStats;
    }, [customerDriverReviewStatsByDriverKey, driverCompletedOrdersCountByDriverKey, localCustomerDriverReviewStatsByDriverKey]);
    const getDriverReviewStatsForRouteDriver = useCallback((routeDriver, shipment = null) => {
        const driverKey = getResolvedDriverReviewStatsKey(routeDriver, shipment);
        return driverKey ? effectiveCustomerDriverReviewStatsByDriverKey[driverKey] || null : null;
    }, [effectiveCustomerDriverReviewStatsByDriverKey, getResolvedDriverReviewStatsKey]);

    useEffect(() => {
        if (!customerDriverReviewStatsSupported || !hasMerchantShipmentScope || !merchantAccessToken || driverReviewStatsRequestDriverAuth0Ids.length === 0) {
            setCustomerDriverReviewStatsByDriverKey(currentValue => (Object.keys(currentValue).length === 0 ? currentValue : {}));
            return;
        }

        let cancelled = false;

        runMerchantRequestWithSessionRecovery(() =>
            axios({
                method: 'get',
                url: shipment_urls.customerDriverReviewStats,
                timeout: getMerchantBootstrapRequestTimeout(shipment_urls.customerDriverReviewStats),
                headers: {
                    Authorization: buildAuthorizationHeader(),
                    'content-type': 'application/json',
                },
                params: {
                    driver_auth0_ids: driverReviewStatsRequestDriverAuth0Ids.join(','),
                },
            })
        )
            .then(response => {
                if (cancelled) {
                    return;
                }

                const payload = response && response.data && Array.isArray(response.data.payload)
                    ? response.data.payload.map(normalizeDriverReviewStats).filter(Boolean)
                    : [];
                const requestTargetByDriverAuth0Id = driverReviewStatsRequestTargets.reduce((accumulator, target) => {
                    if (target && target.driverAuth0Id && !accumulator[target.driverAuth0Id]) {
                        accumulator[target.driverAuth0Id] = target;
                    }

                    return accumulator;
                }, {});
                const nextStatsByDriverKey = payload.reduce((accumulator, stats) => {
                    const matchingTarget = stats.driver_auth0_id ? requestTargetByDriverAuth0Id[stats.driver_auth0_id] || null : null;
                    const targetDriverKeys = matchingTarget && Array.isArray(matchingTarget.driverKeys) && matchingTarget.driverKeys.length > 0
                        ? matchingTarget.driverKeys
                        : [stats.driver_key];

                    targetDriverKeys.filter(Boolean).forEach(driverKey => {
                        accumulator[driverKey] = {
                            ...stats,
                            driver_key: driverKey,
                        };
                    });

                    return accumulator;
                }, {});

                setCustomerDriverReviewStatsSupported(true);
                setCustomerDriverReviewStatsByDriverKey(nextStatsByDriverKey);
            })
            .catch(error => {
                if (cancelled) {
                    return;
                }

                const status = error && error.response ? error.response.status : null;

                if (status === 404) {
                    setCustomerDriverReviewStatsSupported(false);
                    setCustomerDriverReviewStatsByDriverKey({});
                }
            });

        return () => {
            cancelled = true;
        };
    }, [customerDriverReviewStatsSupported, driverReviewStatsRequestDriverAuth0Ids, driverReviewStatsRequestTargets, driverReviewStatsScopeKey, hasMerchantShipmentScope, merchantAccessToken]);

    useEffect(() => {
        if (!hasMerchantShipmentScope || !merchantAccessToken || driverReviewStatsRequestTargets.length === 0) {
            setDriverCompletedOrdersCountByDriverKey(currentValue => (Object.keys(currentValue).length === 0 ? currentValue : {}));
            return;
        }

        let cancelled = false;
        const isLocalBrowser = typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost';

        const query = `query MerchantDriverCompletedOrdersCounts {
            ${driverReviewStatsRequestTargets.map((target, index) => {
                const identityFilter = target.driverAuth0Id
                    ? `driver_auth0_id: {_eq: ${JSON.stringify(target.driverAuth0Id)}}`
                    : (target.driverId ? `driver_id: {_eq: ${JSON.stringify(target.driverId)}}` : '');

                if (!identityFilter) {
                    return '';
                }

                return `driver_${index}: Shipments_aggregate(
                where: {
                    deleted_at: {_is_null: true}
                    status: {_in: ["shipment_completed", "shipment_pending_review"]}
                    Route: {
                        RouteDrivers: {
                            deleted_at: {_is_null: true}
                            status: {_eq: "driver"}
                            ${identityFilter}
                        }
                    }
                }
            ) {
                aggregate {
                    count
                }
            }`;
            }).filter(Boolean).join('\n')}
        }`;

        const configuredSecret =
            dashboardConfig &&
            dashboardConfig.config &&
            dashboardConfig.config.api_shipments &&
            dashboardConfig.config.api_shipments.admin &&
            dashboardConfig.config.api_shipments.admin.shipmentsHasuraAdminSecret
                ? String(dashboardConfig.config.api_shipments.admin.shipmentsHasuraAdminSecret)
                : '';
        const envSecret =
            typeof process !== 'undefined' && process.env && process.env.REACT_APP_SHIPMENTS_HASURA_ADMIN_SECRET
                ? String(process.env.REACT_APP_SHIPMENTS_HASURA_ADMIN_SECRET)
                : '';
        const localShipmentsHasuraAdminSecret = (configuredSecret || envSecret).trim().replace(/^"|"$/g, '');
        const productionShipmentsHasuraHost = 'https://api-shipments.hasura.gophr.app/v1/graphql';

        const requestUrls = Array.from(new Set([
            shipment_urls.hasuraHost,
            productionShipmentsHasuraHost
        ].filter(Boolean)));
        const proxyRequest = {
            method: 'post',
            url: '/__local/shipments-hasura',
            timeout: 5000,
            headers: {
                'content-type': 'application/json'
            },
            data: { query },
        };

        const toCountsByDriverKey = payload => {
            if (!payload || payload.errors || !payload.data) {
                return null;
            }

            return driverReviewStatsRequestTargets.reduce((accumulator, target, index) => {
                const aggregateRoot = payload.data[`driver_${index}`];
                const completedOrdersCount = normalizeDriverCompletedCount(
                    aggregateRoot && aggregateRoot.aggregate ? aggregateRoot.aggregate.count : 0
                );

                (Array.isArray(target.driverKeys) ? target.driverKeys : [target.driverKey]).forEach(driverKey => {
                    if (driverKey) {
                        accumulator[driverKey] = completedOrdersCount;
                    }
                });
                return accumulator;
            }, {});
        };

        const fetchCompletedCounts = async () => {
            if (isLocalBrowser) {
                const proxyResponse = await axios(proxyRequest).catch(() => null);
                const proxyCounts = toCountsByDriverKey(proxyResponse && proxyResponse.data);

                if (proxyCounts && Object.keys(proxyCounts).length > 0) {
                    return proxyCounts;
                }
            }

            if (isLocalBrowser && localShipmentsHasuraAdminSecret) {
                const localDirectHasuraResponse = await axios({
                    method: 'post',
                    url: productionShipmentsHasuraHost,
                    timeout: getMerchantBootstrapRequestTimeout(productionShipmentsHasuraHost),
                    headers: {
                        'content-type': 'application/json',
                        'x-hasura-admin-secret': localShipmentsHasuraAdminSecret,
                    },
                    data: { query },
                }).catch(() => null);

                const localDirectCounts = toCountsByDriverKey(localDirectHasuraResponse && localDirectHasuraResponse.data);

                if (localDirectCounts && Object.keys(localDirectCounts).length > 0) {
                    return localDirectCounts;
                }
            }

            for (const requestUrl of requestUrls) {
                const response = await fetch(requestUrl, {
                    method: 'POST',
                    headers: {
                        Authorization: buildAuthorizationHeader(),
                        'content-type': 'application/json'
                    },
                    body: JSON.stringify({ query })
                }).catch(() => null);

                if (!response || !response.ok) {
                    continue;
                }

                const payload = await response.json().catch(() => null);
                const nextCountsByDriverKey = toCountsByDriverKey(payload);

                if (!nextCountsByDriverKey) {
                    continue;
                }

                return nextCountsByDriverKey;
            }

            return null;
        };

        fetchCompletedCounts()
            .then(nextCountsByDriverKey => {
                if (cancelled || !nextCountsByDriverKey) {
                    return;
                }

                setDriverCompletedOrdersCountByDriverKey(nextCountsByDriverKey);
            })
            .catch(() => null);

        return () => {
            cancelled = true;
        };
    }, [driverReviewStatsRequestTargets, driverReviewStatsScopeKey, hasMerchantShipmentScope, merchantAccessToken]);

    useEffect(() => {
        if (storeIdOverride) {
            return;
        }

        if (!currentAuth0Id) {
            return;
        }

        if (currentStoreScopeIds.length > 0) {
            return;
        }

        const inferredStoreIds = normalizeStoreIds(
            visibleShipments.flatMap(candidateShipment => getCandidateShipmentStoreIds(candidateShipment))
        );

        if (inferredStoreIds.length === 0) {
            return;
        }

        setAccessibleStoreIds(previous => {
            const normalizedPrevious = normalizeStoreIds(previous);

            if (
                normalizedPrevious.length === inferredStoreIds.length &&
                inferredStoreIds.every(storeId => normalizedPrevious.includes(storeId))
            ) {
                return previous;
            }

            return inferredStoreIds;
        });
    }, [currentAuth0Id, currentStoreScopeIds, setAccessibleStoreIds, storeIdOverride, visibleShipments]);

    const trackedClaimedOrderChatShipmentIds = useMemo(() => Array.from(new Set(
        visibleShipments
            .filter(candidateShipment => (
                candidateShipment.status === 'shipment_ready_for_driver'
                || candidateShipment.status === 'shipment_assigned'
                || candidateShipment.status === 'shipment_claimed'
                || candidateShipment.status === 'shipment_enroute'
                || candidateShipment.status === 'shipment_completed'
            ))
            .map(candidateShipment => candidateShipment && candidateShipment.shipment_id)
            .filter(Boolean)
    )), [visibleShipments]);

    useEffect(() => {
        if (claimedOrderChatSummariesUnavailableRef.current) {
            setClaimedOrderChatSummariesByThreadId({});
            setClaimedOrderChatUnreadCountsByThreadId({});
            return undefined;
        }

        if (trackedClaimedOrderChatShipmentIds.length === 0) {
            setClaimedOrderChatSummariesByThreadId({});
            setClaimedOrderChatUnreadCountsByThreadId({});
            previousClaimedOrderChatMessageIdsRef.current = {};
            countedClaimedOrderChatMessageIdsRef.current = {};
            return undefined;
        }

        let cancelled = false;

        const syncClaimedOrderChatSummaries = () => {
            const shipmentIdBatches = [];

            for (let index = 0; index < trackedClaimedOrderChatShipmentIds.length; index += CLAIMED_ORDER_CHAT_SUMMARY_BATCH_SIZE) {
                shipmentIdBatches.push(trackedClaimedOrderChatShipmentIds.slice(index, index + CLAIMED_ORDER_CHAT_SUMMARY_BATCH_SIZE));
            }

            Promise.all(
                shipmentIdBatches.flatMap(shipmentIds => CUSTOMER_VISIBLE_CLAIMED_ORDER_CHAT_THREAD_TYPES.map(threadType => axios({
                    method: 'get',
                    headers: {
                        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                        'content-type': 'application/json'
                    },
                    url: `${claimedOrderChatEndpoint}/summaries?shipment_ids=${encodeURIComponent(shipmentIds.join(','))}&thread_type=${encodeURIComponent(threadType)}`
                })))
            )
                .then(responses => {
                    if (cancelled) {
                        return;
                    }

                    if (!responses.every(response => response && response.data && response.data.status === 'successful')) {
                        setClaimedOrderChatSummariesByThreadId({});
                        return;
                    }

                    const summaries = responses.flatMap(response => (
                        response && response.data && response.data.payload && Array.isArray(response.data.payload.summaries)
                            ? response.data.payload.summaries
                            : []
                    ));

                    const nextSummariesByThreadId = summaries.reduce((lookup, summary) => {
                        const threadKey = buildClaimedOrderThreadKey(summary && summary.thread ? summary.thread : null);

                        if (threadKey) {
                            lookup[threadKey] = summary;
                        }

                        return lookup;
                    }, {});

                    setClaimedOrderChatSummariesByThreadId(nextSummariesByThreadId);
                    setClaimedOrderChatUnreadCountsByThreadId(currentValue => {
                        const nextUnreadCountsByThreadId = {};
                        const previousMessageIdsByThreadId = previousClaimedOrderChatMessageIdsRef.current || {};
                        const countedMessageIdsByThreadId = countedClaimedOrderChatMessageIdsRef.current || {};
                        const nextCountedMessageIdsByThreadId = { ...countedMessageIdsByThreadId };

                        summaries.forEach(summary => {
                            const thread = summary && summary.thread ? summary.thread : null;
                            const latestMessage = summary && summary.latest_message ? summary.latest_message : null;
                            const threadKey = buildClaimedOrderThreadKey(thread);
                            const latestMessageId = latestMessage && latestMessage.message_id ? latestMessage.message_id : '';
                            const previousMessageId = threadKey ? previousMessageIdsByThreadId[threadKey] : '';
                            const seenMessageId = threadKey ? claimedOrderChatSeenByThreadId[threadKey] : '';
                            const isIncomingMessage = latestMessage
                                ? latestMessage.direction
                                    ? latestMessage.direction === 'received'
                                    : latestMessage.sender_role === 'driver' || latestMessage.sender_role === 'dispatch'
                                : false;

                            if (!threadKey || !latestMessageId || !isIncomingMessage || seenMessageId === latestMessageId) {
                                if (threadKey) {
                                    nextUnreadCountsByThreadId[threadKey] = 0;
                                    nextCountedMessageIdsByThreadId[threadKey] = seenMessageId || latestMessageId || '';
                                }
                                return;
                            }

                            const currentUnreadCount = Number(currentValue[threadKey]) || 0;
                            const countedMessageId = countedMessageIdsByThreadId[threadKey] || '';
                            const hasObservedNewLatestMessage = Boolean(previousMessageId) && previousMessageId !== latestMessageId;

                            if (hasObservedNewLatestMessage && countedMessageId !== latestMessageId) {
                                nextUnreadCountsByThreadId[threadKey] = currentUnreadCount + 1;
                                nextCountedMessageIdsByThreadId[threadKey] = latestMessageId;
                                return;
                            }

                            nextUnreadCountsByThreadId[threadKey] = currentUnreadCount > 0 ? currentUnreadCount : 1;
                            nextCountedMessageIdsByThreadId[threadKey] = countedMessageId || latestMessageId;
                        });

                        previousClaimedOrderChatMessageIdsRef.current = summaries.reduce((lookup, summary) => {
                            const thread = summary && summary.thread ? summary.thread : null;
                            const latestMessage = summary && summary.latest_message ? summary.latest_message : null;
                            const threadKey = buildClaimedOrderThreadKey(thread);

                            if (threadKey && latestMessage && latestMessage.message_id) {
                                lookup[threadKey] = latestMessage.message_id;
                            }

                            return lookup;
                        }, {});
                        countedClaimedOrderChatMessageIdsRef.current = nextCountedMessageIdsByThreadId;

                        return nextUnreadCountsByThreadId;
                    });
                })
                .catch(error => {
                    if (!cancelled) {
                        const status = error && error.response ? error.response.status : null;

                        if (status === 404) {
                            claimedOrderChatSummariesUnavailableRef.current = true;
                        }

                        setClaimedOrderChatSummariesByThreadId({});
                        setClaimedOrderChatUnreadCountsByThreadId({});
                    }
                });
        };

        syncClaimedOrderChatSummaries();
        const intervalId = window.setInterval(syncClaimedOrderChatSummaries, CLAIMED_ORDER_CHAT_POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [claimedOrderChatSeenByThreadId, trackedClaimedOrderChatShipmentIds]);

    useEffect(() => {
        const summaries = Object.values(claimedOrderChatSummariesByThreadId || {});

        if (summaries.length === 0) {
            previousClaimedOrderChatUnreadCountsRef.current = {};
            if (!hasHydratedClaimedOrderChatSummariesRef.current) {
                hasHydratedClaimedOrderChatSummariesRef.current = true;
            }
            return;
        }

        const previousUnreadCountsByThreadId = previousClaimedOrderChatUnreadCountsRef.current || {};
        const nextUnreadCountsByThreadId = {};
        let firstNewUnreadSummary = null;

        summaries.forEach(summary => {
            const thread = summary && summary.thread ? summary.thread : null;
            const latestMessage = summary && summary.latest_message ? summary.latest_message : null;
            const threadKey = buildClaimedOrderThreadKey(thread);
            const latestMessageId = latestMessage && latestMessage.message_id ? latestMessage.message_id : '';
            const isIncomingMessage = latestMessage
                ? latestMessage.direction
                    ? latestMessage.direction === 'received'
                    : latestMessage.sender_role === 'driver' || latestMessage.sender_role === 'dispatch'
                : false;

            if (!latestMessageId || !threadKey) {
                return;
            }

            nextUnreadCountsByThreadId[threadKey] = Number(claimedOrderChatUnreadCountsByThreadId[threadKey]) || 0;

            const threadRouteStatus = thread && thread.route_status ? String(thread.route_status) : '';
            const threadShipmentStatus = thread && thread.shipment_status ? String(thread.shipment_status) : '';
            // Don't surface chat pop-ups for completed/cancelled orders.
            const isCompletedOrCancelledThread = ['route_completed', 'route_cancelled'].includes(threadRouteStatus)
                || ['shipment_completed', 'shipment_cancelled', 'shipment_declined'].includes(threadShipmentStatus);

            if (!hasHydratedClaimedOrderChatSummariesRef.current || !isIncomingMessage || isCompletedOrCancelledThread) {
                return;
            }

            const previousUnreadCount = Number(previousUnreadCountsByThreadId[threadKey]) || 0;
            const nextUnreadCount = nextUnreadCountsByThreadId[threadKey];

            if (nextUnreadCount <= previousUnreadCount) {
                return;
            }

            if (showOrderChatModal && orderChatRecipientInfo && orderChatRecipientInfo.chatThreadId === threadKey) {
                return;
            }

            if (!firstNewUnreadSummary) {
                firstNewUnreadSummary = summary;
            }
        });

        previousClaimedOrderChatUnreadCountsRef.current = nextUnreadCountsByThreadId;

        if (!hasHydratedClaimedOrderChatSummariesRef.current) {
            hasHydratedClaimedOrderChatSummariesRef.current = true;
            return;
        }

        if (!firstNewUnreadSummary || !firstNewUnreadSummary.thread) {
            return;
        }

        const thread = firstNewUnreadSummary.thread;
        if (!isCustomerVisibleClaimedOrderThreadType(thread.thread_type)) {
            return;
        }

        const shipment = visibleShipments.find(candidateShipment => candidateShipment && candidateShipment.shipment_id === thread.shipment_id) || null;
        const chatParticipantAuth0Id = thread.participant_auth0_id || resolveShipmentCustomerParticipantAuth0Id(shipment);
        const chatThreadType = thread.thread_type;
        const nextRecipientInfo = {
            chatMode: 'order-thread',
            chatDisplayName: chatThreadType === 'customer_driver' ? getShipmentDriverDisplayName(shipment) : 'Dispatch',
            chatRole: chatThreadType === 'customer_driver' ? 'Customer Driver' : 'Dispatch Chat',
            chatShipmentId: thread.shipment_id,
            chatShipmentFriendlyId: thread.shipment_friendly_id || '',
            chatThreadType,
            chatThreadId: buildClaimedOrderThreadKey(thread),
            chatParticipantAuth0Id,
            chatAdminDisplayName: 'Dispatch',
            chatCustomerDisplayName: getShipmentCustomerDisplayName(shipment),
            chatDriverDisplayName: getShipmentDriverDisplayName(shipment),
        };

        setOrderChatRecipientInfo(nextRecipientInfo);
        setShowOrderChatModal(true);
        retrieveOrderChatMessages(nextRecipientInfo);
        playChatNotificationSound();
        props.notify('tc', `${chatThreadType === 'customer_driver' ? 'New driver message' : 'New dispatch message'} for shipment ${thread.shipment_friendly_id || thread.shipment_id}.`, 'warning');
    }, [claimedOrderChatSummariesByThreadId, claimedOrderChatUnreadCountsByThreadId, orderChatRecipientInfo, props, showOrderChatModal, visibleShipments]);

    useEffect(() => {
        setClaimedOrderChatSeenByThreadId(readMerchantClaimedOrderChatSeenMap(merchantClaimedOrderChatSeenScope));
        setClaimedOrderChatUnreadCountsByThreadId({});
        setClaimedOrderChatSummariesByThreadId({});
        previousClaimedOrderChatMessageIdsRef.current = {};
        countedClaimedOrderChatMessageIdsRef.current = {};
        previousClaimedOrderChatUnreadCountsRef.current = {};
        hasHydratedClaimedOrderChatSummariesRef.current = false;
    }, [merchantClaimedOrderChatSeenScope]);

    useEffect(() => {
        setDeletedShipmentIds([]);
    }, [currentStoreId]);

    useEffect(() => {
        if (!hasMerchantQueryScope) {
            setFallbackShipmentsLoading(false);
            return;
        }

        let cancelled = false;

        const fetchFallbackShipments = () => {
            if (fallbackShipmentsRequestInFlightRef.current) {
                return;
            }

            if (isLocalDashboardEnvironment) {
                const now = Date.now();

                if (localHasuraRateLimitUntilRef.current > now) {
                    setFallbackShipmentsLoading(false);
                    return;
                }

                if (now - localHasuraLastRequestAtRef.current < LOCAL_SHIPMENTS_HASURA_MIN_INTERVAL_MS) {
                    setFallbackShipmentsLoading(false);
                    return;
                }
            }

            fallbackShipmentsRequestInFlightRef.current = true;
            setFallbackShipmentsLoading(true);

            const requestMerchantDashboardShipments = () => {
                if (isLocalDashboardEnvironment) {
                    return runMerchantRequestWithSessionRecovery(() => axios({
                        method: 'get',
                        url: shipment_urls.merchantDashboardShipments,
                        timeout: getMerchantBootstrapRequestTimeout(shipment_urls.merchantDashboardShipments),
                        headers: {
                            Authorization: buildAuthorizationHeader(),
                            'content-type': 'application/json'
                        }
                    }))
                        .then(response => {
                            const payload = response && response.data && Array.isArray(response.data.payload)
                                ? response.data.payload
                                : [];

                            appendMerchantSourceDebug('fallback', {
                                source: 'fallback.rest.local',
                                ok: true,
                                count: payload.length,
                                url: shipment_urls.merchantDashboardShipments,
                            });

                            return response;
                        })
                        .catch(error => {
                            appendMerchantSourceDebug('fallback', {
                                source: 'fallback.rest.local',
                                ok: false,
                                message: error && error.message ? error.message : 'request failed',
                                url: shipment_urls.merchantDashboardShipments,
                            });
                            throw error;
                        });
                }

                if (shouldUseMerchantHasuraFeed({
                    isLocalDashboardEnvironment,
                    localHasuraFallbackEnabled: process.env.REACT_APP_ENABLE_LOCAL_HASURA_FALLBACK === 'true',
                    storeIds: currentStoreScopeIds,
                })) {
                    const query = `query MerchantFallbackLocal($storeIds:[uuid!]) {
                                Shipments(
                                    order_by: {updated_at: desc}
                                    limit: 2000
                                    ${buildMerchantStoreShipmentWhereClause()}
                                ) {
                                    is_expedited
                                    num_of_gophrs
                                    shipment_friendly_id
                                    cust_auth0_id
                                    customer_blob
                                    distance
                                    shipment_total
                                    contains_alcohol
                                    shipping_fee
                                    sum_of_orders_total
                                    weight
                                    created_at
                                    shipment_id
                                    updated_at
                                    description
                                    drop_off_instructions
                                    pick_up_instructions
                                    scheduled_for
                                    shipment_type
                                    status
                                    billing_status
                                    vehicle_type
                                    is_roundtrip
                                    merchant_order_id
                                    shipping_fee_blob
                                    placed_by
                                    completed_at
                                    fedex_data
                                    ai_recommendation
                                    billing_details
                                    Orders(order_by: { order_friendly_id: asc }) {
                                        order_id
                                        shipment_id
                                        store_id
                                        location_blob
                                        order_friendly_id
                                        actions
                                        status
                                        subtotal
                                        sum_tax
                                        total
                                        created_at
                                        updated_at
                                        pick_up_instructions
                                        description
                                        weight
                                        drop_off_instructions
                                        estimated_arrival_time
                                        completed_at
                                        photos
                                        actual_action
                                        Items {
                                            item_id
                                            product_blob
                                            quantity
                                            product_price
                                            status
                                            product_id
                                            ItemOptions {
                                                option_name
                                                option_price
                                                variant_name
                                                variant_blob
                                                option_blob
                                            }
                                        }
                                    }
                                    LocationUpdates(order_by: { timestamp: asc }) {
                                        latitude
                                        longitude
                                        timestamp
                                    }
                                    Route {
                                        RouteDrivers(where: { status: { _eq: "driver" }, deleted_at: { _is_null: true } }) {
                                            driver_auth0_id
                                            driver_auth0_id
                                            driver_blob
                                            driver_id
                                            vehicle_blob
                                            tracking_blob
                                        }
                                    }
                                    ShipmentHandshakes {
                                        url
                                        type
                                    }
                                }
                            }`;
                    const variables = {
                        storeIds: currentStoreScopeIds.length > 0 ? currentStoreScopeIds : [],
                    };
                    const toPayloadResponse = (response, sourceLabel) => {
                        if (response && response.data && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
                            const firstError = response.data.errors[0];
                            appendMerchantSourceDebug('fallback', {
                                source: sourceLabel,
                                ok: false,
                                message: firstError && firstError.message ? firstError.message : 'GraphQL error',
                            });
                            throw new Error(firstError && firstError.message ? firstError.message : 'Merchant fallback query failed');
                        }

                        const shipments = response && response.data && response.data.data && Array.isArray(response.data.data.Shipments)
                            ? response.data.data.Shipments
                            : [];

                        appendMerchantSourceDebug('fallback', {
                            source: sourceLabel,
                            ok: true,
                            count: shipments.length,
                        });

                        return {
                            data: {
                                payload: shipments,
                            }
                        };
                    };
                    const requestLocalProxy = () => axios({
                        method: 'post',
                        url: '/__local/shipments-hasura',
                        timeout: 2000,
                        headers: {
                            'content-type': 'application/json'
                        },
                        data: {
                            query,
                            variables
                        }
                    }).then(response => {
                        localHasuraLastRequestAtRef.current = Date.now();
                        return toPayloadResponse(response, 'fallback.proxy.local');
                    }).catch(error => {
                        const status = error && error.response ? error.response.status : null;

                        if (status === 429) {
                            localHasuraRateLimitUntilRef.current = Date.now() + LOCAL_SHIPMENTS_HASURA_RATE_LIMIT_BACKOFF_MS;
                        }

                        appendMerchantSourceDebug('fallback', {
                            source: 'fallback.proxy.local',
                            ok: false,
                            message: error && error.message ? error.message : 'request failed',
                            url: '/__local/shipments-hasura',
                            status,
                        });
                        throw error;
                    });
                    const configuredSecret =
                        dashboardConfig &&
                        dashboardConfig.config &&
                        dashboardConfig.config.api_shipments &&
                        dashboardConfig.config.api_shipments.admin &&
                        dashboardConfig.config.api_shipments.admin.shipmentsHasuraAdminSecret
                            ? String(dashboardConfig.config.api_shipments.admin.shipmentsHasuraAdminSecret)
                            : '';
                    const envSecret =
                        typeof process !== 'undefined' && process.env && process.env.REACT_APP_SHIPMENTS_HASURA_ADMIN_SECRET
                            ? String(process.env.REACT_APP_SHIPMENTS_HASURA_ADMIN_SECRET)
                            : '';
                    const localShipmentsHasuraAdminSecret = (configuredSecret || envSecret).trim().replace(/^"|"$/g, '');
                    const productionShipmentsHasuraHost = 'https://api-shipments.hasura.gophr.app/v1/graphql';
                    const productionMerchantDashboardUrl = 'https://api-shipments.gophr.app/shipments/merchant-dashboard';
                    const requestAdminSecret = () => axios({
                        method: 'post',
                        url: shipment_urls.hasuraHost,
                        timeout: getMerchantBootstrapRequestTimeout(shipment_urls.hasuraHost),
                        headers: {
                            'x-hasura-admin-secret': localShipmentsHasuraAdminSecret,
                            'content-type': 'application/json',
                        },
                        data: {
                            query,
                            variables,
                        }
                    }).then(response => toPayloadResponse(response, 'fallback.admin-secret.configured')).catch(error => {
                        appendMerchantSourceDebug('fallback', {
                            source: 'fallback.admin-secret.configured',
                            ok: false,
                            message: error && error.message ? error.message : 'request failed',
                            url: shipment_urls.hasuraHost,
                        });
                        throw error;
                    });
                    const requestBearer = (requestUrl = shipment_urls.hasuraHost) => runMerchantRequestWithSessionRecovery(() => axios({
                        method: 'post',
                        url: requestUrl,
                        timeout: getMerchantBootstrapRequestTimeout(requestUrl),
                        headers: {
                            Authorization: buildAuthorizationHeader(),
                            'content-type': 'application/json'
                        },
                        data: {
                            query,
                            variables,
                        }
                    })).then(response => toPayloadResponse(response, requestUrl === productionShipmentsHasuraHost ? 'fallback.bearer.production' : 'fallback.bearer.configured')).catch(error => {
                        appendMerchantSourceDebug('fallback', {
                            source: requestUrl === productionShipmentsHasuraHost ? 'fallback.bearer.production' : 'fallback.bearer.configured',
                            ok: false,
                            message: error && error.message ? error.message : 'request failed',
                            url: requestUrl,
                        });
                        throw error;
                    });
                    const requestMerchantDashboardRest = () => {
                        const primaryUrl = shipment_urls.merchantDashboardShipments;
                        const hostedUrl = shipment_urls.toHostedShipmentUrl(primaryUrl);
                        const fallbackProductionUrl = productionMerchantDashboardUrl;
                        const isTerminalShipmentStatus = statusValue => {
                            const status = String(statusValue || '').toLowerCase();

                            if (!status) {
                                return false;
                            }

                            return (
                                status.includes('complete')
                                || status.includes('deliver')
                                || status.includes('cancel')
                                || status.includes('failed')
                            );
                        };
                        const hasActiveShipments = shipments =>
                            Array.isArray(shipments) && shipments.some(shipment => !isTerminalShipmentStatus(shipment && shipment.status));
                        const requestByUrl = requestUrl => runMerchantRequestWithSessionRecovery(() => axios({
                            method: 'get',
                            url: requestUrl,
                            timeout: getMerchantBootstrapRequestTimeout(requestUrl),
                            headers: {
                                Authorization: buildAuthorizationHeader(),
                                'content-type': 'application/json'
                            }
                        }));

                        return requestByUrl(primaryUrl).then(response => {
                            const primaryPayload = response && response.data && Array.isArray(response.data.payload) ? response.data.payload : [];
                            appendMerchantSourceDebug('fallback', {
                                source: 'fallback.rest.primary',
                                ok: true,
                                count: primaryPayload.length,
                                url: primaryUrl,
                            });

                            if (!isLocalDashboardEnvironment || !hostedUrl || hostedUrl === primaryUrl) {
                                return response;
                            }

                            return requestByUrl(hostedUrl).then(hostedResponse => {
                                const hostedPayload = hostedResponse && hostedResponse.data && Array.isArray(hostedResponse.data.payload)
                                    ? hostedResponse.data.payload
                                    : [];

                                appendMerchantSourceDebug('fallback', {
                                    source: 'fallback.rest.hosted.compare',
                                    ok: true,
                                    count: hostedPayload.length,
                                    url: hostedUrl,
                                });

                                const shouldPreferHosted =
                                    hostedPayload.length > primaryPayload.length
                                    && (!hasActiveShipments(primaryPayload) || primaryPayload.length === 0);

                                if (shouldPreferHosted) {
                                    return hostedResponse;
                                }

                                return response;
                            }).catch(() => response);
                        }).catch(error => {
                            appendMerchantSourceDebug('fallback', {
                                source: 'fallback.rest.primary',
                                ok: false,
                                message: error && error.message ? error.message : 'request failed',
                                url: primaryUrl,
                            });
                            if (!hostedUrl || hostedUrl === primaryUrl) {
                                if (fallbackProductionUrl && fallbackProductionUrl !== primaryUrl) {
                                    return requestByUrl(fallbackProductionUrl).then(response => {
                                        const payload = response && response.data && Array.isArray(response.data.payload) ? response.data.payload : [];
                                        appendMerchantSourceDebug('fallback', {
                                            source: 'fallback.rest.production',
                                            ok: true,
                                            count: payload.length,
                                            url: fallbackProductionUrl,
                                        });
                                        return response;
                                    });
                                }

                                throw error;
                            }

                            return requestByUrl(hostedUrl).then(response => {
                                const payload = response && response.data && Array.isArray(response.data.payload) ? response.data.payload : [];
                                appendMerchantSourceDebug('fallback', {
                                    source: 'fallback.rest.hosted',
                                    ok: true,
                                    count: payload.length,
                                    url: hostedUrl,
                                });
                                return response;
                            }).catch(() => {
                                appendMerchantSourceDebug('fallback', {
                                    source: 'fallback.rest.hosted',
                                    ok: false,
                                    message: error && error.message ? error.message : 'request failed',
                                    url: hostedUrl,
                                });
                                if (fallbackProductionUrl && fallbackProductionUrl !== primaryUrl && fallbackProductionUrl !== hostedUrl) {
                                    return requestByUrl(fallbackProductionUrl).then(response => {
                                        const payload = response && response.data && Array.isArray(response.data.payload) ? response.data.payload : [];
                                        appendMerchantSourceDebug('fallback', {
                                            source: 'fallback.rest.production',
                                            ok: true,
                                            count: payload.length,
                                            url: fallbackProductionUrl,
                                        });
                                        return response;
                                    });
                                }

                                throw error;
                            });
                        });
                    };
                    const withRestFallbackIfEmpty = response => {
                        if (isLocalDashboardEnvironment) {
                            return response;
                        }

                        const shipments = response && response.data && Array.isArray(response.data.payload)
                            ? response.data.payload
                            : [];

                        if (shipments.length > 0) {
                            return response;
                        }

                        return requestMerchantDashboardRest()
                            .then(restResponse => {
                                const restPayload = restResponse && restResponse.data && Array.isArray(restResponse.data.payload)
                                    ? restResponse.data.payload
                                    : [];

                                return {
                                    data: {
                                        payload: restPayload,
                                    }
                                };
                            })
                            .catch(() => response);
                    };

                    const requestPrimaryLocal = () => {
                        if (localShipmentsHasuraAdminSecret) {
                            return requestAdminSecret()
                                .then(withRestFallbackIfEmpty)
                                .catch(() => requestBearer(productionShipmentsHasuraHost)
                                    .then(withRestFallbackIfEmpty)
                                    .catch(() => requestBearer().then(withRestFallbackIfEmpty)));
                        }

                        return requestBearer(productionShipmentsHasuraHost)
                            .then(withRestFallbackIfEmpty)
                            .catch(() => requestBearer().then(withRestFallbackIfEmpty));
                    };

                    return requestPrimaryLocal()
                        .then(response => {
                            const shipments = response && response.data && Array.isArray(response.data.payload)
                                ? response.data.payload
                                : [];

                            if (shipments.length > 0) {
                                return response;
                            }

                            return requestLocalProxy().then(withRestFallbackIfEmpty).catch(() => response);
                        })
                        .catch(() => requestLocalProxy().then(withRestFallbackIfEmpty).catch(() => ({ data: { payload: [] } })));
                }

                const primaryUrl = shipment_urls.merchantDashboardShipments;
                const hostedUrl = shipment_urls.toHostedShipmentUrl(primaryUrl);
                const requestByUrl = requestUrl => runMerchantRequestWithSessionRecovery(() => axios({
                    method: 'get',
                    url: requestUrl,
                    timeout: getMerchantBootstrapRequestTimeout(requestUrl),
                    headers: {
                        Authorization: buildAuthorizationHeader(),
                        'content-type': 'application/json'
                    }
                }));

                return requestByUrl(primaryUrl).catch(error => {
                    if (!hostedUrl || hostedUrl === primaryUrl) {
                        throw error;
                    }

                    return requestByUrl(hostedUrl);
                });
            };

            if (shouldUseScopedMerchantDashboardFeed || isLocalDashboardEnvironment) {
                requestMerchantDashboardShipments()
                    .then(response => {
                        if (cancelled) {
                            return;
                        }

                        const shipments = response && response.data && Array.isArray(response.data.payload)
                            ? response.data.payload
                            : [];

                        setFallbackShipments(current => {
                            if (shipments.length === 0 && Array.isArray(current) && current.length > 0) {
                                return current;
                            }

                            return areShipmentsEqualById(current, shipments) ? current : shipments;
                        });
                    })
                    .catch(error => {
                        if (!cancelled) {
                            void error;
                        }
                    })
                    .finally(() => {
                        if (!cancelled) {
                            setFallbackShipmentsLoading(false);
                        }

                        fallbackShipmentsRequestInFlightRef.current = false;
                    });

                return;
            }

            requestMerchantDashboardShipments()
                .then(response => {
                    if (cancelled) {
                        return;
                    }

                                        const shipments = response && response.data && Array.isArray(response.data.payload)
                                                ? response.data.payload
                                                : [];

                                        setFallbackShipments(current => {
                                            if (shipments.length === 0 && Array.isArray(current) && current.length > 0) {
                                                return current;
                                            }

                                            return areShipmentsEqualById(current, shipments) ? current : shipments;
                                        });
                })
                .catch(error => {
                    if (!cancelled) {
                        void error;
                    }
                })
                .finally(() => {
                    if (!cancelled) {
                        setFallbackShipmentsLoading(false);
                    }

                    fallbackShipmentsRequestInFlightRef.current = false;
                });
        };

        if (documentsModalOpen) {
            return () => {
                cancelled = true;
                fallbackShipmentsRequestInFlightRef.current = false;
            };
        }

        fetchFallbackShipments();

        if (isLocalDashboardEnvironment) {
            return () => {
                cancelled = true;
            };
        }

        const pollId = window.setInterval(fetchFallbackShipments, 15000);

        return () => {
            cancelled = true;
            fallbackShipmentsRequestInFlightRef.current = false;
            window.clearInterval(pollId);
        };
    }, [currentAuth0Id, currentStoreId, currentStoreScopeIdsKey, hasMerchantQueryScope, isLocalDashboardEnvironment, rawShipmentCount, documentsModalOpen, shouldUseScopedMerchantDashboardFeed]);
    useEffect(() => {
        if (storeIdOverride || !currentAuth0Id || fallbackShipments.length === 0) {
            return undefined;
        }

        const derivedStoreId = resolveSingleStoreIdFromShipments(fallbackShipments);

        if (!derivedStoreId || derivedStoreId === currentStoreId) {
            return undefined;
        }

        const currentStoreMatchesFeed = currentStoreId
            ? fallbackShipments.some(candidateShipment => getCandidateShipmentStoreIds(candidateShipment).includes(String(currentStoreId)))
            : false;

        if (currentStoreMatchesFeed) {
            return undefined;
        }

        const bootstrapKey = `${currentAuth0Id}:${derivedStoreId}`;

        if (feedDerivedStoreBootstrapKeyRef.current === bootstrapKey) {
            return undefined;
        }

        feedDerivedStoreBootstrapKeyRef.current = bootstrapKey;

        let active = true;
        setStoreLoading(true);

        axios({
            method: 'post',
            timeout: getMerchantBootstrapRequestTimeout(inventory_urls.hasuraHost),
            url: inventory_urls.hasuraHost,
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                'content-type': 'application/json'
            },
            data: {
                query: `{Stores(where: {store_id: {_eq: "${derivedStoreId}"}}) { store_id sells_alcohol permit_type timezone latitude longitude market_id } }`
            }
        })
            .then(response => {
                if (!active) {
                    return;
                }

                if (response.data.errors && response.data.errors.length > 0) {
                    const [firstError] = response.data.errors;

                    if (firstError.message === 'Malformed Authorization header' || (firstError.extensions && firstError.extensions.code === 'invalid-jwt')) {
                        trySilentRenewForMerchantAuthError();
                    }
                    setStoreLoading(false);
                    return;
                }

                const resolvedStore = response.data && response.data.data && response.data.data.Stores ? response.data.data.Stores[0] : null;

                if (!resolvedStore) {
                    setStoreLoading(false);
                    return;
                }

                setMarketIdMerchants(resolvedStore.market_id);
                setStore(resolvedStore);
                setCoordinates({ latitude: resolvedStore.latitude, longitude: resolvedStore.longitude });
                setStoreLoading(false);

                if (!isLocalDashboardEnvironment) {
                    axios({
                        method: 'post',
                        timeout: getMerchantBootstrapRequestTimeout(shipment_urls.hasuraHost),
                        headers: {
                            Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                            'content-type': 'application/json'
                        },
                        url: shipment_urls.hasuraHost,
                        data: {
                            query:
                                `{
              Markets (where: {market_id: {_eq: "` +
                                resolvedStore.market_id +
                                `"}}){
                market_name
                note
                is_disabled            
                }
              }`
                        }
                    })
                        .then(res => {
                            if (!active) {
                                return;
                            }

                            if (res.data.data) {
                                setMarketNote(res.data.data.Markets[0].note);
                            } else if (res.data.errors) {
                                setMarketNote('');
                            }
                        })
                        .catch(() => {
                            if (active) {
                                setMarketNote('');
                            }
                        });
                }
            })
            .catch(() => {
                if (active) {
                    setStoreLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [currentAuth0Id, currentStoreId, fallbackShipments, isLocalDashboardEnvironment, props.history, storeIdOverride]);

    const resolveShipmentDashboardTab = candidateShipment => {
        if (!candidateShipment) {
            return 'pending';
        }

        const status = candidateShipment.status;

        if (status === 'shipment_completed' || isShipmentCompletedForDisplay(candidateShipment)) {
            return 'completed';
        }

        if (
            status === 'shipment_scheduled' ||
            status === 'shipment_saved' ||
            status === 'shipment_pending_saved' ||
            status === 'shipment_saved_pending_payment_retry' ||
            status === 'shipment_pending_payment' ||
            isSavedDraftShipment(candidateShipment)
        ) {
            // Locally promote shipments that just went through checkout before live data refreshes
            if (pendingCheckoutShipmentIds.has(candidateShipment.shipment_id)) {
                return 'inProgress';
            }
            return 'saved';
        }

        if (MERCHANT_IN_PROGRESS_SHIPMENT_STATUSES.includes(status)) {
            return 'inProgress';
        }

        if (status === 'shipment_cancelled' || status === 'shipment_declined') {
            return 'cancelled';
        }

        return 'pending';
    };

    const shipmentsToDisplay = useMemo(() => {
        if (!hasMerchantShipmentScope) {
            return [];
        }

        let shownShipments = visibleShipments.filter(candidateShipment => resolveShipmentDashboardTab(candidateShipment) === selected);

        if (selected === 'completed') {
            shownShipments = [...shownShipments].sort((a, b) => {
                const leftTimestamp = getShipmentCompletionTimestampForDisplay(a);
                const rightTimestamp = getShipmentCompletionTimestampForDisplay(b);

                return new Date(rightTimestamp || 0) - new Date(leftTimestamp || 0);
            });
        }

        return shownShipments;
    }, [hasMerchantShipmentScope, selected, visibleShipments]);

    const inProgressShipmentCount = useMemo(() => {
        if (!hasMerchantShipmentScope) {
            return 0;
        }

        return visibleShipments.filter(candidateShipment => resolveShipmentDashboardTab(candidateShipment) === 'inProgress').length;
    }, [hasMerchantShipmentScope, visibleShipments]);

    const inProgressUnreadShipmentCount = useMemo(() => {
        if (!hasMerchantShipmentScope) {
            return 0;
        }

        return visibleShipments.filter(hasUnreadClaimedOrderChatForShipment).length;
    }, [claimedOrderChatUnreadCountsByThreadId, hasMerchantShipmentScope, visibleShipments]);

    const savedForLaterShipmentCount = useMemo(() => {
        if (!hasMerchantShipmentScope) {
            return 0;
        }

        return visibleShipments.filter(candidateShipment => resolveShipmentDashboardTab(candidateShipment) === 'saved').length;
    }, [hasMerchantShipmentScope, visibleShipments]);

    const readyShipments = useMemo(() => {
        if (!readyShipmentsCheck || !hasMerchantShipmentScope) {
            return [];
        }

        return visibleShipments.filter(candidateShipment => candidateShipment.status === 'shipment_ready_for_driver');
    }, [hasMerchantShipmentScope, readyShipmentsCheck, visibleShipments]);

    const deliveringShipments = useMemo(() => {
        if (!deliveringShipmentsCheck || !hasMerchantShipmentScope) {
            return [];
        }

        return visibleShipments.filter(candidateShipment =>
            candidateShipment.status === 'shipment_claimed' ||
            candidateShipment.status === 'shipment_assigned' ||
            candidateShipment.status === 'shipment_enroute'
        );
    }, [deliveringShipmentsCheck, hasMerchantShipmentScope, visibleShipments]);

    useEffect(() => {
        const shouldForceScrollTop = localStorage.getItem('merchant_orders_force_scroll_top') === '1';
        if (!shouldForceScrollTop) {
            return;
        }

        localStorage.removeItem('merchant_orders_force_scroll_top');
        forceScrollTop();
        const timeoutId = window.setTimeout(forceScrollTop, 120);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, []);

    useEffect(() => {
        if (!lastCreatedShipmentId || !lastCreatedSnapshot || !currentStoreId || !Array.isArray(effectiveRawShipments)) {
            return;
        }

        const hasShipmentInSubscription = effectiveRawShipments.some(candidate => candidate.shipment_id === lastCreatedShipmentId);
        if (hasShipmentInSubscription) {
            setHydratedLastCreatedShipment(current => (current ? null : current));
            return;
        }

        if (isLocalShipmentSnapshot(lastCreatedSnapshot)) {
            const localSnapshotShipment = withActionHost(
                normalizeShipmentForDisplay(lastCreatedSnapshot.shipment),
                (lastCreatedSnapshot.shipment && lastCreatedSnapshot.shipment.__actionHost) || shipment_urls.shipmentHost
            );

            setHydratedLastCreatedShipment(current => {
                if (
                    current &&
                    current.shipment_id === localSnapshotShipment.shipment_id &&
                    current.__actionHost === localSnapshotShipment.__actionHost
                ) {
                    return current;
                }

                return localSnapshotShipment;
            });
            return;
        }

        let cancelled = false;

        axios({
            method: 'post',
            url: shipment_urls.hasuraHost,
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                'content-type': 'application/json'
            },
            data: {
                query: `query MerchantOrderTableLastCreated($shipmentId: uuid!) {
                  Shipments(where: {shipment_id: {_eq: $shipmentId}}, limit: 1) {
                    shipment_id
                    shipment_friendly_id
                                                                                cust_auth0_id
                                        customer_blob
                                        distance
                                        shipment_total
                                        contains_alcohol
                                        shipping_fee
                                        sum_of_orders_total
                                        weight
                                        created_at
                                        updated_at
                                        description
                                        drop_off_instructions
                                        pick_up_instructions
                                        scheduled_for
                    shipment_type
                    status
                                        billing_status
                                        vehicle_type
                                        is_roundtrip
                                        merchant_order_id
                                        shipping_fee_blob
                                        placed_by
                                        completed_at
                                        fedex_data
                                        ai_recommendation
                                        billing_details
                                        Orders(order_by: { order_friendly_id: asc }) {
                                            order_id
                                            shipment_id
                                            store_id
                                            location_blob
                      order_friendly_id
                                            actions
                                            status
                                            subtotal
                                            sum_tax
                                            total
                                            created_at
                                            updated_at
                                            pick_up_instructions
                                            description
                                            weight
                                            drop_off_instructions
                                            estimated_arrival_time
                                            completed_at
                                            photos
                      actual_action
                    }
                    LocationUpdates(order_by: { timestamp: asc }) {
                      latitude
                      longitude
                      timestamp
                    }
                    Route {
                                            RouteDrivers(where: { deleted_at: { _is_null: true } }) {
                        driver_auth0_id
                        driver_blob
                        driver_id
                                                vehicle_id
                        vehicle_blob
                        tracking_blob
                      }
                    }
                    ShipmentHandshakes {
                      url
                      type
                    }
                  }
                }`,
                variables: {
                    shipmentId: lastCreatedShipmentId
                }
            }
        })
            .then(response => {
                if (cancelled) {
                    return;
                }

                const fetchedShipment =
                    response &&
                    response.data &&
                    response.data.data &&
                    Array.isArray(response.data.data.Shipments) &&
                    response.data.data.Shipments.length > 0
                        ? response.data.data.Shipments[0]
                        : null;

                if (fetchedShipment) {
                    const hostedShipment = withActionHost(fetchedShipment, shipment_urls.hostedShipmentHost);
                    setHydratedLastCreatedShipment(current => {
                        if (
                            current &&
                            current.shipment_id === hostedShipment.shipment_id &&
                            current.__actionHost === hostedShipment.__actionHost
                        ) {
                            return current;
                        }

                        return hostedShipment;
                    });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setHydratedLastCreatedShipment(current => (current ? null : current));
                }
            });

        return () => {
            cancelled = true;
        };
    }, [lastCreatedShipmentId, lastCreatedSnapshot, currentStoreId, effectiveRawShipments]);

    useEffect(() => {
        if (!lastCreatedShipmentId || storeLoading || !currentStoreId) {
            return;
        }

        const candidateShipment = effectiveRawShipments.find(candidate => candidate.shipment_id === lastCreatedShipmentId) || hydratedLastCreatedShipment;
        if (!candidateShipment) {
            if (!effectiveRawShipments.length && !hydratedLastCreatedShipment) {
                return;
            }

            const missingLogKey = [lastCreatedShipmentId, currentStoreId, effectiveRawShipments.length, Boolean(hydratedLastCreatedShipment)].join(':');
            if (lastCreatedVisibilityMissingLogRef.current === missingLogKey) {
                return;
            }

            lastCreatedVisibilityMissingLogRef.current = missingLogKey;
            return;
        }

        lastCreatedVisibilityMissingLogRef.current = '';

        const appearsInTable = shipmentsToDisplay.some(item => item.shipment_id === lastCreatedShipmentId);
        const appearsInVisibleShipments = visibleShipments.some(item => item.shipment_id === lastCreatedShipmentId);
        const visibilityLogKey = [
            lastCreatedShipmentId,
            candidateShipment.status,
            selected,
            appearsInTable,
            appearsInVisibleShipments,
            Boolean(hydratedLastCreatedShipment && hydratedLastCreatedShipment.shipment_id === lastCreatedShipmentId),
        ].join(':');

        if (lastCreatedVisibilityCheckLogRef.current === visibilityLogKey) {
            return;
        }

        lastCreatedVisibilityCheckLogRef.current = visibilityLogKey;
    }, [
        currentAuth0Id,
        currentStoreId,
        filter,
        hydratedLastCreatedShipment,
        lastCreatedShipmentId,
        effectiveRawShipments,
        rawShipments,
        selected,
        storeLoading,
        shipmentsToDisplay,
        visibleShipments,
    ]);

    const getCurrentVehicleBlob = (routeDriver, driverAction) => {
        const routeVehicle = routeDriver && routeDriver.vehicle_blob ? routeDriver.vehicle_blob : null;
        const driverProfile = driverAction && driverAction.Driver ? driverAction.Driver : {};
        const liveVehicles = Array.isArray(driverProfile.Vehicles) ? driverProfile.Vehicles : [];
        const activeVehicle = driverProfile.active_vehicle
            ? liveVehicles.find(vehicle => vehicle && vehicle.vehicle_id === driverProfile.active_vehicle)
            : null;
        const selectedVehicle = activeVehicle || liveVehicles[0] || routeVehicle;

        if (!selectedVehicle) {
            return null;
        }

        return {
            vehicle_id: selectedVehicle.vehicle_id || (routeVehicle && routeVehicle.vehicle_id) || null,
            type: selectedVehicle.type || (routeVehicle && routeVehicle.type) || null,
            make: selectedVehicle.make || (routeVehicle && routeVehicle.make) || null,
            model: selectedVehicle.model || (routeVehicle && routeVehicle.model) || null,
            color: selectedVehicle.color || (routeVehicle && routeVehicle.color) || null,
            year: selectedVehicle.year || (routeVehicle && routeVehicle.year) || null,
            plate_state: selectedVehicle.plate_state || (routeVehicle && routeVehicle.plate_state) || null,
            plate_value: selectedVehicle.plate_value || (routeVehicle && (routeVehicle.plate_value || routeVehicle.plate_number)) || null,
            render_image_url:
                getVehicleRenderUrl(selectedVehicle) ||
                selectedVehicle.render_image_url ||
                selectedVehicle.vehicle_image_url ||
                selectedVehicle.image_url ||
                (routeVehicle && (routeVehicle.render_image_url || routeVehicle.vehicle_image_url || routeVehicle.image_url)) ||
                '',
            image_url:
                getVehicleRenderUrl(selectedVehicle) ||
                selectedVehicle.image_url ||
                selectedVehicle.render_image_url ||
                selectedVehicle.vehicle_image_url ||
                (routeVehicle && (routeVehicle.image_url || routeVehicle.render_image_url || routeVehicle.vehicle_image_url)) ||
                ''
        };
    };

    const formatVehicleInfo = vehicle => {
        if (!vehicle) {
            return '';
        }

        return [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') + (vehicle.color ? ` (${vehicle.color})` : '');
    };

    const buildDriverUser = (routeDriver, driverAction) => {
        const driverBlob = routeDriver && routeDriver.driver_blob ? routeDriver.driver_blob : {};
        const vehicle = getCurrentVehicleBlob(routeDriver, driverAction);
        const driverProfile = driverAction && driverAction.Driver ? driverAction.Driver : {};
        const permits = [];

        if (driverProfile.can_deliver_alcohol) {
            permits.push('Alcohol');
        }

        (driverProfile.restricted_area_credentials || []).forEach(credential => {
            permits.push(credential);
        });

        return {
            first_name: driverBlob.first_name,
            last_name: driverBlob.last_name,
            phone: driverBlob.phone,
            vehicle_info: formatVehicleInfo(vehicle),
            profile_photo_url: driverProfile.profile_photo_url,
            permit: permits.join(', '),
            render_image_url: vehicle && vehicle.render_image_url ? vehicle.render_image_url : '',
            image_url: vehicle && vehicle.image_url ? vehicle.image_url : '',
            vehicle_blob: vehicle,
        };
    };

    const fetchLastDriverActions = async driverIDs => {
        if (!driverIDs || driverIDs.length === 0) {
            return [];
        }

        const response = await axios({
            method: 'post',
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                'content-type': 'application/json'
            },
            url: user_urls.hasuraHost,
            data: {
                query: `query LastDriverActions ($driverIDs: [uuid!]) {
                  LastDriverActions(where: {driver_id: {_in: $driverIDs}}) {
                    driver_id
                    location_blob
                    Driver {
                      profile_photo_url
                      restricted_area_credentials
                      can_deliver_alcohol
                                            active_vehicle
                                            Vehicles {
                                                vehicle_id
                                                type
                                                make
                                                model
                                                color
                                                year
                                                plate_state
                                                plate_value
                                                render_image_url
                                                vehicle_image_url
                                                image_url
                                                VehicleDocuments(where: {doc_type: {_eq: "vehicle_render"}}, order_by: {updated_at: desc}) {
                                                    vehicle_id
                                                    user_auth0_id
                                                    doc_type
                                                    file_name
                                                    signed_url
                                                    url
                                                    path_id
                                                    ocr_text
                                                    updated_at
                                                }
                                            }
                    }
                  }
                }`,
                variables: { driverIDs }
            }
        });

        return response.data && response.data.data && response.data.data.LastDriverActions ? response.data.data.LastDriverActions : [];
    };

    const DRIVER_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    const fetchDriverProfilePhotoOverride = useCallback(async lookup => {
        const cacheKey = lookup && lookup.cacheKey ? String(lookup.cacheKey) : '';
        const uuidIdentifiers = lookup && Array.isArray(lookup.uuidIdentifiers) ? lookup.uuidIdentifiers : [];
        const complianceIdentifiers = lookup && Array.isArray(lookup.complianceIdentifiers) ? lookup.complianceIdentifiers : [];
        const normalizePhoneDigits = phoneValue => {
            const digitsOnly = phoneValue != null ? String(phoneValue).replace(/\D/g, '') : '';
            if (!digitsOnly) {
                return '';
            }

            return digitsOnly.length > 10 ? digitsOnly.slice(-10) : digitsOnly;
        };
        const lookupPhoneDigits = normalizePhoneDigits(lookup && lookup.phoneDigits ? lookup.phoneDigits : '');
        const lookupName = lookup && lookup.name ? String(lookup.name).trim().toLowerCase() : '';

        if (!cacheKey) {
            return;
        }

        if (driverProfilePhotoOverridesRef.current[cacheKey]) {
            return;
        }

        if (driverProfilePhotoLookupInFlightRef.current[cacheKey]) {
            return;
        }

        driverProfilePhotoLookupInFlightRef.current[cacheKey] = true;

        try {
            let candidatePhotoUrl = '';
            let expandedComplianceIdentifiers = Array.from(new Set(complianceIdentifiers));

            if (uuidIdentifiers.length > 0) {
                const driverActions = await fetchLastDriverActions(uuidIdentifiers);
                const matchingDriverAction = (Array.isArray(driverActions) ? driverActions : []).find(driverAction => {
                    if (!driverAction || !driverAction.driver_id) {
                        return false;
                    }

                    return uuidIdentifiers.includes(String(driverAction.driver_id));
                });

                const driverProfile = matchingDriverAction && matchingDriverAction.Driver ? matchingDriverAction.Driver : null;
                candidatePhotoUrl = [
                    driverProfile && driverProfile.profile_photo_url,
                    driverProfile && driverProfile.profile_picture_url,
                    driverProfile && driverProfile.profile_image_url,
                    driverProfile && driverProfile.photo_url,
                    driverProfile && driverProfile.avatar_url,
                ]
                    .map(candidate => normalizeAssetUrl(candidate))
                    .find(Boolean) || '';
            }

            if (!candidatePhotoUrl && uuidIdentifiers.length > 0) {
                try {
                    const driversByIdResponse = await axios({
                        method: 'post',
                        headers: {
                            Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                            'content-type': 'application/json'
                        },
                        url: user_urls.hasuraHost,
                        data: {
                            query: `query DriversByIdForProfilePhoto($driverIDs: [uuid!]) {
                              Drivers(where: {driver_id: {_in: $driverIDs}}) {
                                driver_id
                                auth0_id
                                user_id
                                profile_photo_url
                              }
                            }`,
                            variables: { driverIDs: uuidIdentifiers }
                        }
                    });

                    const matchedDriverRecord =
                        driversByIdResponse &&
                        driversByIdResponse.data &&
                        driversByIdResponse.data.data &&
                        Array.isArray(driversByIdResponse.data.data.Drivers) &&
                        driversByIdResponse.data.data.Drivers.length > 0
                            ? driversByIdResponse.data.data.Drivers[0]
                            : null;

                    candidatePhotoUrl = normalizeAssetUrl(matchedDriverRecord && matchedDriverRecord.profile_photo_url);

                    expandedComplianceIdentifiers = Array.from(
                        new Set([
                            ...expandedComplianceIdentifiers,
                            matchedDriverRecord && matchedDriverRecord.driver_id,
                            matchedDriverRecord && matchedDriverRecord.auth0_id,
                            matchedDriverRecord && matchedDriverRecord.user_id,
                        ]
                            .map(identifier => (identifier != null ? String(identifier).trim() : ''))
                            .filter(Boolean))
                    );
                } catch (driversByIdError) {
                    void driversByIdError;
                }
            }

            if (!candidatePhotoUrl) {
                for (let index = 0; index < expandedComplianceIdentifiers.length && !candidatePhotoUrl; index++) {
                    const complianceIdentifier = expandedComplianceIdentifiers[index];

                    try {
                        const complianceResponse = await axios({
                            method: 'get',
                            headers: {
                                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                                'content-type': 'application/json'
                            },
                            url: user_urls.driverCompliance(complianceIdentifier),
                        });

                        const compliancePayload = complianceResponse && complianceResponse.data ? complianceResponse.data.payload : null;
                        const complianceDriver = compliancePayload && compliancePayload.driver ? compliancePayload.driver : null;
                        const complianceOnboarding =
                            compliancePayload &&
                            compliancePayload.compliance &&
                            compliancePayload.compliance.onboarding
                                ? compliancePayload.compliance.onboarding
                                : null;

                        candidatePhotoUrl = [
                            complianceOnboarding && complianceOnboarding.driver_visibility && complianceOnboarding.driver_visibility.profile_photo_url,
                            complianceOnboarding && complianceOnboarding.driver_visibility && complianceOnboarding.driver_visibility.profile_picture_url,
                            complianceDriver && complianceDriver.profile_photo_url,
                            complianceDriver && complianceDriver.profile_picture_url,
                            complianceDriver && complianceDriver.profile_image_url,
                            complianceDriver && complianceDriver.photo_url,
                            complianceDriver && complianceDriver.avatar_url,
                        ]
                            .map(candidate => normalizeAssetUrl(candidate))
                            .find(Boolean) || '';
                    } catch (complianceError) {
                        void complianceError;
                    }
                }
            }

            if (!candidatePhotoUrl) {
                try {
                    const dispatchDriversEndpoint = user_urls.dispatchDriversList([]) || `https://api-users.gophr.app/admin/dispatch/drivers`;
                    const driversResponse = await axios({
                        method: 'get',
                        headers: {
                            Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                            'content-type': 'application/json'
                        },
                        url: dispatchDriversEndpoint,
                    });

                    const driversPayload = Array.isArray(driversResponse && driversResponse.data && driversResponse.data.payload)
                        ? driversResponse.data.payload
                        : [];

                    const matchingDriver = driversPayload.find(driverRecord => {
                        if (!driverRecord) {
                            return false;
                        }

                        const recordIdentifiers = [
                            driverRecord.driver_id,
                            driverRecord.auth0_id,
                            driverRecord.user_id,
                            driverRecord.User && driverRecord.User.auth0_id,
                            driverRecord.User && driverRecord.User.user_id,
                        ]
                            .map(identifier => (identifier != null ? String(identifier).trim() : ''))
                            .filter(Boolean);

                        const hasMatchingIdentifier = recordIdentifiers.some(identifier => expandedComplianceIdentifiers.includes(identifier));
                        if (hasMatchingIdentifier) {
                            return true;
                        }

                        const recordPhoneDigits = [
                            driverRecord.phone,
                            driverRecord.User && driverRecord.User.phone,
                            driverRecord.driver_blob && driverRecord.driver_blob.phone,
                        ]
                            .map(phoneValue => normalizePhoneDigits(phoneValue))
                            .find(Boolean) || '';

                        if (lookupPhoneDigits && recordPhoneDigits && lookupPhoneDigits === recordPhoneDigits) {
                            return true;
                        }

                        const recordName = [
                            (driverRecord.User && driverRecord.User.firstname) || driverRecord.first_name || '',
                            (driverRecord.User && driverRecord.User.lastname) || driverRecord.last_name || '',
                        ]
                            .join(' ')
                            .trim()
                            .toLowerCase();

                        return Boolean(lookupName && recordName && lookupName === recordName);
                    });

                    candidatePhotoUrl = [
                        matchingDriver && matchingDriver.profile_photo_url,
                        matchingDriver && matchingDriver.profile_picture_url,
                        matchingDriver && matchingDriver.profile_image_url,
                        matchingDriver && matchingDriver.driver_visibility && matchingDriver.driver_visibility.profile_photo_url,
                        matchingDriver && matchingDriver.User && matchingDriver.User.profile_photo_url,
                        matchingDriver && matchingDriver.User && matchingDriver.User.profile_picture_url,
                        matchingDriver && matchingDriver.User && matchingDriver.User.profile_image_url,
                    ]
                        .map(candidate => normalizeAssetUrl(candidate))
                        .find(Boolean) || '';
                } catch (dispatchDriversError) {
                    void dispatchDriversError;
                }
            }

            if (!candidatePhotoUrl) {
                return;
            }

            setDriverProfilePhotoOverridesById(currentValue => {
                if (currentValue[cacheKey] === candidatePhotoUrl) {
                    return currentValue;
                }

                return {
                    ...currentValue,
                    [cacheKey]: candidatePhotoUrl,
                };
            });
        } catch (error) {
            void error;
        } finally {
            delete driverProfilePhotoLookupInFlightRef.current[cacheKey];
        }
    }, []);

    const buildDriversEnrouteDisplay = async shipments => {
        const trackedShipments = (shipments || []).filter(isTrackedShipment);
        const routeDriversById = new Map();

        trackedShipments.forEach(shipment => {
            const routeDriver = getPrimaryRouteDriver(shipment);
            if (routeDriver && routeDriver.driver_id && !routeDriversById.has(routeDriver.driver_id)) {
                routeDriversById.set(routeDriver.driver_id, routeDriver);
            }
        });

        const driverActions = await fetchLastDriverActions(Array.from(routeDriversById.keys()));

        return driverActions
            .filter(driverAction => driverAction.location_blob && driverAction.location_blob.lat != null && driverAction.location_blob.lng != null)
            .map(driverAction => {
                const user = buildDriverUser(routeDriversById.get(driverAction.driver_id), driverAction);

                return {
                    driver_id: driverAction.driver_id,
                    LastDriverActions: driverAction,
                    User: user,
                    vehicle_blob: user.vehicle_blob,
                    image_url: user.image_url,
                };
            });
    };

    const areDriversEnrouteEqual = (currentDrivers, nextDrivers) => {
        if (currentDrivers === nextDrivers) {
            return true;
        }

        if (!Array.isArray(currentDrivers) || !Array.isArray(nextDrivers)) {
            return false;
        }

        if (currentDrivers.length !== nextDrivers.length) {
            return false;
        }

        for (let index = 0; index < currentDrivers.length; index++) {
            const current = currentDrivers[index];
            const next = nextDrivers[index];

            const currentLocation = current && current.LastDriverActions && current.LastDriverActions.location_blob;
            const nextLocation = next && next.LastDriverActions && next.LastDriverActions.location_blob;

            if (!current || !next || current.driver_id !== next.driver_id) {
                return false;
            }

            if (!currentLocation || !nextLocation || currentLocation.lat !== nextLocation.lat || currentLocation.lng !== nextLocation.lng) {
                return false;
            }

            if ((current && current.image_url) !== (next && next.image_url)) {
                return false;
            }

            if ((current && current.User && current.User.vehicle_info) !== (next && next.User && next.User.vehicle_info)) {
                return false;
            }
        }

        return true;
    };

    const buildShipmentWithDriver = async shipment => {
        if (!shipment || !isTrackingDisplayShipment(shipment)) {
            return shipment;
        }

        const routeDriver = getPrimaryRouteDriver(shipment);
        if (!routeDriver || !routeDriver.driver_id) {
            return shipment;
        }

        const driverActions = await fetchLastDriverActions([routeDriver.driver_id]);
        const driverAction = driverActions[0];

        const currentVehicleBlob = getCurrentVehicleBlob(routeDriver, driverAction);
        const fallbackDriverBlob = routeDriver && routeDriver.driver_blob ? routeDriver.driver_blob : {};
        const existingShipmentDriver = shipment.driver && typeof shipment.driver === 'object' ? shipment.driver : {};
        const fallbackUser = {
            ...((existingShipmentDriver && existingShipmentDriver.User) || {}),
            ...fallbackDriverBlob,
            vehicle_info: formatVehicleInfo(currentVehicleBlob) || ((existingShipmentDriver && existingShipmentDriver.User && existingShipmentDriver.User.vehicle_info) || ''),
            profile_photo_url:
                (existingShipmentDriver && existingShipmentDriver.User && existingShipmentDriver.User.profile_photo_url) ||
                '',
            permit:
                (existingShipmentDriver && existingShipmentDriver.User && existingShipmentDriver.User.permit) ||
                '',
            render_image_url:
                (currentVehicleBlob && currentVehicleBlob.render_image_url) ||
                (existingShipmentDriver && existingShipmentDriver.User && existingShipmentDriver.User.render_image_url) ||
                '',
            image_url:
                (currentVehicleBlob && currentVehicleBlob.image_url) ||
                (existingShipmentDriver && existingShipmentDriver.User && existingShipmentDriver.User.image_url) ||
                '',
            vehicle_blob:
                currentVehicleBlob ||
                (existingShipmentDriver && existingShipmentDriver.User && existingShipmentDriver.User.vehicle_blob) ||
                null,
        };

        return {
            ...shipment,
            driver: {
                ...existingShipmentDriver,
                driver_id:
                    (driverAction && driverAction.driver_id) ||
                    routeDriver.driver_id ||
                    existingShipmentDriver.driver_id ||
                    null,
                LastDriverActions:
                    driverAction ||
                    existingShipmentDriver.LastDriverActions ||
                    null,
                User: driverAction ? buildDriverUser(routeDriver, driverAction) : fallbackUser,
                vehicle_blob:
                    currentVehicleBlob ||
                    existingShipmentDriver.vehicle_blob ||
                    null,
                render_image_url:
                    (currentVehicleBlob && currentVehicleBlob.render_image_url) ||
                    existingShipmentDriver.render_image_url ||
                    '',
                image_url:
                    (currentVehicleBlob && currentVehicleBlob.image_url) ||
                    existingShipmentDriver.image_url ||
                    ''
            }
        };
    };

    const fetchCompletedShipmentTrackingSnapshot = async (shipmentId, shipmentFriendlyId) => {
        if (!shipmentId && shipmentFriendlyId == null) {
            return null;
        }

        const hasFriendlyId = shipmentFriendlyId != null && shipmentFriendlyId !== '';
        const query = hasFriendlyId
            ? `query MerchantCompletedTrackingSnapshot($shipmentFriendlyId: bigint!) {
            Shipments(where: { shipment_friendly_id: { _eq: $shipmentFriendlyId } }, limit: 1) {
                shipment_id
                shipment_friendly_id
                status
                completed_at
                LocationUpdates(order_by: { timestamp: asc }) {
                    latitude
                    longitude
                    timestamp
                }
                Route {
                    status
                    RouteDrivers(where: { status: { _eq: "driver" }, deleted_at: { _is_null: true } }) {
                        driver_blob
                        driver_id
                        vehicle_blob
                        tracking_blob
                    }
                }
            }
        }`
            : `query MerchantCompletedTrackingSnapshot($shipmentId: uuid!) {
            Shipments(where: { shipment_id: { _eq: $shipmentId } }, limit: 1) {
                shipment_id
                shipment_friendly_id
                status
                completed_at
                LocationUpdates(order_by: { timestamp: asc }) {
                    latitude
                    longitude
                    timestamp
                }
                Route {
                    status
                    RouteDrivers(where: { status: { _eq: "driver" }, deleted_at: { _is_null: true } }) {
                        driver_blob
                        driver_id
                        vehicle_blob
                        tracking_blob
                    }
                }
            }
        }`;

        const requestUrls = Array.from(new Set([
            shipment_urls.hasuraHost,
            'https://api-shipments.hasura.gophr.app/v1/graphql'
        ].filter(Boolean)));

        let shipment = null;

        for (const requestUrl of requestUrls) {
            const response = await fetch(requestUrl, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                    'content-type': 'application/json'
                },
                body: JSON.stringify({
                    query,
                    variables: hasFriendlyId
                        ? { shipmentFriendlyId: String(shipmentFriendlyId) }
                        : { shipmentId },
                })
            }).catch(() => null);

            if (!response || !response.ok) {
                continue;
            }

            const payload = await response.json().catch(() => null);
            const shipments = payload && payload.data && Array.isArray(payload.data.Shipments)
                ? payload.data.Shipments
                : [];

            if (shipments.length > 0) {
                shipment = shipments[0];
                break;
            }
        }

        if (!shipment) {
            return null;
        }

        return {
            ...shipment,
            Route: shipment.Route
                ? {
                    ...shipment.Route,
                    RouteDrivers: Array.isArray(shipment.Route.RouteDrivers)
                        ? shipment.Route.RouteDrivers.map(enrichRouteDriverVehicleBlob)
                        : [],
                }
                : { RouteDrivers: [] },
        };
    };

    const ShipmentEtaSummary = ({ shipment }) => {
        const pickupOrder = shipment.Orders.find(order => order.actions.hasOwnProperty('pick_up') && !order.actions.pick_up[0].includes('(drop)'));
        const dropoffOrder = [...shipment.Orders].reverse().find(order => order.actions.hasOwnProperty('drop_off') || order.actual_action === 'dropoff');
        const pickupEstimatedArrivalTime = pickupOrder && pickupOrder.estimated_arrival_time;
        const dropoffEstimatedArrivalTime = dropoffOrder && dropoffOrder.estimated_arrival_time;
        const isPickupCompleted = isOrderCompletedForDisplay(pickupOrder);

        if (!isPickupCompleted && !pickupEstimatedArrivalTime && !dropoffEstimatedArrivalTime) {
            return null;
        }

        return (
            <>
                {pickupOrder && (
                    <p>
                        <strong>ETA to pickup:</strong> {isPickupCompleted ? formatOrderCompletionForDisplay(pickupOrder) : formatETA(pickupEstimatedArrivalTime)}
                    </p>
                )}
                {dropoffEstimatedArrivalTime && <p><strong>ETA to dropoff:</strong> {formatETA(dropoffEstimatedArrivalTime)}</p>}
            </>
        );
    };

    useEffect(() => {
        if (!selectedTrackingShipmentId) {
            return;
        }

        const latestShipment = visibleShipments.find(shipment => shipment.shipment_id === selectedTrackingShipmentId);

        if (!latestShipment) {
            return;
        }

        setShipmentClick(currentShipment => {
            if (!currentShipment || currentShipment === '' || currentShipment.shipment_id !== selectedTrackingShipmentId) {
                return currentShipment;
            }

            if (currentShipment === latestShipment) {
                return currentShipment;
            }

            return mergeTrackedShipmentSelection(currentShipment, latestShipment);
        });
    }, [selectedTrackingShipmentId, visibleShipments]);

    useEffect(() => {
        if (!shipmentClick || shipmentClick === '' || !isShipmentCompletedForDisplay(shipmentClick)) {
            return;
        }

        const completedSupplementShipment = completedSupplementShipments.find(
            candidateShipment => candidateShipment && candidateShipment.shipment_id === shipmentClick.shipment_id
        );
        const selectedLocationUpdateCount = Array.isArray(shipmentClick.LocationUpdates)
            ? shipmentClick.LocationUpdates.length
            : 0;
        const supplementLocationUpdateCount = completedSupplementShipment && Array.isArray(completedSupplementShipment.LocationUpdates)
            ? completedSupplementShipment.LocationUpdates.length
            : 0;

        if (supplementLocationUpdateCount > selectedLocationUpdateCount) {
            setShipmentClick(currentShipment => {
                if (!currentShipment || currentShipment === '' || currentShipment.shipment_id !== shipmentClick.shipment_id) {
                    return currentShipment;
                }

                return preferFreshShipmentWithTrackingHistory(currentShipment, completedSupplementShipment);
            });

            return;
        }

        if (selectedLocationUpdateCount > 1) {
            return;
        }

        let active = true;

        fetchCompletedShipmentTrackingSnapshot(shipmentClick.shipment_id, shipmentClick.shipment_friendly_id)
            .then(snapshotShipment => {
                if (!active || !snapshotShipment) {
                    return;
                }

                setShipmentClick(currentShipment => {
                    if (!currentShipment || currentShipment === '' || currentShipment.shipment_id !== snapshotShipment.shipment_id) {
                        return currentShipment;
                    }

                    return {
                        ...currentShipment,
                        Route: snapshotShipment.Route || currentShipment.Route,
                        LocationUpdates: Array.isArray(snapshotShipment.LocationUpdates) && snapshotShipment.LocationUpdates.length > 0
                            ? snapshotShipment.LocationUpdates
                            : currentShipment.LocationUpdates,
                    };
                });
            })
            .catch(() => {
                if (!active) {
                    return;
                }
            });

        return () => {
            active = false;
        };
    }, [completedSupplementShipments, shipmentClick]);

    useEffect(() => {
        driverProfilePhotoOverridesRef.current = driverProfilePhotoOverridesById;
    }, [driverProfilePhotoOverridesById]);

    useEffect(() => {
        if (currentStoreId) {
            let i = 0;
            let j = 0;
            visibleShipments.forEach(element => {
                if (
                    element.status === 'shipment_created' ||
                    element.status === 'shipment_pending' ||
                    element.status === 'shipment_pending_schedule'
                ) {
                    i++;
                }
                if ((element.status === 'shipment_pending' || element.status === 'shipment_pending_schedule') && element.shipping_fee > 0) {
                    j++;
                }
            });
            if (i > amountPending || j > amountApproval) {
                play();
            }
            if (i !== amountPending) {
                setAmountPending(i);
            }
            if (j !== amountApproval) {
                setAmountApproval(j);
            }
        }
    }, [amountApproval, amountPending, currentStoreId, play, visibleShipments]);

    useEffect(() => {
        if (!isLocalDashboardEnvironment && error) {
            if (error.message === 'cannot start as connection_init failed with : Could not verify JWT: JWTExpired') {
                props.history.push('/');
            }
        }
    }, [error, isLocalDashboardEnvironment, props.history]);

    // useEffect(() => {
    //   if (error) {
    //     if (error.message === 'cannot start as connection_init failed with : Could not verify JWT: JWTExpired') {
    //       props.history.push('/');
    //     }
    // }, [error]);

    useEffect(() => {
        let active = true;

        if (isLocalDashboardEnvironment && !storeIdOverride) {
            bootstrappedAuth0IdRef.current = '';
            setStoreLoading(false);

            return () => {
                active = false;
            };
        }

        if (storeIdOverride) {
            bootstrappedAuth0IdRef.current = '';
            setStoreLoading(true);
            setStore(currentStore => (currentStore && currentStore.store_id === storeIdOverride ? currentStore : null));

            axios({
                method: 'post',
                timeout: getMerchantBootstrapRequestTimeout(inventory_urls.hasuraHost),
                url: inventory_urls.hasuraHost,
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                    'content-type': 'application/json'
                },
                data: {
                    query: `{Stores(where: {store_id: {_eq: "${storeIdOverride}"}}) { store_id sells_alcohol permit_type timezone latitude longitude market_id } }`
                }
            })
                .then(response => {
                    if (!active) {
                        return;
                    }

                    if (response.data.errors && response.data.errors.length > 0) {
                        const [firstError] = response.data.errors;

                        if (firstError.message === 'Malformed Authorization header' || (firstError.extensions && firstError.extensions.code === 'invalid-jwt')) {
                            trySilentRenewForMerchantAuthError();
                        }
                        setStoreLoading(false);
                        return;
                    }

                    const resolvedStore = response.data && response.data.data && response.data.data.Stores ? response.data.data.Stores[0] : null;

                    if (!resolvedStore) {
                        setStoreLoading(false);
                        return;
                    }

                    setMarketIdMerchants(resolvedStore.market_id);
                    setStore(resolvedStore);
                    setCoordinates({ latitude: resolvedStore.latitude, longitude: resolvedStore.longitude });
                    setStoreLoading(false);

                    if (!isLocalDashboardEnvironment) {
                        axios({
                            method: 'post',
                            timeout: getMerchantBootstrapRequestTimeout(shipment_urls.hasuraHost),
                            headers: {
                                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                                'content-type': 'application/json'
                            },
                            url: shipment_urls.hasuraHost,
                            data: {
                                query:
                                    `{
              Markets (where: {market_id: {_eq: "` +
                                    resolvedStore.market_id +
                                    `"}}){
                market_name
                note
                is_disabled            
                }
              }`
                            }
                        })
                            .then(res => {
                                if (!active) {
                                    return;
                                }

                                if (res.data.data) {
                                    setMarketNote(res.data.data.Markets[0].note);
                                } else if (res.data.errors) {
                                    setMarketNote('');
                                }
                            })
                            .catch(() => {
                                if (active) {
                                    setMarketNote('');
                                }
                            });
                    }
                })
                .catch(() => {
                    if (active) {
                        setStoreLoading(false);
                    }
                });

            return () => {
                active = false;
            };
        }

        const auth0Id = getCurrentAuth0Id();

        if (!auth0Id) {
            bootstrappedAuth0IdRef.current = '';
            setStoreLoading(false);
            return () => {
                active = false;
            };
        }

        if (bootstrappedAuth0IdRef.current === auth0Id) {
            return () => {
                active = false;
            };
        }

        bootstrappedAuth0IdRef.current = auth0Id;

        axios({
            method: 'post',
            timeout: getMerchantBootstrapRequestTimeout(user_urls.hasuraHost),
            url: user_urls.hasuraHost,
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                'content-type': 'application/json'
            },
            data: {
                query: `{Users(where: {auth0_id: {_eq: "${auth0Id}"}}) {store_id}}`
            }
        })
            .then(userResponse => {
                if (!active) {
                    return null;
                }

                const userRecord = userResponse.data && userResponse.data.data && userResponse.data.data.Users ? userResponse.data.data.Users[0] : null;
                const resolvedStoreId = userRecord && userRecord.store_id ? userRecord.store_id : '';

                if (!resolvedStoreId) {
                    setStoreLoading(false);
                    return null;
                }

                return axios({
                    method: 'post',
                    timeout: getMerchantBootstrapRequestTimeout(inventory_urls.hasuraHost),
                    url: inventory_urls.hasuraHost,
                    headers: {
                        Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                        'content-type': 'application/json'
                    },
                    data: {
                        query: `{Stores(where: {store_id: {_eq: "${resolvedStoreId}"}}) { store_id sells_alcohol permit_type timezone latitude longitude market_id } }`
                    }
                });
            })
            .then(response => {
                if (!active || !response) {
                    return;
                }

                if (response.data.errors && response.data.errors.length > 0) {
                    const [firstError] = response.data.errors;

                    if (firstError.message === 'Malformed Authorization header' || (firstError.extensions && firstError.extensions.code === 'invalid-jwt')) {
                        trySilentRenewForMerchantAuthError();
                    }
                    setStoreLoading(false);
                    return;
                }

                const resolvedStore = response.data && response.data.data && response.data.data.Stores ? response.data.data.Stores[0] : null;

                if (!resolvedStore) {
                    setStoreLoading(false);
                    return;
                }

                setMarketIdMerchants(resolvedStore.market_id);
                setStore(resolvedStore);
                setCoordinates({ latitude: resolvedStore.latitude, longitude: resolvedStore.longitude });
                setStoreLoading(false);

                if (!isLocalDashboardEnvironment) {
                    axios({
                        method: 'post',
                        timeout: getMerchantBootstrapRequestTimeout(shipment_urls.hasuraHost),
                        headers: {
                            Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                            'content-type': 'application/json'
                        },
                        url: shipment_urls.hasuraHost,
                        data: {
                            query:
                                `{
              Markets (where: {market_id: {_eq: "` +
                                resolvedStore.market_id +
                                `"}}){
                market_name
                note
                is_disabled            
                }
              }`
                        }
                    })
                        .then(res => {
                            if (!active) {
                                return;
                            }

                            if (res.data.data) {
                                setMarketNote(res.data.data.Markets[0].note);
                            } else if (res.data.errors) {
                                setMarketNote('');
                            }
                        })
                        .catch(() => {
                            setMarketNote('');
                        });
                }
            })
            .catch(() => {
                if (active) {
                    setStoreLoading(false);
                }
            });

        return () => {
            active = false;
        };
    }, [isLocalDashboardEnvironment, props.history, setMarketIdMerchants, storeIdOverride]);

    useEffect(() => {
        if (currentStoreId) {
            const nextReady = [];
            const nextAlmostReady = [];

            visibleShipments.forEach(element => {
                if (element.status !== 'shipment_scheduled') {
                    return;
                }

                if (now.isAfter(element.scheduled_for)) {
                    nextReady.push(element.shipment_id);
                    return;
                }

                if (tomorrow.isAfter(element.scheduled_for)) {
                    nextAlmostReady.push(element.shipment_id);
                }
            });

            setReady(current => (areStringListsEqual(current, nextReady) ? current : nextReady));
            setAlmostReady(current => (areStringListsEqual(current, nextAlmostReady) ? current : nextAlmostReady));
            return;
        }

        setReady(current => (current.length === 0 ? current : []));
        setAlmostReady(current => (current.length === 0 ? current : []));
    }, [almostReady, currentStoreId, now, ready, tomorrow, visibleShipments]);

    const resetExpand = () => {
        expandedItems[selected] = {};
        if (typeof props.expand === 'function') {
            props.expand(expandedItems);
        }
    };

    useEffect(() => {
        if (!driversEnrouteCheck) {
            setDriversEnroute(currentDrivers => (currentDrivers.length === 0 ? currentDrivers : []));
        } else {
            if (currentStoreId) {
                let active = true;

                buildDriversEnrouteDisplay(visibleShipments)
                    .then(driversDisplay => {
                        if (active) {
                            setDriversEnroute(currentDrivers => (areDriversEnrouteEqual(currentDrivers, driversDisplay) ? currentDrivers : driversDisplay));
                        }
                    })
                    .catch(() => {
                        if (active) {
                            setDriversEnroute(currentDrivers => (currentDrivers.length === 0 ? currentDrivers : []));
                        }
                    });

                return () => {
                    active = false;
                };
            } else {
                setDriversEnroute(currentDrivers => (currentDrivers.length === 0 ? currentDrivers : []));
            }
        }
    }, [currentStoreId, driversEnrouteCheck, visibleShipments]);

    useEffect(() => {
        let active = true;
        const hasSelectedTrackedShipment = selectedTrackingShipmentId !== '' && isShipmentTrackingDisplayStatus(selectedTrackingShipmentStatus);

        if (!driversEnrouteCheck && !hasSelectedTrackedShipment) {
            return () => {
                active = false;
            };
        }

        const refreshLiveTracking = async () => {
            if (driversEnrouteCheck) {
                try {
                    const driversDisplay = await buildDriversEnrouteDisplay(visibleShipments);
                    if (active) {
                        setDriversEnroute(currentDrivers => (areDriversEnrouteEqual(currentDrivers, driversDisplay) ? currentDrivers : driversDisplay));
                    }
                } catch (_) {}
            }

            if (hasSelectedTrackedShipment) {
                const selectedShipment =
                    visibleShipments.find(shipment => shipment.shipment_id === selectedTrackingShipmentId) || (shipmentClick !== '' ? shipmentClick : null);

                if (!selectedShipment) {
                    return;
                }

                if (isShipmentCompletedForDisplay(selectedShipment)) {
                    return;
                }

                try {
                    const refreshedShipment = await buildShipmentWithDriver(selectedShipment);
                    if (active) {
                        setShipmentClick(currentShipment => {
                            if (!currentShipment || currentShipment === '' || currentShipment.shipment_id !== refreshedShipment.shipment_id) {
                                return currentShipment;
                            }

                            return getTrackedShipmentDisplayToken(currentShipment) === getTrackedShipmentDisplayToken(refreshedShipment)
                                ? currentShipment
                                : refreshedShipment;
                        });
                    }
                } catch (_) {}
            }
        };

        const timeoutId = setTimeout(refreshLiveTracking, getNextTrackingRefreshMs());

        return () => {
            active = false;
            clearTimeout(timeoutId);
        };
    }, [driversEnrouteCheck, driversEnroute, selectedTrackingShipmentId, selectedTrackingShipmentStatus, shipmentClick, visibleShipments]);

    const DeliveryDescription = ({ shipment }) => {
        return (
            <>
                {shipment.description.includes('{') &&
                shipment.description.includes('[') &&
                shipment.description.includes(':') &&
                shipment.description.includes('quantity') &&
                shipment.description.includes('detail') ? (
                    JSON.parse(shipment.description).map((de, index) => {
                        return (
                            <p key={[de.quantity, de.detail, index].join('-')} style={{ wordSpacing: '2px' }}>
                                {de.quantity}-{de.detail}
                            </p>
                        );
                    })
                ) : (
                    <p> {shipment.description}</p>
                )}
                {shipment.shipping_fee_blob.hasOwnProperty('is_fedex') && shipment.shipping_fee_blob.is_fedex && (
                    <>
                        <p>
                            Tracking Number:{' '}
                            <a
                                href={`https://www.fedex.com/fedextrack/?trknbr=${shipment.fedex_data.output.transactionShipments[0].pieceResponses[0].trackingNumber}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: 'white', textDecoration: 'underline' }}
                            >
                                {shipment.fedex_data.output.transactionShipments[0].pieceResponses[0].trackingNumber}
                            </a>
                        </p>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <Button
                                color="info"
                                style={{ minWidth: 'fit-content', whiteSpace: 'nowrap', padding: '0.5rem 1rem' }}
                                onClick={() => {
                                    setSelectedShipmentId(shipment.shipment_id);
                                    setShowFedexLabel(true);
                                }}
                            >
                                <i className="fas fa-print me-2"></i>
                                &nbsp; Print FedEx Label
                            </Button>
                        </div>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <Button
                                color="success"
                                style={{ minWidth: 'fit-content', whiteSpace: 'nowrap', padding: '0.5rem 1rem' }}
                                onClick={() => {
                                    setSelectedShipmentId(shipment.shipment_id);
                                    setShowTracking(true);
                                }}
                            >
                                <i className="fas fa-truck me-2"></i>
                                &nbsp;Track Package
                            </Button>
                        </div>
                    </>
                )}
            </>
        );
    };

    const RoutedDirectItemList = ({ order }) => {
        if (!order || !order.actions) {
            return null;
        }

        return order.actions.hasOwnProperty('pick_up')
            ? order.actions['pick_up'].map((item, index) => {
                  return index === 0 ? (
                      <p key={`pick-${index}-${item}`}>Pick: {item}</p>
                  ) : (
                      <p key={`pick-${index}-${item}`} style={{ textIndent: '30px' }}>
                          {item}
                      </p>
                  );
              })
            : order.actions['drop_off'].map((item, index) => {
                  return index === 0 ? (
                      <p key={`drop-${index}-${item}`}>Drop: {item}</p>
                  ) : (
                      <p key={`drop-${index}-${item}`} style={{ textIndent: '35px' }}>
                          {item}
                      </p>
                  );
              });
    };

    const RoutedDirectItemListTwist = ({ order }) => {
        if (!order || !order.actions || !Array.isArray(order.actions['pick_up'])) {
            return null;
        }

        return order.actions['pick_up'].map((item, index) => {
            if (item.includes('(drop)')) {
                return index === 0 ? (
                    <p key={`twist-drop-${index}-${item}`}>Drop: {item.replace('(drop)', '')}</p>
                ) : (
                    <p key={`twist-drop-${index}-${item}`} style={{ textIndent: '30px' }}>
                        {item.replace('(drop)', '')}
                    </p>
                );
            } else {
                return index === 0 ? (
                    <p key={`twist-pick-${index}-${item}`}>Pick: {item}</p>
                ) : (
                    <p key={`twist-pick-${index}-${item}`} style={{ textIndent: '30px' }}>
                        {item}
                    </p>
                );
            }
        });
    };

    const BasicInfo = ({ order }) => {
        if (!order || !order.location_blob) {
            return null;
        }

        return (
            <>
                <p>{order.location_blob.name}</p>
                <p>{order.location_blob.phone}</p>
                <p>{order.location_blob.email}</p>
                <p>{order.location_blob.address_1}</p>
                <p>
                    {order.location_blob.city}, {order.location_blob.state}, {order.location_blob.zip}
                </p>
            </>
        );
    };

    const BasicInfoDirectPick = ({ shipment }) => {
        const order = Array.isArray(shipment && shipment.Orders)
            ? shipment.Orders.find(od => od && od.actions && od.actions.hasOwnProperty('pick_up'))
            : null;

        if (!order || !order.location_blob) {
            return (
                <>
                    <strong>Pickup Info</strong>
                    <p>Pickup details unavailable for this local shipment snapshot.</p>
                </>
            );
        }

        return (
            <>
                <strong>Pickup Info</strong>
                <p>{order.location_blob.name}</p>
                <p>{order.location_blob.phone}</p>
                <p>{order.location_blob.email}</p>
                <p>{order.location_blob.address_1}</p>
                <p>
                    {order.location_blob.city}, {order.location_blob.state}, {order.location_blob.zip}
                </p>
                <PickOrDropInstructions order={order} />
                {isCustomerShipmentInDriverProgress(shipment.status) && (
                    <p>
                        <strong>Status:</strong> {isOrderCompletedForDisplay(order) ? formatOrderCompletionForDisplay(order) : 'approaching'}
                    </p>
                )}
                {isCustomerShipmentInDriverProgress(shipment.status) && order.estimated_arrival_time && !isOrderCompletedForDisplay(order) && (
                    <p><strong>ETA:</strong> {formatETA(order.estimated_arrival_time)}</p>
                )}
                <PhotoGallery photos={order.photos} buttonText="📷 View Photos" />
            </>
        );
    };

    const BasicInfoDirectDrop = ({ shipment }) => {
        const order = Array.isArray(shipment && shipment.Orders)
            ? shipment.Orders.find(od => od && od.actions && od.actions.hasOwnProperty('drop_off'))
            : null;
        const orderPick = Array.isArray(shipment && shipment.Orders)
            ? shipment.Orders.find(od => od && od.actions && od.actions.hasOwnProperty('pick_up'))
            : null;

        if (!order || !order.location_blob) {
            return (
                <>
                    <strong>Dropoff Info</strong>
                    <p>Dropoff details unavailable for this local shipment snapshot.</p>
                </>
            );
        }

        return (
            <>
                <strong>Dropoff Info</strong>
                <p>{order.location_blob.name}</p>
                <p>{order.location_blob.phone}</p>
                <p>{order.location_blob.email}</p>
                <p>{order.location_blob.address_1}</p>
                <p>
                    {order.location_blob.city}, {order.location_blob.state}, {order.location_blob.zip}
                </p>
                <PickOrDropInstructions order={order} />
                {isCustomerShipmentInDriverProgress(shipment.status) && <WaypointStatus pickOrder={orderPick} currentOrder={order} />}
                <PhotoGallery photos={order.photos} buttonText="📷 View Photos" />
            </>
        );
    };

    const PickOrDropInstructions = ({ order }) => {
        if (!order || !order.actions) {
            return null;
        }

        if (order.actions.hasOwnProperty('pick_up')) {
            return order.pick_up_instructions ? (
                <>
                    <p style={{ textIndent: '10px' }}> Instructions: </p>
                    <p style={{ textIndent: '30px' }}>{order.pick_up_instructions}</p>
                </>
            ) : null;
        } else if (order.actions.hasOwnProperty('drop_off')) {
            return order.drop_off_instructions ? (
                <>
                    <p style={{ textIndent: '10px' }}> Instructions: </p>
                    <p style={{ textIndent: '30px' }}>{order.drop_off_instructions}</p>
                </>
            ) : null;
        }
    };

    const PickOrDropInstructionsTwist = ({ order }) => {
        if (!order || !order.actions || !Array.isArray(order.actions['pick_up'])) {
            return null;
        }

        let isDrop = false;
        order.actions['pick_up'].forEach(item => {
            if (item.includes('(drop)')) {
                isDrop = true;
            }
        });
        return isDrop ? (
            <>
                {order.drop_off_instructions ? (
                    <>
                        <p style={{ textIndent: '10px' }}> Instructions: </p>
                        <p style={{ textIndent: '30px' }}>{order.drop_off_instructions}</p>
                    </>
                ) : null}
            </>
        ) : (
            <>
                {order.pick_up_instructions ? (
                    <>
                        <p style={{ textIndent: '10px' }}> Instructions: </p>
                        <p style={{ textIndent: '30px' }}>{order.pick_up_instructions}</p>
                    </>
                ) : null}
            </>
        );
    };

    const BasicInfoMultidropWaypoint = ({ orders, display }) => {
        const normalizedOrders = Array.isArray(orders) ? orders.filter(order => order && order.actions) : [];

        if (!normalizedOrders.length) {
            return (
                <Col className="col-2.5">
                    <strong>Route Info</strong>
                    <p>Waypoint details unavailable for this local shipment snapshot.</p>
                </Col>
            );
        }

        let pickOrder = normalizedOrders.find(order => order.actions.hasOwnProperty('pick_up')) || normalizedOrders[0];
        let finalDropOrder =
            [...normalizedOrders].reverse().find(order => order.actions.hasOwnProperty('drop_off')) || normalizedOrders[normalizedOrders.length - 1];
        let waypointOrders = normalizedOrders.filter(order => order !== pickOrder && order !== finalDropOrder);

        // Merge consecutive pickup/dropoff waypoints at the same address
        let mergedWaypoints = [];
        for (let i = 0; i < waypointOrders.length; i++) {
            const currentOrder = waypointOrders[i];
            const nextOrder = waypointOrders[i + 1];

            // Check if current is pickup and next is dropoff at same address
            if (
                nextOrder &&
                currentOrder.actual_action === 'pickup' &&
                nextOrder.actual_action === 'dropoff' &&
                currentOrder.location_blob &&
                nextOrder.location_blob &&
                currentOrder.location_blob.address === nextOrder.location_blob.address
            ) {
                // Merge them
                mergedWaypoints.push({
                    type: 'merged',
                    pickupOrder: currentOrder,
                    dropoffOrder: nextOrder,
                    address: currentOrder.location_blob.address
                });
                i++; // Skip next order since we merged it
            } else {
                // Standalone waypoint
                mergedWaypoints.push({
                    type: currentOrder.actual_action === 'dropoff' ? 'dropoff' : 'pickup',
                    order: currentOrder
                });
            }
        }

        return (
            <>
                {pickOrder ? (
                    <Col className="col-2.5">
                        <strong>Pickup Info</strong>
                        <BasicInfo order={pickOrder} />
                        <RoutedDirectItemList order={pickOrder} />
                        <PickOrDropInstructions order={pickOrder} />
                        {(pickOrder.status === 'driver_assigned' || pickOrder.status === 'driver_claimed' || isOrderCompletedForDisplay(pickOrder)) && display && (
                            <p>
                                <strong>Status:</strong> {isOrderCompletedForDisplay(pickOrder) ? formatOrderCompletionForDisplay(pickOrder) : 'approaching'}
                            </p>
                        )}
                        {!isOrderCompletedForDisplay(pickOrder) && (pickOrder.status === 'driver_assigned' || pickOrder.status === 'driver_claimed') && pickOrder.estimated_arrival_time && (
                            <p><strong>ETA:</strong> {formatETA(pickOrder.estimated_arrival_time)}</p>
                        )}
                    </Col>
                ) : null}
                {mergedWaypoints.map((waypoint, idx) => {
                    if (waypoint.type === 'merged') {
                        // Merged pickup/dropoff at same address
                        const { pickupOrder, dropoffOrder } = waypoint;
                        return (
                            <Col key={getWaypointColumnKey(waypoint, idx)} className="col-2.5">
                                <strong>Pickup/Dropoff Info</strong>
                                <BasicInfo order={pickupOrder} />

                                {/* Show pickup items */}
                                <RoutedDirectItemList order={pickupOrder} />
                                <PickOrDropInstructions order={pickupOrder} />

                                {/* Show dropoff items */}
                                <RoutedDirectItemListTwist order={dropoffOrder} />
                                <PickOrDropInstructionsTwist order={dropoffOrder} />

                                {(pickupOrder.status === 'driver_assigned' || pickupOrder.status === 'driver_claimed' || isOrderCompletedForDisplay(pickupOrder)) && display && (
                                    <WaypointStatus pickOrder={pickOrder} currentOrder={pickupOrder} />
                                )}
                            </Col>
                        );
                    } else if (waypoint.type === 'dropoff') {
                        // Standalone dropoff waypoint
                        const { order } = waypoint;
                        return (
                            <Col key={getWaypointColumnKey(waypoint, idx)} className="col-2.5">
                                <strong>Dropoff Info</strong>
                                <BasicInfo order={order} />

                                <RoutedDirectItemListTwist order={order} />
                                <PickOrDropInstructionsTwist order={order} />

                                {(order.status === 'driver_assigned' || order.status === 'driver_claimed' || isOrderCompletedForDisplay(order)) && display && (
                                    <WaypointStatus pickOrder={pickOrder} currentOrder={order} />
                                )}
                            </Col>
                        );
                    } else {
                        // Standalone pickup waypoint
                        const { order } = waypoint;
                        return (
                            <Col key={getWaypointColumnKey(waypoint, idx)} className="col-2.5">
                                <strong>Pickup Info</strong>
                                <BasicInfo order={order} />

                                <RoutedDirectItemList order={order} />
                                <PickOrDropInstructions order={order} />

                                {(order.status === 'driver_assigned' || order.status === 'driver_claimed' || order.status === 'order_completed') && display && (
                                    <WaypointStatus pickOrder={pickOrder} currentOrder={order} />
                                )}
                            </Col>
                        );
                    }
                })}
                {finalDropOrder && finalDropOrder !== pickOrder ? (
                    <Col className="col-2.5">
                        <strong>Dropoff Info</strong>
                        <BasicInfo order={finalDropOrder} />
                        <RoutedDirectItemList order={finalDropOrder} />
                        <PickOrDropInstructions order={finalDropOrder} />
                        {(finalDropOrder.status === 'driver_assigned' || finalDropOrder.status === 'driver_claimed' || finalDropOrder.status === 'order_completed') && display && (
                            <WaypointStatus pickOrder={pickOrder} currentOrder={finalDropOrder} />
                        )}
                    </Col>
                ) : null}
            </>
        );
    };

    const BasicInfoRoutedWaypoint = ({ orders, display }) => {
        let pickOrders = [];
        let firstPickOrder;
        let middlePickOrder;
        let middleDropOrder;
        let finalDropOrder;
        let hasMiddleDrop = false;

        orders.forEach(order => {
            if (order.actions.hasOwnProperty('drop_off')) {
                finalDropOrder = order;
            } else if (order.actions.hasOwnProperty('pick_up')) {
                if (order.actions['pick_up'][0].includes('drop')) {
                    middleDropOrder = order;
                    hasMiddleDrop = true;
                } else {
                    pickOrders.push(order);
                }
            }
        });

        if (orders.length === 4) {
            pickOrders.forEach(po => {
                if (po.location_blob.address === middleDropOrder.location_blob.address) {
                    middlePickOrder = po;
                } else {
                    firstPickOrder = po;
                }
            });
        } else if (orders.length === 3) {
            if (hasMiddleDrop) {
                firstPickOrder = pickOrders[0];
            } else {
                firstPickOrder = pickOrders[0];
                middlePickOrder = pickOrders[1];
            }
        }

        return (
            <>
                <Col className="col-2.5">
                    <strong>Pickup Info</strong>
                    <BasicInfo order={firstPickOrder} />
                    <RoutedDirectItemList order={firstPickOrder} />
                    {(firstPickOrder.status === 'driver_assigned' || firstPickOrder.status === 'driver_claimed' || isOrderCompletedForDisplay(firstPickOrder)) && display && (
                        <p>
                            <strong>Status:</strong> {isOrderCompletedForDisplay(firstPickOrder) ? formatOrderCompletionForDisplay(firstPickOrder) : 'approaching'}
                        </p>
                    )}
                    {!isOrderCompletedForDisplay(firstPickOrder) && (firstPickOrder.status === 'driver_assigned' || firstPickOrder.status === 'driver_claimed') && firstPickOrder.estimated_arrival_time && (
                        <p>ETA: {formatETA(firstPickOrder.estimated_arrival_time)}</p>
                    )}
                </Col>

                <Col className="col-2.5">
                    <strong>Pickup/Dropoff Info</strong>
                    <BasicInfo order={middlePickOrder ? middlePickOrder : middleDropOrder} />
                    {orders.length === 3 ? (
                        !hasMiddleDrop ? (
                            <>
                                <RoutedDirectItemList order={middlePickOrder} />
                                <PickOrDropInstructionsTwist order={middlePickOrder} />
                                {(middlePickOrder.status === 'driver_assigned' || middlePickOrder.status === 'driver_claimed' || middlePickOrder.status === 'order_completed') &&
                                    display && <WaypointStatus pickOrder={firstPickOrder} currentOrder={middlePickOrder} />}
                            </>
                        ) : (
                            <>
                                <RoutedDirectItemListTwist order={middleDropOrder} />
                                <PickOrDropInstructionsTwist order={middleDropOrder} />
                                {(middleDropOrder.status === 'driver_assigned' || middleDropOrder.status === 'driver_claimed' || middleDropOrder.status === 'order_completed') &&
                                    display && <WaypointStatus pickOrder={firstPickOrder} currentOrder={middleDropOrder} />}
                            </>
                        )
                    ) : (
                        <div>
                            <RoutedDirectItemList order={middlePickOrder} />
                            <PickOrDropInstructions order={middlePickOrder} />
                            {(middlePickOrder.status === 'driver_assigned' || middlePickOrder.status === 'driver_claimed' || middlePickOrder.status === 'order_completed') &&
                                display && <WaypointStatus pickOrder={firstPickOrder} currentOrder={middlePickOrder} />}
                            <RoutedDirectItemListTwist order={middleDropOrder} />
                            <PickOrDropInstructionsTwist order={middleDropOrder} />

                            {(middleDropOrder.status === 'driver_assigned' || middleDropOrder.status === 'driver_claimed' || middleDropOrder.status === 'order_completed') &&
                                display && <WaypointStatus pickOrder={firstPickOrder} currentOrder={middleDropOrder} />}
                        </div>
                    )}
                </Col>
                <Col className="col-2.5">
                    <strong>Dropoff Info</strong>
                    <BasicInfo order={finalDropOrder} />
                    <RoutedDirectItemList order={finalDropOrder} />
                    <PickOrDropInstructions order={finalDropOrder} />
                    {(finalDropOrder.status === 'driver_assigned' || finalDropOrder.status === 'driver_claimed' || finalDropOrder.status === 'order_completed') && display && (
                        <WaypointStatus pickOrder={firstPickOrder} currentOrder={finalDropOrder} />
                    )}
                </Col>
            </>
        );
    };

    const LoadDetails = ({ shipment }) => {
        let blob = shipment.shipping_fee_blob.calculated;
        let shipInfo = shipment.shipping_fee_blob.shipment;
        const demurrage = shipment && shipment.billing_details && shipment.billing_details.demurrage ? shipment.billing_details.demurrage : null;
        const demurrageApproval = demurrage && demurrage.approval ? demurrage.approval : null;
        const demurrageApplication = demurrage && demurrage.application ? demurrage.application : null;
        const liveDemurrageClockContext = useMemo(
            () => getCustomerLiveDemurrageClockContext(shipment, demurrage, shipInfo),
            [shipment, demurrage, shipInfo]
        );
        const [liveDemurrageNowMs, setLiveDemurrageNowMs] = useState(() => Date.now());

        useEffect(() => {
            if (!liveDemurrageClockContext) {
                return undefined;
            }

            setLiveDemurrageNowMs(Date.now());
            const intervalId = window.setInterval(() => {
                setLiveDemurrageNowMs(Date.now());
            }, 30000);

            return () => {
                window.clearInterval(intervalId);
            };
        }, [liveDemurrageClockContext]);

        const liveWaitMinutes = useMemo(() => {
            const arrivalTimestamp = liveDemurrageClockContext && liveDemurrageClockContext.arrivalAt
                ? parseLiveDemurrageTimestamp(liveDemurrageClockContext.arrivalAt)
                : null;

            if (!arrivalTimestamp) {
                return null;
            }

            return Math.max(0, Math.floor((liveDemurrageNowMs - arrivalTimestamp.getTime()) / 60000));
        }, [liveDemurrageClockContext, liveDemurrageNowMs]);

        const demurrageStops = useMemo(() => (demurrage && Array.isArray(demurrage.stops) ? demurrage.stops : []), [demurrage]);

        const displayedDemurrageStops = useMemo(() => {
            if (demurrageStops.length === 0) {
                return [];
            }

            return demurrageStops.map(stop => {
                const isActiveStop = Boolean(
                    stop
                    && liveDemurrageClockContext
                    && liveWaitMinutes != null
                    && stop.stop_id
                    && stop.stop_id === liveDemurrageClockContext.stopId
                );

                if (!isActiveStop) {
                    return stop;
                }

                return {
                    ...stop,
                    wait_minutes: Math.max(Number(stop.wait_minutes || 0), liveWaitMinutes),
                    free_minutes: Math.max(Number(stop.free_minutes || 0), Number(liveDemurrageClockContext.freeMinutes || 0)),
                    billable_minutes: Math.floor(
                        Math.max(
                            0,
                            Math.max(Number(stop.wait_minutes || 0), liveWaitMinutes)
                            - Math.max(Number(stop.free_minutes || 0), Number(liveDemurrageClockContext.freeMinutes || 0))
                        ) / normalizeDemurrageIncrementMinutes(demurrage && demurrage.policy && demurrage.policy.increment_minutes)
                    ) * normalizeDemurrageIncrementMinutes(demurrage && demurrage.policy && demurrage.policy.increment_minutes),
                    amount: getDemurrageAmountFromTiming({
                        hourlyRate: stop.hourly_rate || (demurrage && demurrage.hourly_rate),
                        waitMinutes: Math.max(Number(stop.wait_minutes || 0), liveWaitMinutes),
                        freeMinutes: Math.max(Number(stop.free_minutes || 0), Number(liveDemurrageClockContext.freeMinutes || 0)),
                        incrementMinutes: demurrage && demurrage.policy && demurrage.policy.increment_minutes,
                    }),
                };
            });
        }, [demurrageStops, demurrage, liveDemurrageClockContext, liveWaitMinutes]);

        const displayedWaitMinutes = displayedDemurrageStops.length > 0
            ? displayedDemurrageStops.reduce((sum, stop) => sum + Number((stop && stop.wait_minutes) || 0), 0)
            : liveWaitMinutes == null
            ? Number(demurrage && demurrage.total_wait_minutes ? demurrage.total_wait_minutes : 0)
            : Math.max(Number(demurrage && demurrage.total_wait_minutes ? demurrage.total_wait_minutes : 0), liveWaitMinutes);
        const displayedFreeMinutes = displayedDemurrageStops.length > 0
            ? displayedDemurrageStops.reduce((sum, stop) => sum + Number((stop && stop.free_minutes) || 0), 0)
            : liveDemurrageClockContext
            ? Math.max(Number(demurrage && demurrage.total_free_minutes ? demurrage.total_free_minutes : 0), Number(liveDemurrageClockContext.freeMinutes || 0))
            : Number(demurrage && demurrage.total_free_minutes ? demurrage.total_free_minutes : 0);
        const hasPendingDemurrageReview = Boolean(
            demurrage && (
                demurrage.review_required === true
                || demurrage.status === 'review_required'
                || (demurrage.materialization && demurrage.materialization.status === 'awaiting_review')
                || Number(demurrage.review_billable_minutes || 0) > 0
                || Number(demurrage.review_amount || 0) > 0
            ) && (!demurrageApproval || demurrageApproval.status === 'pending')
        );
        const liveDemurrageDisplayAmount = Math.max(
            getDemurrageDisplayAmount(demurrage, demurrageApplication, blob),
            roundDemurrageCurrency(displayedDemurrageStops.reduce((sum, stop) => sum + Number((stop && stop.amount) || 0), 0)),
            getDemurrageAmountFromTiming({
                hourlyRate: demurrage && demurrage.hourly_rate,
                waitMinutes: displayedWaitMinutes,
                freeMinutes: displayedFreeMinutes,
                incrementMinutes: demurrage && demurrage.policy && demurrage.policy.increment_minutes,
            })
        );
        const demurrageReviewReasons = demurrage && Array.isArray(demurrage.stops)
            ? Array.from(new Set(demurrage.stops.flatMap(stop => (stop.review_reasons || []))))
            : [];
        const formatDurationMinutes = minutes => {
            const safeMinutes = Math.max(0, Number(minutes || 0));
            const hours = Math.floor(safeMinutes / 60);
            const remainingMinutes = safeMinutes % 60;

            if (hours > 0) {
                return `${hours}h ${remainingMinutes}m`;
            }

            return `${remainingMinutes}m`;
        };
        const formatCurrency = value => formatCurrencyAmount(value);
        const demurrageStatusLabel = demurrage
            ? demurrage.status === 'review_required'
                ? 'Review Required'
                : demurrage.status === 'accruing'
                ? 'Accruing'
                : demurrage.total_billable_minutes > 0
                ? 'Calculated'
                : 'Within Free Time'
            : null;
        return (
            <>
                {shipInfo.hasOwnProperty('is_ltl') && shipInfo.is_ltl ? (
                    <p>
                        <strong>LTL Freight </strong>
                    </p>
                ) : null}
                {shipment.merchant_order_id ? (
                    <p>
                        Store Order ID: <strong>{'  ' + shipment.merchant_order_id} </strong>{' '}
                    </p>
                ) : null}
                <p>
                    Vehicle Type Suggested:{' '}
                    <strong>
                        {' ' + getVehicleTypeLabel(shipment.vehicle_type)}
                    </strong>
                </p>
                <p>
                    Estimated Weight: <strong>{'  ' + shipment.weight} lbs.</strong>
                </p>
                {hasVisibleGophrCount(shipment.num_of_gophrs) ? (
                    <p>
                        Gophrs Needed: <strong>{'  ' + shipment.num_of_gophrs}</strong>
                    </p>
                ) : null}
                {getShipmentPlacedByDisplayName(shipment) ? (
                    <p>
                        Placed By: <strong>{'  ' + getShipmentPlacedByDisplayName(shipment)} </strong>{' '}
                    </p>
                ) : null}
                {shipment.scheduled_for && (
                    <p>
                        Scheduled For: <strong>{formatTimestamp(shipment.scheduled_for)}</strong>{' '}
                    </p>
                )}
                {/* <p>
          Delivery Fee: <strong>{'  $' + shipment.shipping_fee.toFixed(2)}</strong>
        </p> */}
                {shipment.is_roundtrip && (
                    <p>
                        {' '}
                        <strong>round trip</strong>{' '}
                    </p>
                )}

                <>
                    <p>{shipment.shipping_fee_blob.shipment.is_personal_protective_equipment && 'PPE required'}</p>
                    <p>{blob.hasOwnProperty('hand_load_fee') && blob.hand_load_fee >= 0 && 'Hand Load: $' + blob.hand_load_fee.toFixed(2)}</p>
                    <p>{blob.hasOwnProperty('dolly_fee') && 'Dolly: $' + blob.dolly_fee.toFixed(2)}</p>
                    <p>{blob.hasOwnProperty('pallet_jack_fee') && 'Pallet Jack: $' + blob.pallet_jack_fee.toFixed(2)}</p>
                    <p>{blob.hasOwnProperty('twic_card_fee') && 'TWIC Card: $' + blob.twic_card_fee.toFixed(2)}</p>
                    <p>{blob.hasOwnProperty('liftgate_fee') && 'Liftgate: $' + blob.liftgate_fee.toFixed(2)}</p>
                    <p>{blob.hasOwnProperty('hazmat_fee') && 'Hazmat: $' + blob.hazmat_fee.toFixed(2)}</p>
                    <p>{blob.hasOwnProperty('weather_protection_fee') && 'Weather Protection: $' + blob.weather_protection_fee.toFixed(2)}</p>
                    <p>{blob.hasOwnProperty('medical_fee') && 'Medical: $' + blob.medical_fee.toFixed(2)}</p>
                </>

                <p>
                    Total Delivery Fee:{' '}
                    <strong>{'  ' + getShipmentDeliveryFeeDisplay(shipment)}</strong>
                </p>
                {demurrage ? (
                    <>
                        <p>
                            <strong>Demurrage Status:</strong> {demurrageStatusLabel}
                        </p>
                        <p>
                            <strong>Wait Time:</strong> {formatDurationMinutes(displayedWaitMinutes)}/{formatDurationMinutes(displayedFreeMinutes)} free
                        </p>
                        <p>
                            <strong>Demurrage Price:</strong> {formatCurrency(liveDemurrageDisplayAmount)}
                        </p>
                        {displayedDemurrageStops.map((stop, index) => {
                            if (!stop) {
                                return null;
                            }

                            const stopLabel = stop.stop_type ? String(stop.stop_type) : `Stop ${index + 1}`;

                            return (
                                <p key={`${stop.stop_id || stopLabel}-${index}`}>
                                    <strong>{stopLabel}:</strong> {formatDurationMinutes(stop.wait_minutes)}/{formatDurationMinutes(stop.free_minutes)} free
                                </p>
                            );
                        })}
                        {demurrage.total_billable_minutes > 0 ? (
                            <p>
                                <strong>Applied Demurrage:</strong> {formatDurationMinutes(demurrage.total_billable_minutes)} {formatCurrency(demurrage.total_amount)}
                            </p>
                        ) : null}
                        {hasPendingDemurrageReview ? (
                            <p style={{ color: '#ffb347' }}>
                                <strong>Needs Review:</strong> {formatDurationMinutes(demurrage.review_billable_minutes)} {formatCurrency(demurrage.review_amount)}
                            </p>
                        ) : null}
                        {demurrage.preview_billable_minutes > 0 ? (
                            <p style={{ color: '#57c7ff' }}>
                                <strong>Preview:</strong> {formatDurationMinutes(demurrage.preview_billable_minutes)} {formatCurrency(demurrage.preview_amount)}
                            </p>
                        ) : null}
                        {demurrageReviewReasons.length > 0 ? (
                            <p>
                                <strong>Review Flags:</strong> {demurrageReviewReasons.join(', ')}
                            </p>
                        ) : null}
                    </>
                ) : null}
            </>
        );
    };

    const AIRecommendation = ({ shipment }) => {
        if (!shipment.ai_recommendation) {
            return null;
        }

        return (
            <>
                <Row>
                    <Col className="col-3" style={{ textAlign: 'left' }}>
                        <strong>AI Recommendation</strong>
                        <p>{shipment.ai_recommendation.recommendation}</p>
                    </Col>
                </Row>
            </>
        );
    };

    const getDriverProfilePhotoUrl = ({ shipment, routeDriver, driverBlob }) => {
        const shipmentDriver = shipment && shipment.driver ? shipment.driver : null;
        const driverActionDriver = shipmentDriver && shipmentDriver.LastDriverActions && shipmentDriver.LastDriverActions.Driver
            ? shipmentDriver.LastDriverActions.Driver
            : null;
        const listedDriver = Array.isArray(shipment && shipment.Drivers) && shipment.Drivers.length > 0 ? shipment.Drivers[0] : null;
        const listedDriverBlob = listedDriver && listedDriver.driver_blob ? listedDriver.driver_blob : null;
        const routeDriverDriver = routeDriver && routeDriver.Driver ? routeDriver.Driver : null;
        const collectEntityPhotoCandidates = entity => {
            if (!entity || typeof entity !== 'object') {
                return [];
            }

            const documents = Array.isArray(entity.UserDocuments) ? entity.UserDocuments : [];
            const visibility = entity.driver_visibility || {};

            return [
                ...documents
                    .filter(document => document && (document.doc_type === 'profile_photo' || document.doc_type === 'profile_picture'))
                    .map(document => document && (document.url || document.signed_url || document.path_id || '')),
                entity.profile_photo_url,
                entity.profile_picture_url,
                entity.profile_image_url,
                entity.photo,
                entity.photo_url,
                entity.avatar,
                entity.avatar_url,
                entity.image,
                entity.image_url,
                visibility.profile_photo_url,
                visibility.profile_picture_url,
                visibility.profile_image_url,
            ];
        };
        const profileDocuments = [
            ...(Array.isArray(routeDriver && routeDriver.UserDocuments) ? routeDriver.UserDocuments : []),
            ...(Array.isArray(routeDriver && routeDriver.User && routeDriver.User.UserDocuments) ? routeDriver.User.UserDocuments : []),
            ...(Array.isArray(driverBlob && driverBlob.UserDocuments) ? driverBlob.UserDocuments : []),
            ...(Array.isArray(driverActionDriver && driverActionDriver.UserDocuments) ? driverActionDriver.UserDocuments : []),
            ...(Array.isArray(routeDriverDriver && routeDriverDriver.UserDocuments) ? routeDriverDriver.UserDocuments : []),
            ...(Array.isArray(listedDriver && listedDriver.UserDocuments) ? listedDriver.UserDocuments : []),
            ...(Array.isArray(listedDriverBlob && listedDriverBlob.UserDocuments) ? listedDriverBlob.UserDocuments : []),
        ]
            .filter(document => document && (document.doc_type === 'profile_photo' || document.doc_type === 'profile_picture'))
            .slice()
            .sort((left, right) => {
                const leftTimestamp = new Date(left && left.updated_at ? left.updated_at : 0).getTime();
                const rightTimestamp = new Date(right && right.updated_at ? right.updated_at : 0).getTime();
                return rightTimestamp - leftTimestamp;
            });

        const photoCandidates = [
            ...profileDocuments.map(document => document && (document.url || document.signed_url || document.path_id || '')),
            ...collectEntityPhotoCandidates(shipmentDriver),
            ...collectEntityPhotoCandidates(shipmentDriver && shipmentDriver.User),
            ...collectEntityPhotoCandidates(driverActionDriver),
            ...collectEntityPhotoCandidates(routeDriver),
            ...collectEntityPhotoCandidates(routeDriver && routeDriver.User),
            ...collectEntityPhotoCandidates(routeDriverDriver),
            ...collectEntityPhotoCandidates(driverBlob),
            ...collectEntityPhotoCandidates(listedDriver),
            ...collectEntityPhotoCandidates(listedDriverBlob),
        ];

        const normalizedCandidates = photoCandidates.reduce((result, candidate) => {
            const normalizedCandidate = normalizeAssetUrl(candidate);
            if (normalizedCandidate && !result.includes(normalizedCandidate)) {
                result.push(normalizedCandidate);
            }
            return result;
        }, []);

        const unsignedCandidate = normalizedCandidates.find(candidate => !isSignedAssetUrl(candidate));
        if (unsignedCandidate) {
            return unsignedCandidate;
        }

        return normalizedCandidates[0] || '';
    };

    const buildDriverPhotoLookup = ({ shipment, routeDriver, driverBlob }) => {
        const shipmentDriver = shipment && shipment.driver ? shipment.driver : null;
        const routeDriverDriver = routeDriver && routeDriver.Driver ? routeDriver.Driver : null;
        const listedDriver = Array.isArray(shipment && shipment.Drivers) && shipment.Drivers.length > 0 ? shipment.Drivers[0] : null;
        const listedDriverBlob = listedDriver && listedDriver.driver_blob ? listedDriver.driver_blob : null;

        const allIdentifiers = [
            routeDriver && routeDriver.driver_id,
            routeDriver && routeDriver.auth0_id,
            routeDriver && routeDriver.user_id,
            routeDriver && routeDriver.User && routeDriver.User.auth0_id,
            routeDriver && routeDriver.User && routeDriver.User.user_id,
            routeDriverDriver && routeDriverDriver.driver_id,
            routeDriverDriver && routeDriverDriver.auth0_id,
            routeDriverDriver && routeDriverDriver.user_id,
            driverBlob && driverBlob.driver_id,
            driverBlob && driverBlob.auth0_id,
            driverBlob && driverBlob.user_id,
            shipmentDriver && shipmentDriver.driver_id,
            shipmentDriver && shipmentDriver.auth0_id,
            shipmentDriver && shipmentDriver.user_id,
            shipmentDriver && shipmentDriver.User && shipmentDriver.User.auth0_id,
            shipmentDriver && shipmentDriver.User && shipmentDriver.User.user_id,
            listedDriver && listedDriver.driver_id,
            listedDriver && listedDriver.auth0_id,
            listedDriver && listedDriver.user_id,
            listedDriverBlob && listedDriverBlob.driver_id,
            listedDriverBlob && listedDriverBlob.auth0_id,
            listedDriverBlob && listedDriverBlob.user_id,
        ]
            .map(identifier => (identifier != null ? String(identifier).trim() : ''))
            .filter(Boolean);

        const complianceIdentifiers = Array.from(new Set(allIdentifiers));
        const uuidIdentifiers = complianceIdentifiers.filter(identifier => DRIVER_UUID_PATTERN.test(identifier));
        const normalizePhoneDigits = phoneValue => {
            const digitsOnly = phoneValue != null ? String(phoneValue).replace(/\D/g, '') : '';
            if (!digitsOnly) {
                return '';
            }

            return digitsOnly.length > 10 ? digitsOnly.slice(-10) : digitsOnly;
        };
        const phoneDigits = [
            driverBlob && driverBlob.phone,
            routeDriver && routeDriver.phone,
            routeDriver && routeDriver.User && routeDriver.User.phone,
            routeDriverDriver && routeDriverDriver.phone,
            shipmentDriver && shipmentDriver.phone,
            shipmentDriver && shipmentDriver.User && shipmentDriver.User.phone,
            listedDriver && listedDriver.phone,
            listedDriverBlob && listedDriverBlob.phone,
        ]
            .map(phoneValue => normalizePhoneDigits(phoneValue))
            .find(Boolean) || '';
        const name = [
            (driverBlob && driverBlob.first_name) || (routeDriver && routeDriver.first_name) || (routeDriver && routeDriver.User && routeDriver.User.firstname) || '',
            (driverBlob && driverBlob.last_name) || (routeDriver && routeDriver.last_name) || (routeDriver && routeDriver.User && routeDriver.User.lastname) || '',
        ]
            .join(' ')
            .trim();
        const fallbackCacheKey =
            (phoneDigits ? `phone:${phoneDigits}` : '') ||
            (name ? `name:${name.toLowerCase()}` : '') ||
            (shipment && shipment.shipment_id ? `shipment:${shipment.shipment_id}` : '');
        const cacheKey = uuidIdentifiers[0] || complianceIdentifiers[0] || fallbackCacheKey;
        const lookupSignature = [
            cacheKey,
            complianceIdentifiers.join('|'),
            phoneDigits,
            name.toLowerCase(),
        ].join('::');

        return {
            cacheKey,
            uuidIdentifiers,
            complianceIdentifiers,
            phoneDigits,
            name,
            lookupSignature,
        };
    };

    const DriverInfo = ({ shipment, showEta = true }) => {
        const routeDriver = getPrimaryRouteDriver(shipment);
        const driverBlob = routeDriver && routeDriver.driver_blob ? routeDriver.driver_blob : null;
        const driverReviewStats = getDriverReviewStatsForRouteDriver(routeDriver, shipment);
        const driverPhotoLookup = buildDriverPhotoLookup({ shipment, routeDriver, driverBlob });
        const vehicleBlob = routeDriver && routeDriver.vehicle_blob ? routeDriver.vehicle_blob : null;
        const driverName = driverBlob ? `${driverBlob.first_name || ''} ${driverBlob.last_name || ''}`.trim() : '';
        const driverProfilePhotoUrl =
            getDriverProfilePhotoUrl({ shipment, routeDriver, driverBlob }) ||
            (driverPhotoLookup.cacheKey && driverProfilePhotoOverridesById[driverPhotoLookup.cacheKey] ? driverProfilePhotoOverridesById[driverPhotoLookup.cacheKey] : '');
        const customerDriverUnreadCount = getClaimedOrderChatUnreadCountForThread({
            shipmentId: shipment && shipment.shipment_id,
            threadType: 'customer_driver',
            participantAuth0Id: resolveShipmentCustomerParticipantAuth0Id(shipment),
        });
        useEffect(() => {
            if (!driverProfilePhotoUrl && driverPhotoLookup.cacheKey) {
                fetchDriverProfilePhotoOverride(driverPhotoLookup);
            }
        }, [driverPhotoLookup.lookupSignature, driverProfilePhotoUrl]);

        return (
            <>
                <strong>Driver</strong>
                {routeDriver && driverBlob ? (
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <p style={{ marginBottom: 0 }}>{driverName}</p>
                        </div>
                        {driverProfilePhotoUrl ? (
                            <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '4px' }}>
                                <img
                                    src={driverProfilePhotoUrl}
                                    alt={`${driverName || 'Driver'} profile`}
                                    referrerPolicy="no-referrer"
                                    style={{
                                        border: '1px solid rgba(255, 255, 255, 0.25)',
                                        borderRadius: '50%',
                                        height: '51px',
                                        objectFit: 'cover',
                                        width: '51px',
                                    }}
                                />
                            </div>
                        ) : null}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <p style={{ marginBottom: 0 }}>{formatPhoneNumber(driverBlob.phone)}</p>
                            <button
                                type="button"
                                onClick={() => openShipmentThreadChat(shipment, 'customer_driver')}
                                title="Open driver chat"
                                aria-label="Open driver chat"
                                style={{
                                    alignItems: 'center',
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#f7941d',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    lineHeight: 1,
                                    padding: 0,
                                }}
                            >
                                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FontAwesomeIcon icon={faComment} style={{ fontSize: '1.15em' }} />
                                    {renderMerchantChatUnreadBadge(customerDriverUnreadCount)}
                                </span>
                            </button>
                        </div>
                        <p>
                            {vehicleBlob
                                ? vehicleBlob.year + ' ' + vehicleBlob.make + ' ' + vehicleBlob.model + ' (' + vehicleBlob.color + ')'
                                : null}
                        </p>
                        {driverReviewStats ? (
                            <div style={{ marginTop: '-8px', marginBottom: '8px', width: '100%' }}>
                                <div style={{ alignItems: 'center', color: '#6c757d', display: 'flex', flexWrap: 'nowrap', fontSize: '0.9rem', gap: '6px', justifyContent: 'center', whiteSpace: 'nowrap', width: '100%' }}>
                                    <FaStar style={{ color: '#f7941d' }} />
                                    <span>{formatDriverRatingSummary(driverReviewStats)}</span>
                                </div>
                                <div style={{ color: '#8d99ae', fontSize: '0.82rem', lineHeight: 1.3, whiteSpace: 'nowrap', width: '100%' }}>
                                    {formatDriverCompletedOrdersLabel(driverReviewStats.completed_orders_count)}
                                </div>
                            </div>
                        ) : null}
                        {shipmentClick.driver && <p>{shipmentClick.driver.User.permit ? 'Permit: ' + shipmentClick.driver.User.permit : null}</p>}
                        {showEta ? <ShipmentEtaSummary shipment={shipment} /> : null}
                    </div>
                ) : shipment.status === 'shipment_claimed' && shipment.shipping_fee_blob.hasOwnProperty('is_fedex') && shipment.shipping_fee_blob.is_fedex ? (
                    <p>Fedex</p>
                ) : (
                    <p>Awaiting for driver</p>
                )}
            </>
        );
    };

    const SchoolLunchInfo = ({ order, shipment }) => {
        const routeDriver = getPrimaryRouteDriver(shipment);
        const driverBlob = routeDriver && routeDriver.driver_blob ? routeDriver.driver_blob : null;
        const driverPhotoLookup = buildDriverPhotoLookup({ shipment, routeDriver, driverBlob });
        const driverName = driverBlob ? `${driverBlob.first_name || ''} ${driverBlob.last_name || ''}`.trim() : '';
        const driverProfilePhotoUrl =
            getDriverProfilePhotoUrl({ shipment, routeDriver, driverBlob }) ||
            (driverPhotoLookup.cacheKey && driverProfilePhotoOverridesById[driverPhotoLookup.cacheKey] ? driverProfilePhotoOverridesById[driverPhotoLookup.cacheKey] : '');
        const customerDriverUnreadCount = getClaimedOrderChatUnreadCountForThread({
            shipmentId: shipment && shipment.shipment_id,
            threadType: 'customer_driver',
            participantAuth0Id: resolveShipmentCustomerParticipantAuth0Id(shipment),
        });
        useEffect(() => {
            if (!driverProfilePhotoUrl && driverPhotoLookup.cacheKey) {
                fetchDriverProfilePhotoOverride(driverPhotoLookup);
            }
        }, [driverPhotoLookup.lookupSignature, driverProfilePhotoUrl]);

        return (
            <>
                <div style={{ paddingTop: '10px', paddingBottom: '10px', marginLeft: '10px', textAlign: 'left' }}>
                    <Row>
                        <Col className="col-3">
                            <strong>Dining Period</strong>
                            <p>{order.actions.pick_up[0].slice(15).slice(0, -3)}</p>
                        </Col>

                        <Col className="col-3">
                            <strong>Quantity - Meal Type</strong>
                            {order.actions.pick_up.map((meal, index) => {
                                if (index > 0) {
                                    return <p>{meal}</p>;
                                }

                                return null;
                            })}
                        </Col>
                        <Col className="col-3">
                            <strong>Amount</strong>
                            <p>${order.subtotal.toFixed(2)}</p>
                        </Col>

                        {order.status === 'order_created' && (
                            <Col className="col-3">
                                <div>
                                    <ButtonGroup>
                                        <Button
                                            style={{ width: `100%`, textAlign: 'center' }}
                                            color="info"
                                            name="ready"
                                            className={'btn btn-simple'}
                                            onClick={e => {
                                                e.stopPropagation();
                                                readyOrder(order);
                                            }}
                                        >
                                            Ready
                                        </Button>
                                    </ButtonGroup>
                                </div>
                            </Col>
                        )}
                        {(shipment.status === 'shipment_claimed' || shipment.status === 'shipment_assigned') && (
                            <Col className="col-3">
                                <strong>Driver</strong>
                                {routeDriver && driverBlob ? (
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                            <p style={{ marginBottom: 0 }}>{driverName}</p>
                                        </div>
                                        {driverProfilePhotoUrl ? (
                                            <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '4px' }}>
                                                <img
                                                    src={driverProfilePhotoUrl}
                                                    alt={`${driverName || 'Driver'} profile`}
                                                    referrerPolicy="no-referrer"
                                                    style={{
                                                        border: '1px solid rgba(255, 255, 255, 0.25)',
                                                        borderRadius: '50%',
                                                        height: '51px',
                                                        objectFit: 'cover',
                                                        width: '51px',
                                                    }}
                                                />
                                            </div>
                                        ) : null}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                            <p style={{ marginBottom: 0 }}>{formatPhoneNumber(driverBlob.phone)}</p>
                                            <button
                                                type="button"
                                                onClick={() => openShipmentThreadChat(shipment, 'customer_driver')}
                                                title="Open driver chat"
                                                aria-label="Open driver chat"
                                                style={{
                                                    alignItems: 'center',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#f7941d',
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    justifyContent: 'center',
                                                    lineHeight: 1,
                                                    padding: 0,
                                                }}
                                            >
                                                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <FontAwesomeIcon icon={faComment} style={{ fontSize: '1.15em' }} />
                                                    {renderMerchantChatUnreadBadge(customerDriverUnreadCount)}
                                                </span>
                                            </button>
                                        </div>
                                        <ShipmentEtaSummary shipment={shipment} />
                                    </div>
                                ) : shipment.shipping_fee_blob.hasOwnProperty('is_fedex') && shipment.shipping_fee_blob.is_fedex ? (
                                    <p>Fedex</p>
                                ) : (
                                    <p>Awaiting for driver</p>
                                )}
                            </Col>
                        )}
                        {shipment.status === 'shipment_completed' && (
                            <Col className="col-3">
                                <strong>Status</strong>
                                <p>{shipment.status === 'shipment_completed' ? 'Complete' : 'None Found'}</p>
                                {shipment.ShipmentHandshakes.length > 0 && (
                                    <>
                                        <img
                                            src={shipment.ShipmentHandshakes[0].url}
                                            alt="Signature"
                                            height={100}
                                            onClick={() => {
                                                setShowSignature(true);
                                                setSignatureUrl(shipment.ShipmentHandshakes[0].url);
                                            }}
                                        />
                                        {/* <p>Click to see full image</p> */}
                                    </>
                                )}
                            </Col>
                        )}
                    </Row>
                </div>
            </>
        );
    };

    const WaypointStatus = ({ pickOrder, currentOrder }) => {
        if (!pickOrder || !currentOrder) {
            return null;
        }

        return (
            <>
                <p>
                    <strong>Status:</strong>
                    {isOrderCompletedForDisplay(pickOrder) && isOrderCompletedForDisplay(currentOrder)
                        ? formatOrderCompletionForDisplay(currentOrder)
                        : isOrderCompletedForDisplay(pickOrder) && !isOrderCompletedForDisplay(currentOrder)
                        ? ' delivering'
                        : ' waiting'}
                </p>
                {!isOrderCompletedForDisplay(currentOrder) && (currentOrder.status === 'driver_assigned' || currentOrder.status === 'driver_claimed') && currentOrder.estimated_arrival_time && (
                    <p><strong>ETA:</strong> {formatETA(currentOrder.estimated_arrival_time)}</p>
                )}
            </>
        );
    };

    const QuoteOrderButtons = ({ shipment }) => {
        const actionLabel = shipment && shipment.scheduled_for ? 'Schedule' : 'Ready for Pick Up';

        return (
            <>
                <Row>
                    <ButtonGroup>
                        {shipment.shipping_fee === 0 ? (
                            <Button style={{ width: `100%`, textAlign: 'center' }} color="info" name="waitingApproval" className={'btn btn-simple'} disabled>
                                Waiting Approval
                            </Button>
                        ) : (
                            <Button style={{ width: `100%`, textAlign: 'center' }} color="success" name="quoteApproved" className={'btn btn-simple'} disabled>
                                Approved
                            </Button>
                        )}
                    </ButtonGroup>
                </Row>
                <Row>
                    <ButtonGroup>
                        <Button
                            style={{ width: `100%`, textAlign: 'center' }}
                            color="info"
                            name="ready"
                            className={'btn btn-simple'}
                            onClick={event => {
                                handleReadyForPickupClick(event, shipment);
                            }}
                        >
                            {actionLabel}
                        </Button>

                        <Button
                            style={{ width: `100%`, textAlign: `center` }}
                            color="danger"
                            name="decline"
                            className={'btn btn-simple'}
                            onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                setShipment(shipment);
                                setShowDeclineQuoteShipmentAlert(true);
                            }}
                        >
                            Cancel Order
                        </Button>
                    </ButtonGroup>
                </Row>
            </>
        );
    };

    const checkOrder = (permit, shipment, item) => {
        // console.log('shipment: ', shipment);
        // console.log('permit: ', permit);
        // console.log('item: ', item);
        let numFoodItems = 0;
        let subtotal = shipment.Orders[0].subtotal;
        let foodTotal = 0;
        let beerTotal = 0;
        let wineTotal = 0;
        let spiritsTotal = 0;
        let otherTotal = 0;

        shipment.Orders[0].Items.forEach(item => {
            if (item.product_blob.is_food && item.status !== 'item_declined') {
                numFoodItems++;
                foodTotal = foodTotal + item.product_price;
            } else if (!item.contains_alcohol && item.status !== 'item_declined') {
                otherTotal = otherTotal + item.product_price;
            } else if (item.product_blob.alcohol_type === 'beer' && item.status !== 'item_declined') {
                beerTotal = beerTotal + item.product_price;
            } else if ((item.product_blob.alcohol_type === 'wine' || item.product_blob.alcohol_type === 'sparking_wine') && item.status !== 'item_declined') {
                wineTotal = wineTotal + item.product_price;
            } else if (item.product_price.alcohol_type === 'spirit' && item.status !== 'item_declined') {
                spiritsTotal = spiritsTotal + item.product_price;
            }
        });

        // console.log('numFoodItems: ', numFoodItems);
        // console.log('foodTotal: ', foodTotal);

        if (permit === 'louisiana_calcasieu_class_b') {
            if (item.product_blob.is_food) {
                if (numFoodItems <= 1) {
                    return classBAlert();
                } else {
                    return defaultAlert();
                }
            } else {
                return defaultAlert();
            }
        } else if (permit === 'louisiana_calcasieu_class_a' || permit === 'louisiana_calcasieu_class_ar') {
            if (item.product_blob.is_food && foodTotal !== 0) {
                if ((foodTotal - item.product_price) / (subtotal - wineTotal - item.product_price) < 0.3) {
                    return classAAlert();
                } else {
                    return defaultAlert();
                }
            } else {
                return defaultAlert();
            }
        } else {
            return null;
        }
        // axios({
        //   method: 'post',
        //   url: inventory_urls.hasuraHost,
        //   headers: {
        //       'Authorization': 'Bearer ' + localStorage.getItem('access_token'),
        //       'content-type': 'application/json'
        //   },
        //   data: shipment.Orders
        // })
    };

    const defaultAlert = () => {
        // console.log('shipment: ', shipment);
        return (
            <ReactBSAlert
                warning
                style={{ display: 'block', marginTop: '-200px' }}
                title="Item Unavailable?"
                onConfirm={() => {
                    if (shipment.shipment_type === 'marketplace') {
                        declineItem(item);
                    } else {
                        declineItemBridge(item);
                    }

                    setItem(null);
                    setShowDecline(false);
                }}
                onCancel={() => {
                    setItem(null);
                    setShowDecline(false);
                }}
                confirmBtnBsStyle="danger"
                cancelBtnBsStyle="default"
                confirmBtnText="Confirm"
                cancelBtnText="Cancel"
                showCancel
                btnSize=""
            >
                {shipment.shipment_type === 'marketplace' ? (
                    <div>
                        This item will be marked as <strong>"Out of Stock"</strong> on the Gophr app until you manually re-enable it in your Inventory Manager.
                    </div>
                ) : (
                    <div>
                        Are you sure this item is <strong>"Out of Stock"</strong> ? Please check again before confirming.
                    </div>
                )}
            </ReactBSAlert>
        );
    };

    const classBAlert = () => {
        return (
            <ReactBSAlert
                warning
                style={{ display: 'block', marginTop: '-200px' }}
                title="Item Unavailable?"
                onConfirm={() => {
                    declineItem(item);
                    setItem(null);
                    setShowDecline(false);
                }}
                onCancel={() => {
                    setItem(null);
                    setShowDecline(false);
                }}
                confirmBtnBsStyle="danger"
                cancelBtnBsStyle="default"
                confirmBtnText="Delete Shipment"
                cancelBtnText="Nevermind"
                showCancel
                btnSize=""
            >
                <div>
                    Because of alcohol delivery regulations, declining this item will void the entire order. Are you sure you don't want to contact the customer and offer a
                    substitution instead?
                </div>
            </ReactBSAlert>
        );
    };

    const classAAlert = () => {
        return (
            <ReactBSAlert
                warning
                style={{ display: 'block', marginTop: '-200px' }}
                title="Item Unavailable?"
                onConfirm={() => {
                    declineItem(item);
                    setItem(null);
                    setShowDecline(false);
                }}
                onCancel={() => {
                    setItem(null);
                    setShowDecline(false);
                }}
                confirmBtnBsStyle="danger"
                cancelBtnBsStyle="default"
                confirmBtnText="Delete Shipment"
                cancelBtnText="Nevermind"
                showCancel
                btnSize=""
            >
                <div>
                    Because of alcohol delivery regulations, declining this item will void the entire order. Are you sure you don't want to contact the customer and offer a
                    substitution instead?
                </div>
            </ReactBSAlert>
        );
    };

    // Disables an item in inventory and changes item status.
    const declineItem = item => {
        axios({
            method: 'DELETE',
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                'content-type': 'application/json'
            },
            url: getItemActionHost(item) + '/shipments/' + item.Order.Shipment.shipment_id + '/orders/' + item.Order.order_id + '/items/' + item.item_id + '/decline'
        })
            .then(response => {
                // setSelected('pending');
                // setSelected("inProgress");
            })
            .catch(() => {
                props.notify('tc', 'Failed to decline item.', 'danger');
            });
    };

    const declineItemBridge = item => {
        axios({
            method: 'DELETE',
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                'content-type': 'application/json'
            },
            url: getItemActionHost(item) + '/shipments/' + item.Order.Shipment.shipment_id + '/orders/' + item.Order.order_id + '/items/' + item.item_id + '/bridge/decline'
        })
            .then(response => {
                // setSelected('pending');
                // setSelected("inProgress");
            })
            .catch(() => {
                props.notify('tc', 'Failed to decline bridge item.', 'danger');
            });
    };

    const readyItem = item => {
        axios({
            method: 'PUT',
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                'content-type': 'application/json'
            },
            url: getItemActionHost(item) + '/shipments/' + item.Order.Shipment.shipment_id + '/orders/' + item.Order.order_id + '/items/' + item.item_id + '/ready'
        })
            .then(response => {
                // setFilter(["shipment_created"]);
                // setSelected('pending');
                // console.log('reached here');
            })
            .catch(() => {
                props.notify('tc', 'Failed to mark item as ready.', 'danger');
            });
    };

    const readyOrder = order => {
        axios({
            method: 'PUT',
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                'content-type': 'application/json'
            },
            url: shipment_urls.ReadyOrder + order.shipment_id + '/orders/' + order.order_id + '/ready'
        })
            .then(response => {
                // setFilter(["shipment_created"]);
                // setSelected('pending');
                // console.log('reached here');
            })
            .catch(() => {
                props.notify('tc', 'Failed to mark order as ready.', 'danger');
            });
    };

    const makeAlmostReady = order_friendly_id => {
        if (almostReady.includes(order_friendly_id)) {
            // return <FaExclamationTriangle size="30" color="#fcb23a" />
            return 'Scheduled for Tomorrow';
        } else {
            // return <FaExclamationTriangle size="30" color="#fcb23a" />
            return 'Scheduled for Tomorrow';
        }
    };

    // Technically, this function should never get hit anymore
    const makeReady = order_friendly_id => {
        if (ready.includes(order_friendly_id)) {
            return 'Overdue';
        } else {
            return 'Overdue';
        }
    };

    const MultiDropDocuments = ({ shipment, allowUpload = false, compact = false }) => {
        const containerStyle = compact
            ? { display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '220px' }
            : {};

        return (
            <Col className={compact ? 'col-auto' : 'col-3'} style={containerStyle}>
                <strong style={compact ? { fontSize: 12, paddingTop: 5, paddingBottom: 5 } : undefined}>Documents</strong>
                <FileViewer
                    shipmentId={shipment.shipment_id}
                    shipment={shipment}
                    shipmentHost={getShipmentActionHost(shipment)}
                    showGenerateBolButton={false}
                    lockGoPilotReports={goPilotReportLocked}
                    onModalStateChange={setDocumentsModalOpen}
                />
                {allowUpload ? <FileUpload shipmentId={shipment.shipment_id} shipmentFriendlyId={shipment.shipment_friendly_id} /> : null}
            </Col>
        );
    };

    const fetchEnrouteAndDriver = async shipment => {
        const enrichedShipment = await buildShipmentWithDriver(shipment);
        if (!enrichedShipment) {
            return;
        }

        setShipmentClick(enrichedShipment);
    };

    const handleRowClick = async (event, rowInfo, cell, element) => {
        if (selected === 'pending') {
            if (expandedItems.pending[event.viewIndex]) {
                expandedItems.pending[event.viewIndex] = !expandedItems.pending[event.viewIndex];
            } else {
                expandedItems.pending[event.viewIndex] = true;
            }
        } else if (selected === 'inProgress') {
            if (expandedItems.inProgress[event.viewIndex]) {
                expandedItems.inProgress[event.viewIndex] = !expandedItems.inProgress[event.viewIndex];
                setShipmentClick('');
            } else {
                expandedItems.inProgress[event.viewIndex] = true;
                // setShipmentClick(event.original);
                await updateTQLTracking(event.original);
                fetchEnrouteAndDriver(event.original);
            }
        } else if (selected === 'completed') {
            if (expandedItems.completed[event.viewIndex]) {
                expandedItems.completed[event.viewIndex] = !expandedItems.completed[event.viewIndex];
                setShipmentClick('');
            } else {
                expandedItems.completed[event.viewIndex] = true;
                await updateTQLTracking(event.original);
                fetchEnrouteAndDriver(event.original);
            }
        } else if (selected === 'saved') {
            if (element.target.toString().split(' ')[1] !== 'SVGPathElement]' && element.target.toString().split(' ')[1] !== 'SVGSVGElement]') {
                if (element.target.className.split(' ')[0] === 'btn' || element.target.className.split(' ')[0] === 'btn-simple') {
                    return;
                }
            }
            if (expandedItems.saved[event.viewIndex]) {
                expandedItems.saved[event.viewIndex] = !expandedItems.saved[event.viewIndex];
            } else {
                expandedItems.saved[event.viewIndex] = true;
            }
        }
        if (typeof props.expand === 'function') {
            props.expand(expandedItems);
        }
    };

    const updateTQLTracking = async shipment => {
        let shipId = shipment.shipment_id;
        let po_number;
        if (
            shipment.Route &&
            shipment.Route.RouteDrivers &&
            shipment.Route.RouteDrivers[0] &&
            shipment.Route.RouteDrivers[0].tracking_blob &&
            shipment.Route.RouteDrivers[0].tracking_blob.hasOwnProperty('po_number')
        ) {
            po_number = shipment.Route.RouteDrivers[0].tracking_blob.po_number;
        } else {
            return;
        }
        axios({
            method: 'put',
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                'content-type': 'application/json'
            },
            url: getShipmentActionHost(shipment) + '/shipments/direct/' + shipId + '/tql_ftl_tracking',
            data: { po_number }
        })
            .then(response => response)
            .catch(() => null);
    };

    const cancelShipment = shipment => {
        setAlmostReady(current => current.filter(orderFriendlyId => orderFriendlyId !== (order && order.order_friendly_id)));
        setReady(current => current.filter(orderFriendlyId => orderFriendlyId !== (order && order.order_friendly_id)));

        const nextSelectedTab = isSavedDraftShipment(shipment) ? 'saved' : selected;

        axios({
            method: 'delete',
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                'content-type': 'application/json'
            },
            url: getShipmentActionHost(shipment) + '/shipments/' + shipment.shipment_id + '/decline',
            data: {
                cancel_reason: 'Deleted by merchant from dashboard',
            }
        })
            .then(response => {
                setDeletedShipmentIds(current => (current.includes(shipment.shipment_id) ? current : [...current, shipment.shipment_id]));
                setFallbackShipments(current => current.filter(candidateShipment => candidateShipment && candidateShipment.shipment_id !== shipment.shipment_id));
                setHydratedLastCreatedShipment(current =>
                    current && current.shipment_id === shipment.shipment_id ? null : current
                );
                setSelected(nextSelectedTab);
                resetExpand();
                props.notify('tc', 'Delivery Successfully Canceled');
            })
            .catch(error => {
                const errorMessage =
                    (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
                    'Failed to cancel delivery.';

                props.notify('tc', errorMessage, 'danger');
            });
    };

    const readyShipment = shipment => {
        // Mark order as ready for driver
        const requestUrl = getShipmentActionHost(shipment) + '/shipments/' + shipment.shipment_id + '/ready';

        runMerchantRequestWithSessionRecovery(() =>
            axios({
                method: 'put',
                headers: {
                    Authorization: buildAuthorizationHeader(),
                    'content-type': 'application/json'
                },
                url: requestUrl
            })
        )
            .then(response => {
                resetExpand();
                // setSelected('pending');
                setSelected('inProgress');
                props.notify('tc', 'Order Successfully Marked As Ready');
            })
            .catch(error => {
                const status = error && error.response ? error.response.status : null;
                const serverMessage =
                    (error && error.response && error.response.data && (error.response.data.message || error.response.data.error))
                    || (error && error.message)
                    || 'Request failed';

                if (status === 409) {
                    props.notify('tc', serverMessage, 'danger');
                } else {
                    props.notify('tc', `Failed to mark order as ready (${status || 'network'}): ${serverMessage}`, 'danger');
                }
            });
    };

    const shipmentRequiresCheckoutBeforeReady = shipment => {
        if (!shipment) {
            return false;
        }

        const savedDraftStatuses = ['shipment_saved_pending_payment_retry', 'shipment_saved', 'shipment_pending_saved'];
        if (!savedDraftStatuses.includes(shipment.status)) {
            return false;
        }

        const billingDetails = shipment.billing_details && typeof shipment.billing_details === 'object'
            ? shipment.billing_details
            : {};
        const deferredBilling = billingDetails.deferred_billing && typeof billingDetails.deferred_billing === 'object'
            ? billingDetails.deferred_billing
            : {};
        const paymentCollectionType = String(deferredBilling.payment_collection_type || '').trim().toLowerCase();

        if (deferredBilling.defer_until_completion === true || (paymentCollectionType && paymentCollectionType !== 'immediate')) {
            return false;
        }

        if (shipment.billing_status === 'completed') {
            return false;
        }

        const shippingFee = Number(shipment.shipping_fee || 0);
        return Number.isFinite(shippingFee) && shippingFee > 0;
    };

    const isReadyForPickupBlocked = shipment =>
        shipment &&
        shipmentRequiresCheckoutBeforeReady(shipment);

    const handleReadyForPickupClick = (event, shipment) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (!shipment) {
            return;
        }

        if (isReadyForPickupBlocked(shipment)) {
            props.notify('tc', 'This shipment must finish checkout before it can be marked ready.', 'danger');
            return;
        }

        if (shipment.scheduled_for) {
            schedulePendingShipment(shipment);
            return;
        }

        setShipment(shipment);
        setShowAlert1(true);
    };

    const schedulePendingShipment = shipment => {
        // Mark order as ready for driver
        const requestUrl = getShipmentActionHost(shipment) + '/shipments/' + shipment.shipment_id + '/pending_schedule';

        runMerchantRequestWithSessionRecovery(() =>
            axios({
                method: 'put',
                headers: {
                    Authorization: buildAuthorizationHeader(),
                    'content-type': 'application/json'
                },
                url: requestUrl
            })
        )
            .then(response => {
                resetExpand();
                // setSelected('pending');
                setSelected('inProgress');
                props.notify('tc', 'Order Successfully Marked As Scheduled');
            })
            .catch(error => {
                const status = error && error.response ? error.response.status : null;
                const serverMessage =
                    (error && error.response && error.response.data && (error.response.data.message || error.response.data.error))
                    || (error && error.message)
                    || 'Request failed';

                props.notify('tc', `Failed to mark order as scheduled (${status || 'network'}): ${serverMessage}`, 'danger');
            });
    };

    const isPending = element => element.status === 'item_created';

    const getTrProps = (rowInfo, column, cell) => {
        if (rowInfo) {
            if (column) {
                if (!loading) {
                    const buildRowProps = styleOverrides => ({
                        onClick: e => {
                            handleRowClick(column, rowInfo, cell, e);
                        },
                        style: {
                            cursor: 'pointer',
                            height: `70px`,
                            margin: `5px`,
                            borderRadius: `5px`,
                            background: hasUnreadClaimedOrderChatForShipment(column.original) ? chatAlertRowBackground : rowSurfaceBackground,
                            padding: '0px',
                            textAlign: 'left',
                            ...(hasUnreadClaimedOrderChatForShipment(column.original)
                                ? {
                                    border: `2px solid ${chatAlertBorderColor}`,
                                    boxShadow: `0 0 0 1px ${chatAlertBorderColor} inset`,
                                }
                                : {}),
                            ...styleOverrides,
                        }
                    });

                    // Handle pending payment retry statuses - allow row expansion
                    const isPendingPaymentRetry = column.original.status === 'shipment_saved_pending_payment_retry';

                    if (isPendingPaymentRetry) {
                        return buildRowProps({ border: '2px solid #dc3545' });
                    }

                    if ((column.original.status === 'shipment_pending' || column.original.status === 'shipment_pending_schedule') && column.original.shipping_fee > 0) {
                        return buildRowProps({ border: '2px solid green' });
                    }
                    if (column.original.shipping_fee_blob.shipment.hasOwnProperty('is_ltl') && column.original.shipping_fee_blob.shipment.is_ltl) {
                        return buildRowProps({ border: '2px solid #2BBBAE' });
                    }
                    if (column.original.shipping_fee_blob.hasOwnProperty('is_fedex') && column.original.shipping_fee_blob.is_fedex) {
                        return buildRowProps({ border: '2px solid purple' });
                    }
                    if (column.original.is_expedited) {
                        return buildRowProps({
                            border: hasUnreadClaimedOrderChatForShipment(column.original) ? `2px solid ${chatAlertBorderColor}` : '1px solid #f79400',
                        });
                    } else {
                        return buildRowProps({});
                    }
                } else {
                    return {};
                }
            }
            return {};
        }
    };

    const formatPhoneNumber = phone => {
        //Filter only numbers from the input
        let cleaned = ('' + phone).replace(/\D/g, '');

        //Check if the input is of correct length
        let match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

        if (match) {
            return match[1] + '-' + match[2] + '-' + match[3];
        }

        return null;
    };

    const getRecipientDisplayPhone = shipment => {
        const isCustomerBlobPhoneShipment = shipment && (shipment.shipment_type === 'marketplace' || shipment.shipment_type === 'bridge' || shipment.shipment_type === 'school_lunch');

        if (isCustomerBlobPhoneShipment) {
            return formatPhoneNumber(shipment && shipment.customer_blob ? shipment.customer_blob.phone : '');
        }

        const recipientLocation = getRecipientLocationBlob(shipment);
        return formatPhoneNumber(recipientLocation ? recipientLocation.phone : '');
    };

    const pendingColumns = [
        {
            expander: true,
            Expander: ({ isExpanded, ...rest }) => (
                <div style={{ textAlign: 'left' }}>
                    {!isExpanded ? (
                        <span>
                            <FaCaretRight color={'#BFC0C5'} size="20" />
                        </span>
                    ) : (
                        <span>
                            <FaCaretDown color={'#BFC0C5'} size="20" />
                        </span>
                    )}
                </div>
            )
        },
        {
            Header: 'ship_id',
            accessor: 'shipment_friendly_id',
            width: 'auto',
            textAlign: 'left',
            Cell: row => (
                <div
                    style={{
                        textAlign: 'left'
                    }}
                >
                    {row.value}
                </div>
            )
        },
        {
            Header: 'Status',
            accessor: 'status',
            width: 'auto',
            // width: 250,
            textAlign: 'left',
            Cell: row => {
                const isPendingPaymentRetry = row.original.status === 'shipment_saved_pending_payment_retry';

                return (
                    <div style={{ textAlign: 'left' }}>
                        {isPendingPaymentRetry ? (
                            <Badge
                                color="warning"
                                style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 12px', cursor: 'pointer' }}
                                onClick={e => {
                                    e.stopPropagation();
                                    setSelectedShipmentForPayment(row.original);
                                    setPaymentRetryModalOpen(true);
                                }}
                            >
                                <FaExclamationTriangle />
                                Payment Required
                            </Badge>
                        ) : row.original.shipment_type === 'school_lunch' ? (
                            'Pending Approval'
                        ) : (row.original.shipment_type === 'direct' || row.original.shipment_type === 'routed_direct' || row.original.shipment_type === 'multidrop_direct') &&
                          row.original.shipping_fee > 0 ? (
                            'Quote Approved'
                        ) : (row.original.shipment_type === 'direct' || row.original.shipment_type === 'routed_direct' || row.original.shipment_type === 'multidrop_direct') &&
                          row.original.shipping_fee === 0 ? (
                            'Quote Pending'
                        ) : (
                            <span style={{ textAlign: 'left' }}>{row.original.Orders[0].Items.some(isPending) ? 'Pending Approval' : 'Waiting on Other Merchant(s)'}</span>
                        )}
                    </div>
                );
            }
        },
        {
            Header: 'Priority',
            accessor: 'is_expedited',
            // width: 250,
            width: 'auto',
            Cell: props => (
                <div
                    style={{
                        width: 1,
                        // color: '#f7941d',
                        color: 'red',
                        fontWeight: `bold`,
                        fontSize: '1.2em'
                    }}
                >
                    {props.value === true ? '🚀 Expedited 🚀' : ''}
                </div>
            )
        },
        {
            Header: 'Type',
            accessor: 'shipment_type',
            width: 'auto',
            textAlign: 'left',
            Cell: props => (
                <div
                    style={{
                        textAlign: 'left'
                    }}
                >
                    {props.value === 'direct' || props.value === 'routed_direct' || props.value === 'multidrop_direct'
                        ? 'Direct'
                        : props.value === 'bridge'
                        ? 'Bridge'
                        : props.value === 'marketplace'
                        ? 'Marketplace'
                        : 'SchoolLunch'}
                </div>
            )
        },
        {
            Header: 'Recipient',
            // accessor: 'customer_blob',
            // width: 150,
            width: 'auto',
            textAlign: 'left',
            Cell: props => (
                <div
                    style={{
                        // width: 1,
                        fontWeight: 'bold',
                        textAlign: 'left'
                    }}
                >
                    {/* {props.value.first_name + ' ' + props.value.last_name} */}
                    {getRecipientDisplayName(props.original)}
                </div>
            )
        },
        {
            Header: 'Recipient Ph.',
            // accessor: 'customer_blob',
            width: 'auto',
            textAlign: 'left',
            Cell: props => (
                <div
                    style={{
                        // width: 'auto',
                        textAlign: 'left'
                    }}
                >
                    {/* {formatPhoneNumber(props.value.phone)} */}
                    {getRecipientDisplayPhone(props.original)}
                </div>
            )
        },
        {
            Header: 'Created At',
            accessor: 'created_at',
            width: 'auto',
            textAlign: 'left',
            // Cell: props => <div style={{ textAlign: 'left' }}>{formatTimestamp(props.value)}</div>
            Cell: props => <div style={{ textAlign: 'left' }}>{formatShipmentTimestampForDisplay(props.value)}</div>
        }
    ];

    const inProgressColumns = [
        {
            Header: 'ship_id',
            accessor: 'shipment_friendly_id',
            width: 'auto',
            textAlign: 'left',
            Cell: row => (
                <div
                    style={{
                        textAlign: 'left'
                    }}
                >
                    {row.value}
                </div>
            )
        },
        {
            Header: 'Status',
            // accessor: 'status',
            // width: 250,
            width: 'auto',
            textAlign: 'left',
            Cell: row => (
                <div
                    style={{
                        textAlign: 'left'
                    }}
                >
                    <span style={{ textAlign: 'left' }}>
                        {// row.value === "shipment_created" ? <FaHourglassHalf size="30" color="#be132d" /> :
                        row.original.status === 'shipment_ready_for_driver'
                            ? 'Awaiting Driver'
                            : row.original.status === 'shipment_pending_review'
                            ? 'Pending Review'
                            : row.original.status === 'shipment_enroute'
                            ? 'Delivery in Progress'
                            : (row.original.status === 'shipment_assigned' || row.original.status === 'shipment_claimed') && isOrderCompletedForDisplay(row.original.Orders[0])
                            ? 'Delivering'
                            : (row.original.status === 'shipment_assigned' || row.original.status === 'shipment_claimed') && !isOrderCompletedForDisplay(row.original.Orders[0])
                            ? 'Approaching'
                            : 'None Found'}
                    </span>
                    {/* <span> {row.original.Route.RouteDrivers.length > 0 ? ` (P: ${formatPhoneNumber(row.original.Route.RouteDrivers[0].driver_blob.phone)})` : null}</span> */}
                </div>
            )
        },
        {
            Header: 'Priority',
            accessor: 'is_expedited',
            width: 'auto',
            Cell: row => {
                const { is_expedited, shipping_fee_blob } = row.original;
                const isLTL = shipping_fee_blob.shipment.hasOwnProperty('is_ltl') && shipping_fee_blob.shipment.is_ltl;

                return (
                    <div
                        style={{
                            fontWeight: 'bold',
                            fontSize: '1.2em',
                            color: is_expedited ? 'red' : isLTL ? '#2BBBAE' : 'inherit' // Set red for expedited, blue for LTL, and default for none.
                        }}
                    >
                        {is_expedited ? '🚀 Expedited 🚀' : isLTL ? 'LTL' : ''}
                    </div>
                );
            }
        },

        {
            Header: 'Type',
            accessor: 'shipment_type',
            width: 'auto',
            textAlign: 'left',
            Cell: props => (
                <div
                    style={{
                        textAlign: 'left'
                    }}
                >
                    {props.value === 'direct' || props.value === 'routed_direct' || props.value === 'multidrop_direct'
                        ? 'Direct'
                        : props.value === 'bridge'
                        ? 'Bridge'
                        : props.value === 'marketplace'
                        ? 'Marketplace'
                        : 'SchoolLunch'}
                </div>
            )
        },
        {
            Header: 'Recipient',
            // width: 150,
            width: 'auto',
            textAlign: 'left',
            Cell: props => (
                <div
                    style={{
                        fontWeight: 'bold',
                        textAlign: 'left'
                    }}
                >
                    {/* {props.original.Orders[props.original.Orders.length - 1].location_blob.name} */}
                    {getRecipientDisplayName(props.original)}
                </div>
            )
        },
        {
            Header: 'Recipient Ph.',
            // accessor: 'Orders[1].location_blob.phone',
            textAlign: 'left',
            width: 'auto',
            // Cell: props => <div style={{ textAlign: 'left' }}>{props.original.Orders[props.original.Orders.length - 1].location_blob.phone}</div>
            Cell: props => (
                <div style={{ textAlign: 'left' }}>
                    {getRecipientDisplayPhone(props.original)}
                </div>
            )
        },
        {
            Header: 'Updated At',
            accessor: 'updated_at',
            width: 'auto',
            // Cell: props => <div>{formatTimestamp(props.value)}</div>
            Cell: props => <div style={{ textAlign: 'left' }}>{formatShipmentTimestampForDisplay(props.value)}</div>
        },
        {
            Header: 'Delivery Fee',
            accessor: 'shipping_fee',
            width: 'auto',
            textAlign: 'left',
            // Cell: props => <div style={{ textAlign: 'left' }}>{'$' + props.value.toFixed(2)}</div>
            Cell: props => (
                <div style={{ textAlign: 'left' }}>
                    {' '}
                    {'  ' + getShipmentDeliveryFeeDisplay(props.original)}
                </div>
            )
        }
    ];

    const completedColumns = [
        // {
        //   expander: true,
        //   Expander: ({isExpanded, ...rest}) =>
        //   <div>
        //     { !isExpanded ? <span><FaCaretRight color={'#BFC0C5'} size="20" /></span> :
        //     <span><FaCaretDown color={'#BFC0C5'} size="20" /></span> }
        //   </div>
        // },
        {
            Header: 'ship_id',
            accessor: 'shipment_friendly_id',
            width: 'auto',
            textAlign: 'left',
            Cell: row => (
                <div
                    style={{
                        textAlign: 'left'
                    }}
                >
                    {row.value}
                </div>
            )
        },
        {
            Header: 'Status',
            accessor: 'status',
            width: 'auto',
            // width: 250,
            textAlign: 'left',
            Cell: row => (
                <div style={{ textAlign: 'left' }}>
                    <span style={{ textAlign: 'left' }}>{row.original.status === 'shipment_completed' ? 'Order Completed' : ''}</span>
                </div>
            )
        },
        {
            Header: 'Type',
            accessor: 'shipment_type',
            width: 'auto',
            textAlign: 'left',
            Cell: props => (
                <div
                    style={{
                        textAlign: 'left'
                    }}
                >
                    {props.value === 'direct' || props.value === 'routed_direct' || props.value === 'multidrop_direct'
                        ? 'Direct'
                        : props.value === 'bridge'
                        ? 'Bridge'
                        : props.value === 'marketplace'
                        ? 'Marketplace'
                        : 'SchoolLunch'}
                </div>
            )
        },
        {
            Header: 'Recipient',
            // width: 150,
            width: 'auto',
            textAlign: 'left',
            Cell: props => (
                <div
                    style={{
                        fontWeight: 'bold',
                        textAlign: 'left'
                    }}
                >
                    {/* {props.original.Orders[props.original.Orders.length - 1].location_blob.name} */}
                    {getRecipientDisplayName(props.original)}
                </div>
            )
        },
        {
            Header: 'Recipient Ph.',
            // accessor: 'Orders[1].location_blob.phone',
            textAlign: 'left',
            width: 'auto',
            // Cell: props => <div style={{ textAlign: 'left' }}>{props.original.Orders[props.original.Orders.length - 1].location_blob.phone}</div>
            Cell: props => (
                <div style={{ textAlign: 'left' }}>
                    {getRecipientDisplayPhone(props.original)}
                </div>
            )
        },
        {
            Header: 'Completed At',
            accessor: row => getShipmentCompletionTimestampForDisplay(row),
            id: 'completed_at',
            width: 'auto',
            textAlign: 'left',
            Cell: props => <div style={{ textAlign: 'left' }}>{formatShipmentTimestampForDisplay(props.value)}</div>
        },
        {
            Header: 'Delivery Fee',
            accessor: 'shipping_fee',
            width: 'auto',
            textAlign: 'left',
            Cell: props => <div style={{ textAlign: 'left' }}>{'$' + props.value.toFixed(2)}</div>
        }
    ];

    const savedForLaterColumns = [
        // {
        //   expander: true,
        //   textAlign: 'center',
        //   Expander: ({isExpanded, ...rest}) =>
        //   <div style={{
        //     width: 20,
        //     textAlign: 'center'
        //   }}>
        //     { !isExpanded ?
        //     <span><FaCaretRight color={'#BFC0C5'} size="20" /></span>
        //      :
        //     <span><FaCaretDown color={'#BFC0C5'} size="20" /></span> }
        //   </div>
        // },
        // {
        //   Header: 'ID',
        //   accessor: 'order_friendly_id',
        //   width: 40,
        //   textAlign: 'left'
        // },
        {
            Header: 'ship_id',
            accessor: 'shipment_friendly_id',
            width: 'auto',
            textAlign: 'left',
            Cell: row => (
                <div
                    style={{
                        textAlign: 'left'
                    }}
                >
                    {row.value}
                </div>
            )
        },
        {
            Header: 'Status',
            // accessor: 'status',
            // width: 'auto',
            width: 'auto',
            textAlign: 'left',
            Cell: row => (
                <div
                    style={{
                        textAlign: 'left'
                    }}
                >
                    <span style={{ textAlign: 'left' }}>
                        {row.original.status === 'shipment_scheduled'
                            ? now.isAfter(row.original.scheduled_for)
                                ? makeReady(row.original.order_friendly_id)
                                : tomorrow.isAfter(row.original.scheduled_for)
                                ? makeAlmostReady(row.original.order_friendly_id)
                                : 'Scheduled'
                            : row.original.status === 'shipment_pending_payment'
                            ? 'Payment Required'
                            : row.original.status === 'shipment_saved_pending_payment_retry'
                            ? 'Payment Required'
                            : isSavedDraftStatus(row.original.status)
                            ? 'Saved'
                            : 'Unknown Status'
                        // : <FaClock size="30" color="#2bbbae" />
                        // : row.value === 'shipment_saved' ? <FaBookmark size="30" color="be1e2d" />
                        // : "None Found"
                        }
                    </span>
                </div>
            )
        },
        {
            Header: 'Type',
            accessor: 'shipment_type',
            width: 'auto',
            textAlign: 'left',
            Cell: props => (
                <div
                    style={{
                        textAlign: 'left'
                    }}
                >
                    {props.value === 'direct' || props.value === 'routed_direct' || props.value === 'multidrop_direct'
                        ? 'Direct'
                        : props.value === 'bridge'
                        ? 'Bridge'
                        : props.value === 'marketplace'
                        ? 'Marketplace'
                        : 'SchoolLunch'}
                </div>
            )
        },
        // {
        //   Header: 'Order Created At',
        //   accessor: 'created_at',
        //   width: 200,
        //   textAlign: 'left',
        //   Cell: props => (
        //     <div style={{
        //       textAlign: 'left'
        //     }}>
        //       {formatTimestamp(props.value)}
        //     </div>
        //   )
        // },
        {
            Header: 'Scheduled For',
            accessor: 'scheduled_for',
            width: 'auto',
            textAlign: 'left',
            Cell: props =>
                props.value ? (
                    // <div style={{ textAlign: 'left' }}>
                    //   <strong>{formatTimestamp(props.value)}</strong>
                    // </div>
                    <div style={{ textAlign: 'left' }}>
                        <strong>{formatShipmentTimestampForDisplay(props.value)}</strong>
                    </div>
                ) : (
                    <div style={{ textAlign: 'left' }}>Not Scheduled</div>
                )
        },
        {
            Header: 'Recipient',
            // accessor: 'Orders[1].location_blob.name',
            width: 'auto',
            textAlign: 'left',
            Cell: props => (
                <div
                    style={{
                        fontWeight: 'bold',
                        textAlign: 'left'
                    }}
                >
                    {/* {props.original.Orders[props.original.Orders.length - 1].location_blob.name} */}
                    {getRecipientDisplayName(props.original)}
                </div>
            )
        },
        {
            Header: 'Recipient Ph.',
            accessor: 'Orders[1].location_blob.phone',
            textAlign: 'left',
            width: 'auto',
            // Cell: props => <div style={{ textAlign: 'left' }}>{props.original.Orders[props.original.Orders.length - 1].location_blob.phone}</div>
            Cell: props => (
                <div style={{ textAlign: 'left' }}>
                    {getRecipientDisplayPhone(props.original)}
                </div>
            )
        },
        {
            Header: 'Delivery Fee',
            accessor: 'shipping_fee',
            width: 'auto',
            textAlign: 'left',
            // Cell: props => <div style={{ textAlign: 'left' }}>{'$' + props.value.toFixed(2)}</div>
            Cell: props => (
                <div style={{ textAlign: 'left' }}>
                    {' '}
                    {'  ' + getShipmentDeliveryFeeDisplay(props.original)}
                </div>
            )
        }
        // {
        //   Header: 'Size',
        //   accessor: 'vehicle_type',
        //   width: '100',
        //   textAlign: 'left',
        //   Cell: row => (
        //     <div style={{textAlign: 'left'}}>
        //       <span>
        //         {
        //           row.value === 'car' ?  <Car/> :
        //           row.value === 'suv' ?  <SUV/> :
        //           row.value === 'truck' ? <Truck/> :
        //           row.value === 'trailer' ? <Trailer size={1.5} /> :
        //           row.value === 'box_truck' ? <BoxTruck/> : "None Found"
        //         }
        //       </span>
        //     </div>
        //   )
        // },
        // {
        //   Header: 'Description',
        //   accessor: 'Shipment.description',
        //   width: 'auto',
        //   textAlign: 'left',
        //   Cell: props => (
        //     <div style={{
        //       width: 'auto',
        //       textAlign: 'left',
        //       //paddingLeft: 10,
        //       overflow: `ellipsis`
        //     }}>
        //       {props.value}
        //     </div>
        //   )
        // },
    ];

    const columns = [
        {
            expander: true,
            Expander: ({ isExpanded, ...rest }) => (
                <div>
                    {!isExpanded ? (
                        <span>
                            <FaCaretRight color={'#BFC0C5'} size="20" />
                        </span>
                    ) : (
                        <span>
                            <FaCaretDown color={'#BFC0C5'} size="20" />
                        </span>
                    )}
                </div>
            )
        },
        {
            Header: 'ship_id',
            accessor: 'shipment_friendly_id',
            width: 'auto',
            textAlign: 'left',
            Cell: row => (
                <div
                    style={{
                        textAlign: 'left'
                    }}
                >
                    {row.value}
                </div>
            )
        },
        {
            Header: 'Status',
            accessor: 'status',
            width: 65,
            Cell: row => (
                <div>
                    <span>
                        {row.value === 'shipment_created' ? (
                            <FaHourglassHalf size="30" color="#be1e2d" />
                        ) : row.value === 'shipment_ready_for_driver' ? (
                            <FaHourglassHalf size="30" color="#be1e2d" />
                        ) : row.value === 'shipment_assigned' ? (
                            <FaPeopleCarry size="30" color="#ff6500" />
                        ) : row.value === 'shipment_enroute' ? (
                            <FaHome size="30" color="#fcb23a" />
                        ) : row.value === 'shipment_completed' ? (
                            <FaRegCheckCircle size="30" color="#2bbbae" />
                        ) : row.value === 'shipment_scheduled' ? (
                            <FaClock size="30" color="#2bbbae" />
                        ) : isSavedDraftStatus(row.value) ? (
                            <FaBookmark size="30" color="#be1e2d" />
                        ) : (
                            'None Found'
                        )}
                    </span>
                </div>
            )
        },
        {
            Header: 'Type',
            accessor: 'Shipment.shipment_type',
            width: 100
        },
        {
            Header: 'Customer',
            accessor: 'Shipment',
            width: 150,
            Cell: props => (
                <div
                    style={{
                        width: 1,
                        fontWeight: 'bold'
                    }}
                >
                    {props.value.customer_blob.first_name + ' ' + props.value.customer_blob.last_name}
                </div>
            )
        },
        {
            Header: 'Size',
            accessor: 'Shipment.vehicle_type',
            width: 100,
            Cell: row => (
                <div>
                    <span>
                        {row.value === 'car' ? (
                            <Car />
                        ) : row.value === 'suv' ? (
                            <SUV />
                        ) : row.value === 'truck' ? (
                            <Truck />
                        ) : row.value === 'trailer' ? (
                            <Trailer size={1.5} />
                        ) : row.value === 'box_truck' ? (
                            <BoxTruck />
                        ) : (
                            row.value
                        )}
                    </span>
                </div>
            )
        },
        {
            Header: 'Gophrs',
            accessor: 'Shipment.num_of_gophrs',
            width: 60,
            Cell: props => (
                <div
                    style={{
                        width: `${props.value}%`
                    }}
                >
                    {}
                    {props.value.toString()}
                </div>
            )
        },
        {
            Header: 'Priority',
            accessor: 'Shipment.is_expedited',
            width: 120,
            Cell: props => (
                <div
                    style={{
                        width: 1,
                        color: '#f7941d',
                        fontWeight: `bold`,
                        fontSize: '1.2em'
                    }}
                >
                    {props.value === true ? 'Expedited' : ''}
                </div>
            )
        },
        {
            Header: 'Description',
            accessor: 'Shipment.description',
            width: 300,
            Cell: props => (
                <div
                    style={{
                        width: `${props.value}%`,
                        overflow: `ellipsis`
                    }}
                >
                    {props.value}
                </div>
            )
        },
        {
            Header: 'Time Stamp',
            accessor: 'created_at',
            width: 190,
            Cell: props => (
                <div
                    style={{
                        textAlign: 'center'
                    }}
                >
                    {formatTimestamp(props.value)}
                </div>
            )
        }
    ];

    const getTheadThProps = () => {
        return {
            style: {
                fontSize: `12px`,
                fontWeight: `600`,
                textAlign: `left`,
                color: `#cdcaca`
            }
        };
    };

    const shouldShowInitialLoading =
        (loading && effectiveRawShipments.length === 0) ||
        (!isLocalDashboardEnvironment && storeLoading && effectiveRawShipments.length === 0);

    const shouldShowMapOrderLoadingDots =
        loading ||
        storeLoading ||
        (!hasMarkedMerchantDataReady.current && effectiveRawShipments.length === 0);

    if (shouldShowInitialLoading) {
        return <Loading />;
    }
    return (
        <div className="content">
            {marketNote && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <p
                        style={{
                            fontFamily: 'Lato, sans-serif',
                            fontSize: '20px',
                            fontStyle: 'italic',
                            fontWeight: 'bold',
                            color: 'red',
                            marginBottom: '10px',
                            marginLeft: '200px',
                            marginRight: '200px',
                            lineHeight: '2.5',
                            padding: '10px'
                        }}
                    >
                        {marketNote}
                    </p>
                </div>
            )}
            {/* comment out the data analysis */}
            {/* <DataAnalysis /> */}
            <div className="float-container">
                <ResizableBox className="box" width={5000} height={4200} minConstraints={[1000, 4000]} maxConstraints={[10000, 5000]} draggableOpts={{ grid: [10, 10] }} axis="x">
                    <div className="float-child-table">
                        {(displayedPendingCustomerDriverReviews.length > 0 && (customerDriverReviewsSupported || Boolean(localTestCustomerDriverReview))) && (
                            <div className="merchant-customer-review-grid">
                                {displayedPendingCustomerDriverReviews.map(review => {
                                    const completedLabel = review.completed_at ? moment(review.completed_at).local().format('MMM D, YYYY h:mm A') : '';
                                    const shipmentLabel = review.shipment_friendly_id ? `Order #${review.shipment_friendly_id}` : 'Completed delivery';

                                    return (
                                        <div key={review.shipment_id} className="merchant-customer-review-card">
                                            <p className="merchant-customer-review-card-topline">{shipmentLabel}</p>
                                            <h4 className="merchant-customer-review-card-title">Rate {review.driver_name}</h4>
                                            <p className="merchant-customer-review-card-copy">
                                                {completedLabel && (
                                                    <>
                                                        <strong>Completed:</strong> <span>{completedLabel}</span>
                                                        <br />
                                                    </>
                                                )}
                                                {review.description || 'Tell us how your driver handled this delivery.'}
                                            </p>
                                            <Button
                                                color="info"
                                                className="merchant-customer-review-card-button"
                                                onClick={() => openCustomerDriverReviewModal(review.shipment_id)}
                                            >
                                                Leave a review
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {' '}
                        {volumePricingUnlocked ? (
                            <VolumeDiscountMeter monthlySpend={volumeDiscountMonthlySpend} />
                        ) : (
                            <div>
                                <div style={{ opacity: 0.45, filter: 'grayscale(1)', pointerEvents: 'none' }}>
                                    <VolumeDiscountMeter monthlySpend={volumeDiscountMonthlySpend} />
                                </div>
                                <UpgradeHint label="Upgrade to Enterprise to unlock volume discounts" />
                            </div>
                        )}
                        <ButtonGroup className="center" style={{ width: '100%' }}>
                            <Button
                                style={{ width: '25%' }}
                                color="info"
                                className={selected === 'pending' ? 'btn' : 'btn-simple'}
                                name="pending"
                                onClick={() => {
                                    setFilter(['shipment_created', 'shipment_pending', 'shipment_pending_schedule']);
                                    setSelected('pending');
                                }}
                            >
                                Pending
                                {renderTabCountChip(amountApproval, { warning: true })}
                                {renderTabCountChip(amountPending)}
                            </Button>
                            <Button
                                style={{
                                    width: '25%',
                                    position: 'relative',
                                    backgroundColor: inProgressUnreadShipmentCount > 0 ? chatAlertRowBackground : undefined,
                                    borderColor: inProgressUnreadShipmentCount > 0 ? chatAlertBorderColor : undefined,
                                    color: inProgressUnreadShipmentCount > 0 ? chatAlertTextColor : undefined,
                                }}
                                // style={{width: '33%'}}
                                className={selected === 'inProgress' ? 'btn' : 'btn-simple'}
                                name="inProgress"
                                color="info"
                                onClick={() => {
                                    setFilter(['shipment_ready_for_driver', 'shipment_assigned', 'shipment_claimed', 'shipment_enroute', 'shipment_pending_review']);
                                    setSelected('inProgress');
                                }}
                            >
                                In Progress
                                {renderTabCountChip(inProgressShipmentCount, { style: { right: '2.5rem' } })}
                                {renderMerchantChatUnreadBadge(inProgressUnreadShipmentCount, {
                                    top: '-2px',
                                    right: '8px',
                                    borderColor: inProgressUnreadShipmentCount > 0 ? chatAlertRowBackground : '#FFFFFF',
                                })}
                            </Button>
                            <Button
                                style={{ width: '25%' }}
                                // style={{width: '34%'}}
                                className={selected === 'completed' ? 'btn' : 'btn-simple'}
                                name="completed"
                                color="info"
                                onClick={() => {
                                    setFilter(['shipment_completed']);
                                    setSelected('completed');
                                }}
                            >
                                Completed
                            </Button>
                            <Button
                                style={{ width: '25%' }}
                                // style={{width: '33%'}}
                                className={selected === 'saved' ? 'btn' : 'btn-simple'}
                                name="saved"
                                color="info"
                                onClick={() => {
                                    setFilter(['shipment_scheduled', 'shipment_saved', 'shipment_pending_saved', 'shipment_saved_pending_payment_retry', 'shipment_pending_payment']);
                                    setSelected('saved');
                                }}
                            >
                                Saved for Later
                                {renderTabCountChip(savedForLaterShipmentCount, { style: { right: '2.5rem' } })}
                                {renderTabCountChip(almostReady.length, { warning: true, style: { right: '4.2rem' } })}
                                {renderTabCountChip(ready.length, { style: { right: '5.9rem' } })}
                            </Button>
                        </ButtonGroup>
                        <ReactTable
                            data={shipmentsToDisplay}
                            columns={
                                                                filter.includes('shipment_scheduled') ||
                                                                filter.includes('shipment_saved') ||
                                                                filter.includes('shipment_pending_saved') ||
                                                                filter.includes('shipment_saved_pending_payment_retry') ||
                                                                filter.includes('shipment_pending_payment')
                                    ? savedForLaterColumns
                                    : filter.includes('shipment_completed')
                                    ? completedColumns
                                    : filter.includes('shipment_ready_for_driver') || filter.includes('shipment_assigned') || filter.includes('shipment_claimed') || filter.includes('shipment_enroute')
                                    ? inProgressColumns
                                    : filter.includes('shipment_created') ||
                                      filter.includes('shipment_pending') ||
                                                                            filter.includes('shipment_pending_schedule')
                                    ? pendingColumns
                                    : columns
                            }
                            sortable={true}
                            resizable={true}
                            defaultPageSize={10}
                            expanded={
                                selected === 'pending'
                                    ? expandedItems.pending
                                    : selected === 'inProgress'
                                    ? expandedItems.inProgress
                                    : selected === 'completed'
                                    ? expandedItems.completed
                                    : expandedItems.saved
                            }
                            getTrProps={getTrProps}
                            getTheadThProps={getTheadThProps}
                            collapseOnSortingChange={true}
                            collapseOnPageChange={true}
                            collapseOnDataChange={false}
                            onSortedChange={e => resetExpand()}
                            onPageChange={e => resetExpand()}
                            onFetchData={e => {
                                resetExpand();
                            }}
                            SubComponent={row => {
                                return (
                                    <div
                                        style={{
                                            backgroundColor: detailSurfaceBackground,
                                            borderRadius: '6px',
                                            paddingTop: '15px',
                                            paddingLeft: '10px',
                                            paddingRight: '10px',
                                            marginRight: '10px',
                                            marginLeft: '10px',
                                            marginBottom: '20px',
                                            height: 'auto',
                                            paddingBottom: '15px'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', flexWrap: 'wrap', minWidth: '260px' }}>
                                                <strong>
                                                    <p style={{ paddingTop: 5, paddingBottom: 5, fontSize: 12, textAlign: 'left' }}>Order Information</p>
                                                    <p style={{ textAlign: 'left' }}>ID -- {row.original.shipment_friendly_id}</p>
                                                </strong>
                                                <strong>
                                                    <p style={{ paddingTop: 5, paddingBottom: 5, fontSize: 12, textAlign: 'left' }}>Placed by</p>
                                                    <p style={{ textAlign: 'left' }}>{getShipmentPlacedByDisplayName(row.original) || 'Unknown'}</p>
                                                </strong>
                                                {row.original.shipment_type === 'multidrop_direct' ? <MultiDropDocuments shipment={row.original} allowUpload={true} compact={true} /> : null}
                                                {row.original.shipment_type === 'multidrop_direct' ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '220px' }}>
                                                        <strong style={{ fontSize: 12, paddingTop: 5, paddingBottom: 5, textAlign: 'left' }}>Actions</strong>
                                                                        {canShowSavedColumnActions(row.original) ? <ButtonGroup style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                            <Button
                                                                color="info"
                                                                name="ready-pickup"
                                                                style={{ padding: 10 }}
                                                                onClick={e => {
                                                                    setSelectSavedAction(e.target.value);
                                                                    setShipment(row.original);
                                                                    setShowAlert1(true);
                                                                }}
                                                                className={selectSavedAction === 'ready-pickup' ? 'btn' : 'btn-simple'}
                                                            >
                                                                Ready for Pick Up
                                                            </Button>
                                                            <Button
                                                                color="info"
                                                                name="delete-order"
                                                                style={{ padding: 10 }}
                                                                onClick={e => {
                                                                    setSelectSavedAction(e.target.value);
                                                                    setShipment(row.original);
                                                                    setShowAlert(true);
                                                                }}
                                                                className={selectSavedAction === 'delete-order' ? 'btn' : 'btn-simple'}
                                                            >
                                                                Delete Order
                                                            </Button>
                                                        </ButtonGroup> : null}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                            <button
                                                type="button"
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    openShipmentThreadChat(row.original, 'customer_driver');
                                                }}
                                                title="Open customer / driver chat"
                                                aria-label="Open customer / driver chat"
                                                style={{
                                                    alignItems: 'center',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#f7941d',
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    gap: '6px',
                                                    justifyContent: 'center',
                                                    lineHeight: 1,
                                                    padding: '4px 0',
                                                }}
                                            >
                                                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <FontAwesomeIcon icon={faComment} style={{ fontSize: '1.1em' }} />
                                                    {renderMerchantChatUnreadBadge(getClaimedOrderChatUnreadCountForThread({
                                                        shipmentId: row.original && row.original.shipment_id,
                                                        threadType: 'customer_driver',
                                                        participantAuth0Id: resolveShipmentCustomerParticipantAuth0Id(row.original),
                                                    }))}
                                                </span>
                                                <span>Customer / Driver Chat</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    openShipmentThreadChat(row.original, 'customer_dispatch');
                                                }}
                                                title="Open dispatch chat"
                                                aria-label="Open dispatch chat"
                                                style={{
                                                    alignItems: 'center',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#f7941d',
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    gap: '6px',
                                                    justifyContent: 'center',
                                                    lineHeight: 1,
                                                    padding: '4px 0',
                                                }}
                                            >
                                                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <FontAwesomeIcon icon={faComment} style={{ fontSize: '1.1em' }} />
                                                    {renderMerchantChatUnreadBadge(getClaimedOrderChatUnreadCountForThread({
                                                        shipmentId: row.original && row.original.shipment_id,
                                                        threadType: 'customer_dispatch',
                                                        participantAuth0Id: resolveShipmentCustomerParticipantAuth0Id(row.original),
                                                    }))}
                                                </span>
                                                <span>Dispatch Chat</span>
                                            </button>
                                            </div>
                                        </div>
                                        {selected === 'pending' && !isSavedDraftShipment(row.original) && !hasInlinePendingShipmentActions(row.original) && (
                                            <div style={{ marginTop: '10px', marginBottom: '10px', maxWidth: '320px' }}>
                                                <QuoteOrderButtons shipment={row.original} />
                                            </div>
                                        )}
                                        {canSubmitSavedDraftForCheckout(row.original) && (
                                            <div style={{ marginTop: '10px', marginBottom: '4px' }}>
                                                <Button
                                                    color="success"
                                                    disabled={submittingSavedDraftId === row.original.shipment_id}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        submitSavedDraftForCheckout(row.original);
                                                    }}
                                                >
                                                    {submittingSavedDraftId === row.original.shipment_id ? 'Submitting…' : 'Submit Order'}
                                                </Button>
                                            </div>
                                        )}
                                        {canEditSavedDraftShipment(row.original) && (
                                            <div>
                                                <Button
                                                    type="button"
                                                    color="info"
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        openSavedDraftEditor(row.original);
                                                    }}
                                                >
                                                    Edit Draft
                                                </Button>
                                            </div>
                                        )}
                                        {selected === 'inProgress' && canMerchantCancelShipment(row.original) && (
                                            <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                                                <Button
                                                    color="danger"
                                                    className="btn btn-simple"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        setShipment(row.original);
                                                        setShowDeclineQuoteShipmentAlert(true);
                                                    }}
                                                >
                                                    Cancel Order
                                                </Button>
                                            </div>
                                        )}
                                        {(row.original.status === 'shipment_saved_pending_payment_retry' || row.original.status === 'shipment_pending_payment') && (
                                            <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                                                <Button
                                                    color="warning"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        setSelectedShipmentForPayment(row.original);
                                                        setPaymentRetryModalOpen(true);
                                                    }}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                                >
                                                    <FaExclamationTriangle />
                                                    Complete Payment
                                                </Button>
                                            </div>
                                        )}
                                        {/* {selected === 'pending' && row.original.is_expedited ? (
                <strong>
                  <p style={{ color: '#f7941d' }}>Expedited Delivery</p>
                </strong>
              ) : null} */}
                                        {selected === 'pending' ? (
                                            row.original.shipment_type === 'marketplace' ? (
                                                row.original.Orders[0].Items.map((item, index, items) => {
                                                    return (
                                                        <div key={item.item_id} style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                                                            {/* {console.log("row: ", row)}
                                {console.log("SHipment type from merchant pending", row.original.shipment_type)} */}
                                                            {/* <div>
                                  <strong>Image</strong>
                                  <p>{item.product_blob.image ? <img src={item.product_blob.image} alt="Product Image" style={{width: "10%"}} />
                                  : "Not Pictured"}</p>
                                </div> */}
                                                            <div>
                                                                <strong>Quantity</strong>
                                                                <p style={{ textAlign: 'center' }}>{item.quantity}</p>
                                                            </div>
                                                            <div>
                                                                <strong>Product Name</strong>
                                                                {/* <p>{item.product_blob.name}</p> */}
                                                                <p>
                                                                    {item.product_blob.name + ' '}
                                                                    {item.ItemOptions
                                                                        ? item.ItemOptions.length > 0
                                                                            ? item.ItemOptions.map((option, index) => {
                                                                                  if (index === 0) {
                                                                                      return '(' + option.option_name;
                                                                                  } else if (index > 0 && index !== item.ItemOptions.length - 1) {
                                                                                      return ', ' + option.option_name;
                                                                                  } else {
                                                                                      return ', ' + option.option_name + ')';
                                                                                  }
                                                                              })
                                                                            : null
                                                                        : null}
                                                                </p>
                                                            </div>
                                                            <div>
                                                                <strong>Price</strong>
                                                                {item.product_blob.subtotal ? <p>${item.product_blob.subtotal.toFixed(2)}</p> : <p>Not Found</p>}
                                                            </div>
                                                            <div>
                                                                <strong>SKU</strong>
                                                                <p>{item.product_blob.sku}</p>
                                                            </div>
                                                            {item.status === 'item_created' ? (
                                                                <div>
                                                                    <ButtonGroup>
                                                                        <Button
                                                                            style={{ width: `50%`, textAlign: 'center' }}
                                                                            color="info"
                                                                            name="ready"
                                                                            className={'btn btn-simple'}
                                                                            onClick={() => readyItem(item)}
                                                                        >
                                                                            Ready
                                                                        </Button>
                                                                        <Button
                                                                            style={{ width: `50%` }}
                                                                            color="info"
                                                                            name="decline"
                                                                            className={'btn btn-simple'}
                                                                            onClick={() => {
                                                                                setShipment(row.original);
                                                                                setItem(item);
                                                                                setShowDecline(true);
                                                                            }}
                                                                        >
                                                                            Decline
                                                                        </Button>
                                                                    </ButtonGroup>
                                                                </div>
                                                            ) : item.status === 'item_declined' ? (
                                                                <div>
                                                                    <Button
                                                                        style={{ width: `100%`, textAlign: 'center' }}
                                                                        color="danger"
                                                                        name="declined"
                                                                        className={'btn btn-simple'}
                                                                        disabled
                                                                    >
                                                                        Declined
                                                                    </Button>
                                                                </div>
                                                            ) : item.status === 'item_ready' ? (
                                                                <div>
                                                                    <Button
                                                                        style={{ width: `100%`, textAlign: 'center' }}
                                                                        color="success"
                                                                        name="approved"
                                                                        className={'btn btn-simple'}
                                                                        disabled
                                                                    >
                                                                        Approved for Pickup
                                                                    </Button>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    );
                                                })
                                            ) : row.original.shipment_type === 'school_lunch' ? (
                                                <SchoolLunchInfo order={row.original.Orders[0]} shipment={row.original} />
                                            ) : row.original.shipment_type === 'direct' ? (
                                                <>
                                                    <Row className="merchant-order-detail-copy" style={{ paddingTop: 10, paddingBottom: 0, marginRight: 0, marginLeft: 0, textAlign: 'left' }}>
                                                        <Col className="col-2">
                                                            <BasicInfoDirectPick shipment={row.original} />
                                                        </Col>
                                                        {row.original.Orders[1] ? (
                                                            <Col className="col-2">
                                                                <BasicInfoDirectDrop shipment={row.original} />
                                                            </Col>
                                                        ) : null}
                                                        <Col className="col-3">
                                                            <strong>Load Details</strong>
                                                            <LoadDetails shipment={row.original} />
                                                        </Col>
                                                        <Col className="col-2">
                                                            <strong>Item(s) Description</strong>
                                                            <DeliveryDescription shipment={row.original} />
                                                        </Col>
                                                        <Col className="col-2">
                                                            <QuoteOrderButtons shipment={row.original} />
                                                        </Col>
                                                    </Row>
                                                    <AIRecommendation shipment={row.original} />
                                                </>
                                            ) : row.original.shipment_type === 'routed_direct' ? (
                                                <>
                                                    <Row className="merchant-order-detail-copy" style={{ paddingTop: 10, paddingBottom: 0, marginRight: 0, marginLeft: 0, textAlign: 'left' }}>
                                                        <BasicInfoRoutedWaypoint orders={row.original.Orders} display={false} />

                                                        <Col className="col-2">
                                                            <strong>Load Details</strong>
                                                            <LoadDetails shipment={row.original} />
                                                        </Col>

                                                        <Col className="col-2">
                                                            <QuoteOrderButtons shipment={row.original} />
                                                        </Col>

                                                        {/* <Col className="col-2">
                          <strong>Description of Item(s)</strong>
                          <RoutedDirectItemList orders={row.original.Orders} />
                        </Col> */}
                                                    </Row>
                                                </>
                                            ) : row.original.shipment_type === 'multidrop_direct' ? (
                                                <>
                                                    <Row className="merchant-order-detail-copy" style={{ paddingTop: 10, paddingBottom: 0, marginRight: 0, marginLeft: 0, textAlign: 'left' }}>
                                                        <BasicInfoMultidropWaypoint orders={row.original.Orders} display={false} />
                                                        <Col className="col-2">
                                                            <strong>Load Details</strong>
                                                            <LoadDetails shipment={row.original} />
                                                        </Col>
                                                        <Col className="col-2">
                                                            <QuoteOrderButtons shipment={row.original} />
                                                        </Col>
                                                    </Row>
                                                </>
                                            ) : (
                                                //! Direct styled Bridge Shipment
                                                // <>
                                                //   <strong style={{color: '#cdcaca', }}>Bridge Delivery - {row.original.status === "shipment_ready_for_driver" || row.original.status === "shipment_assigned" || row.original.status === "shipment_claimed" ? "Awaiting Driver " : "Status Unknown "}{row.original.is_expedited ? <p style={{color: '#f7941d'}}>Expedited Delivery</p> : null}</strong>
                                                //     {console.log("order: ", row.original.Orders[0])}
                                                //     <Row style={{paddingTop: 10, paddingBottom: 0, marginRight: 0, marginLeft: 0, textAlign: 'center'}}>
                                                //       <Col className='col-2'>
                                                //         <strong>Pickup Location</strong>
                                                //         <p>{row.original.Orders[0].location_blob.phone}</p>
                                                //         <p>{row.original.Orders[0].location_blob.email}</p>
                                                //         <p>{row.original.Orders[0].location_blob.address_1}</p>
                                                //         <p>{row.original.Orders[0].location_blob.city}, {row.original.Orders[0].location_blob.state}, {row.original.Orders[0].location_blob.zip}</p>
                                                //       </Col>
                                                //       { row.original.Orders[1] ?
                                                //       <Col className='col-2'>
                                                //         <strong>Dropoff Info</strong>
                                                //         <p>{row.original.Orders[1].location_blob.phone}</p>
                                                //         <p>{row.original.Orders[1].location_blob.email}</p>
                                                //         <p>{row.original.Orders[1].location_blob.address_1}</p>
                                                //         <p>{row.original.Orders[1].location_blob.city}, {row.original.Orders[1].location_blob.state}, {row.original.Orders[1].location_blob.zip}</p>
                                                //       </Col>
                                                //       : null }
                                                //       <Col className='col-3'>
                                                //         <strong>Load Details</strong>
                                                //         <p>Vehicle Type Suggested: <strong>{"  " + row.original.vehicle_type === 'car' ? 'Sedan' : row.original.vehicle_type === 'suv' ? 'SUV' : capitalize(row.original.vehicle_type.split('_').join(' '))}</strong></p>
                                                //         <p>Estimated Weight: <strong>{"  " + row.original.weight} lbs.</strong></p>
                                                //         <p>Gophrs Needed: <strong>{"  " + row.original.num_of_gophrs}</strong></p>
                                                //       </Col>
                                                //       <Col className='col-4'>
                                                //         <strong>Description of Item(s)</strong>
                                                //         <p>{row.original.description}</p>
                                                //       </Col>
                                                //   </Row>
                                                // </>
                                                //! Marketplace styled Bridge Shipment
                                                row.original.Orders[0].Items.map((item, index, items) => {
                                                    return (
                                                        <>
                                                            <div key={item.item_id} style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                                                                {/* {console.log("row: ", row)}
                                {console.log("SHipment type from merchant pending", row.original.shipment_type)} */}
                                                                {/* <div>
                                  <strong>Image</strong>
                                  <p>{item.product_blob.image ? <img src={item.product_blob.image} alt="Product Image" style={{width: "10%"}} />
                                  : "Not Pictured"}</p>
                                </div> */}
                                                                <div>
                                                                    <strong>Quantity</strong>
                                                                    <p style={{ textAlign: 'center' }}>{item.quantity}</p>
                                                                </div>
                                                                <div>
                                                                    <strong>Product Name</strong>
                                                                    {/* <p>{item.product_blob.name}</p> */}
                                                                    <p>
                                                                        {item.product_blob.name + ' '}
                                                                        {item.ItemOptions
                                                                            ? item.ItemOptions.length > 0
                                                                                ? item.ItemOptions.map((option, index) => {
                                                                                      if (index === 0) {
                                                                                          return '(' + option.option_name;
                                                                                      } else if (index > 0 && index !== item.ItemOptions.length - 1) {
                                                                                          return ', ' + option.option_name;
                                                                                      } else {
                                                                                          return ', ' + option.option_name + ')';
                                                                                      }
                                                                                  })
                                                                                : null
                                                                            : null}
                                                                    </p>
                                                                </div>
                                                                <div>
                                                                    <strong>Price</strong>
                                                                    {item.product_price === 0.0 ? <p>N/A</p> : <p>item.product_price</p>}
                                                                </div>

                                                                <div>
                                                                    <strong>SKU</strong>
                                                                    {item.product_blob.sku ? <p>item.product_blob.sku</p> : <p>N/A</p>}
                                                                </div>
                                                                <div>
                                                                    <strong>Wight</strong>
                                                                    <p>{item.product_blob.weight.toFixed(2)}</p>
                                                                </div>
                                                                <div>
                                                                    <strong>Dimensions</strong>
                                                                    <p>
                                                                        L: {item.product_blob.length ? item.product_blob.length.toFixed(2) : 'N/A'}, W:{' '}
                                                                        {item.product_blob.width ? item.product_blob.width.toFixed(2) : 'N/A'}, H:{' '}
                                                                        {item.product_blob.height ? item.product_blob.height.toFixed(2) : 'N/A'}
                                                                    </p>
                                                                </div>
                                                                {item.status === 'item_created' ? (
                                                                    <div>
                                                                        <ButtonGroup>
                                                                            <Button
                                                                                style={{ width: `50%`, textAlign: 'center' }}
                                                                                color="success"
                                                                                name="ready"
                                                                                className={'btn btn-simple'}
                                                                                onClick={() => readyItem(item)}
                                                                            >
                                                                                Ready
                                                                            </Button>
                                                                            <Button
                                                                                style={{ width: `50%`, textAlign: `center` }}
                                                                                // color="info"
                                                                                color="danger"
                                                                                name="decline"
                                                                                className={'btn btn-simple'}
                                                                                // className={'btn btn-danger'}
                                                                                onClick={() => {
                                                                                    setShipment(row.original);
                                                                                    setItem(item);
                                                                                    setShowDecline(true);
                                                                                }}
                                                                            >
                                                                                Decline
                                                                            </Button>
                                                                        </ButtonGroup>
                                                                    </div>
                                                                ) : item.status === 'item_declined' ? (
                                                                    <div>
                                                                        <Button
                                                                            style={{ width: `100%`, textAlign: 'center' }}
                                                                            color="danger"
                                                                            name="declined"
                                                                            className={'btn btn-simple'}
                                                                            disabled
                                                                        >
                                                                            Declined
                                                                        </Button>
                                                                    </div>
                                                                ) : item.status === 'item_ready' ? (
                                                                    <div>
                                                                        <Button
                                                                            style={{ width: `100%`, textAlign: 'center' }}
                                                                            color="success"
                                                                            name="approved"
                                                                            className={'btn btn-simple'}
                                                                            disabled
                                                                        >
                                                                            Approved for Pickup
                                                                        </Button>
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </>
                                                    );
                                                })
                                            )
                                        ) : selected === 'inProgress' ? (
                                            <div style={{ paddingLeft: 20 }}>
                                                {row.original.shipment_type === 'direct' ? (
                                                    <>
                                                        <Row className="merchant-order-detail-copy" style={{ paddingTop: 10, paddingBottom: 0, marginRight: 0, marginLeft: 0, textAlign: 'left' }}>
                                                            <Col className="col-2">
                                                                <BasicInfoDirectPick shipment={row.original} />
                                                            </Col>
                                                            {row.original.Orders[1] ? (
                                                                <Col className="col-2">
                                                                    <BasicInfoDirectDrop shipment={row.original} />
                                                                </Col>
                                                            ) : null}
                                                            <Col className="col-3">
                                                                <strong>Load Details</strong>
                                                                <LoadDetails shipment={row.original} />
                                                            </Col>
                                                            <Col className="col-2.5">
                                                                <DriverInfo shipment={row.original} />
                                                                <FileViewer
                                                                    shipmentId={row.original.shipment_id}
                                                                    shipment={row.original}
                                                                    shipmentHost={getShipmentActionHost(row.original)}
                                                                    showGenerateBolButton={false}
                                                                    lockGoPilotReports={goPilotReportLocked}
                                                                    onModalStateChange={setDocumentsModalOpen}
                                                                />
                                                                <FileUpload shipmentId={row.original.shipment_id} shipmentFriendlyId={row.original.shipment_friendly_id} />
                                                            </Col>
                                                            <Col className="col-3">
                                                                <strong>Item(s) Description</strong>
                                                                <DeliveryDescription shipment={row.original} />
                                                            </Col>
                                                        </Row>
                                                        <AIRecommendation shipment={row.original} />
                                                    </>
                                                ) : row.original.shipment_type === 'routed_direct' ? (
                                                    <>
                                                        <Row className="merchant-order-detail-copy" style={{ paddingTop: 10, paddingBottom: 0, marginRight: 0, marginLeft: 0, textAlign: 'left' }}>
                                                            <BasicInfoRoutedWaypoint orders={row.original.Orders} display={true} />
                                                            <Col className="col-2">
                                                                <strong>Load Details</strong>
                                                                <LoadDetails shipment={row.original} />
                                                            </Col>
                                                            <Col className="col-2.5">
                                                                <DriverInfo shipment={row.original} />
                                                            </Col>
                                                            {/* <Col className="col-2">
                          <strong>Description of Item(s)</strong>
                          <RoutedDirectItemList orders={row.original.Orders} />
                        </Col> */}
                                                        </Row>
                                                    </>
                                                ) : row.original.shipment_type === 'multidrop_direct' ? (
                                                    <>
                                                        <Row className="merchant-order-detail-copy" style={{ paddingTop: 10, paddingBottom: 0, marginRight: 0, marginLeft: 0, textAlign: 'left' }}>
                                                            <BasicInfoMultidropWaypoint orders={row.original.Orders} display={true} />
                                                            <Col className="col-2">
                                                                <strong>Load Details</strong>
                                                                <LoadDetails shipment={row.original} />
                                                            </Col>
                                                            <MultiDropDocuments shipment={row.original} allowUpload={true} />
                                                            <Col className="col-2.5">
                                                                <DriverInfo shipment={row.original} />
                                                            </Col>
                                                        </Row>
                                                    </>
                                                ) : row.original.shipment_type === 'bridge' ? (
                                                    //! Direct style bridge shipment
                                                    // <>
                                                    //   <strong style={{ color: '#cdcaca' }}>
                                                    //     Bridge Delivery -{' '}
                                                    //     {row.original.status === 'shipment_ready_for_driver' || row.original.status === 'shipment_assigned' || row.original.status === 'shipment_claimed'
                                                    //       ? 'Awaiting Driver '
                                                    //       : 'Status Unknown '}
                                                    //     {row.original.is_expedited ? <p style={{ color: '#f7941d' }}>Expedited Delivery</p> : null}
                                                    //   </strong>
                                                    //   {console.log('order: ', row.original.Orders[0])}
                                                    //   <Row style={{ paddingTop: 10, paddingBottom: 0, marginRight: 0, marginLeft: 0, textAlign: 'center' }}>
                                                    //     <Col className="col-2">
                                                    //       <strong>Pickup Location</strong>
                                                    //       <p>{row.original.Orders[0].location_blob.phone}</p>
                                                    //       <p>{row.original.Orders[0].location_blob.email}</p>
                                                    //       <p>{row.original.Orders[0].location_blob.address_1}</p>
                                                    //       <p>
                                                    //         {row.original.Orders[0].location_blob.city}, {row.original.Orders[0].location_blob.state}, {row.original.Orders[0].location_blob.zip}
                                                    //       </p>
                                                    //     </Col>
                                                    //     {row.original.Orders[1] ? (
                                                    //       <Col className="col-2">
                                                    //         <strong>Dropoff Info</strong>
                                                    //         <p>{row.original.Orders[1].location_blob.phone}</p>
                                                    //         <p>{row.original.Orders[1].location_blob.email}</p>
                                                    //         <p>{row.original.Orders[1].location_blob.address_1}</p>
                                                    //         <p>
                                                    //           {row.original.Orders[1].location_blob.city}, {row.original.Orders[1].location_blob.state}, {row.original.Orders[1].location_blob.zip}
                                                    //         </p>
                                                    //       </Col>
                                                    //     ) : null}
                                                    //     <Col className="col-3">
                                                    //       <strong>Load Details</strong>
                                                    //       <p>
                                                    //         Vehicle Type Suggested:{' '}
                                                    //         <strong>
                                                    //           {'  ' + row.original.vehicle_type === 'car'
                                                    //             ? 'Sedan'
                                                    //             : row.original.vehicle_type === 'suv'
                                                    //             ? 'SUV'
                                                    //             : capitalize(row.original.vehicle_type.split('_').join(' '))}
                                                    //         </strong>
                                                    //       </p>
                                                    //       <p>
                                                    //         Estimated Weight: <strong>{'  ' + row.original.weight} lbs.</strong>
                                                    //       </p>
                                                    //       <p>
                                                    //         Gophrs Needed: <strong>{'  ' + row.original.num_of_gophrs}</strong>
                                                    //       </p>
                                                    //     </Col>
                                                    //     <Col className="col-4">
                                                    //       <strong>Description of Item(s)</strong>
                                                    //       <p>{row.original.description}</p>
                                                    //     </Col>
                                                    //   </Row>
                                                    // </>
                                                    //! Marketplace style bridge shipment
                                                    <>
                                                        {/* <strong style={{ color: '#cdcaca' }}>
                        Bridge Delivery -{' '}
                        {row.original.status === 'shipment_ready_for_driver' || row.original.status === 'shipment_assigned' || row.original.status === 'shipment_claimed'
                          ? 'Awaiting Driver '
                          : 'Status Unknown '}
                        {row.original.is_expedited ? <p style={{ color: '#f7941d' }}>Expedited Delivery</p> : null}
                      </strong> */}
                                                        {row.original.Orders[0].Items.map((item, index, items) => {
                                                            return (
                                                                <div key={getMarketplaceItemKey(item, index)} style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                                                                    <div>
                                                                        <strong>Quantity</strong>
                                                                        <p style={{ textAlign: 'center' }}>{item.quantity}</p>
                                                                    </div>
                                                                    <div>
                                                                        <strong>Product Name</strong>
                                                                        <p>
                                                                            {item.product_blob.name + ' '}
                                                                            {item.ItemOptions
                                                                                ? item.ItemOptions.length > 0
                                                                                    ? item.ItemOptions.map((option, index) => {
                                                                                          if (index === 0) {
                                                                                              return '(' + option.option_name;
                                                                                          } else if (index > 0 && index !== item.ItemOptions.length - 1) {
                                                                                              return ', ' + option.option_name;
                                                                                          } else {
                                                                                              return ', ' + option.option_name + ')';
                                                                                          }
                                                                                      })
                                                                                    : null
                                                                                : null}
                                                                        </p>
                                                                    </div>
                                                                    <div>
                                                                        <strong>Price</strong>
                                                                        {item.product_price === 0.0 ? <p>N/A</p> : <p>item.product_price</p>}
                                                                    </div>

                                                                    <div>
                                                                        <strong>SKU</strong>
                                                                        {item.product_blob.sku ? <p>item.product_blob.sku</p> : <p>N/A</p>}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Wight</strong>
                                                                        <p>{item.product_blob.weight.toFixed(2)}</p>
                                                                    </div>
                                                                    <div>
                                                                        <strong>Dimensions</strong>
                                                                        <p>
                                                                            L: {item.product_blob.length ? item.product_blob.length.toFixed(2) : 'N/A'}, W:{' '}
                                                                            {item.product_blob.width ? item.product_blob.width.toFixed(2) : 'N/A'}, H:{' '}
                                                                            {item.product_blob.height ? item.product_blob.height.toFixed(2) : 'N/A'}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                ) : row.original.shipment_type === 'school_lunch' ? (
                                                    <SchoolLunchInfo order={row.original.Orders[0]} shipment={row.original} />
                                                ) : (
                                                    <>
                                                        {/* <strong style={{ color: '#cdcaca' }}>
                        Gophr App Delivery -{' '}
                        {row.original.status === 'shipment_ready_for_driver' || row.original.status === 'shipment_assigned' || row.original.status === 'shipment_claimed'
                          ? 'Awaiting Driver '
                          : 'Status Unknown '}
                        {row.original.is_expedited ? <p style={{ color: '#f7941d' }}>Expedited Delivery</p> : null}
                      </strong> */}
                                                        {row.original.Orders[0].Items.map((item, index, items) => {
                                                            return (
                                                                <div key={getMarketplaceItemKey(item, index)} style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                                                                    <div>
                                                                        <strong>Quantity</strong>
                                                                        <p style={{ textAlign: 'center' }}>{item.quantity}</p>
                                                                    </div>
                                                                    <div>
                                                                        <strong>Product Name</strong>
                                                                        <p>
                                                                            {item.product_blob.name + ' '}
                                                                            {item.ItemOptions
                                                                                ? item.ItemOptions.length > 0
                                                                                    ? item.ItemOptions.map((option, index) => {
                                                                                          if (index === 0) {
                                                                                              return '(' + option.option_name;
                                                                                          } else if (index > 0 && index !== item.ItemOptions.length - 1) {
                                                                                              return ', ' + option.option_name;
                                                                                          } else {
                                                                                              return ', ' + option.option_name + ')';
                                                                                          }
                                                                                      })
                                                                                    : null
                                                                                : null}
                                                                        </p>
                                                                    </div>
                                                                    <div>
                                                                        <strong>Price</strong>
                                                                        {item.product_blob.subtotal ? <p>${item.product_blob.subtotal.toFixed(2)}</p> : <p>Not Found</p>}
                                                                    </div>
                                                                    <div>
                                                                        <strong>SKU</strong>
                                                                        <p>{item.product_blob.sku}</p>
                                                                    </div>
                                                                    {/* {item.status === "item_created" ?
                                      <div>
                                        <ButtonGroup>
                                          <Button 
                                            style={{width: `50%`, textAlign: 'center'}}
                                            color="info"
                                            name="ready"
                                            className={"btn btn-simple"}
                                            onClick={() => readyItem(item)}
                                          >
                                            Ready
                                          </Button>
                                          <Button 
                                            style={{width: `50%`}}
                                            color="info"
                                            name="decline"
                                            className={"btn btn-simple"}
                                            onClick={() =>{
                                              setItem(item);
                                              setShowDecline(true)
                                            }}
                                          >
                                            Decline
                                          </Button>
                                        </ButtonGroup>
                                      </div>
                                    : item.status === "item_declined" ?
                                      <div>
                                        <Button 
                                          style={{width: `100%`, textAlign: 'center'}}
                                          color="danger"
                                          name="declined"
                                          className={"btn btn-simple"}
                                          disabled
                                        >
                                          Declined
                                        </Button>
                                      </div>
                                    : item.status === "item_ready" ?
                                      <div>
                                        <Button 
                                          style={{width: `100%`, textAlign: 'center'}}
                                          color="success"
                                          name="approved"
                                          className={"btn btn-simple"}
                                          disabled
                                        >
                                          Approved for Pickup
                                        </Button>
                                      </div>
                                    : null } */}
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                            </div>
                                        ) : selected === 'completed' ? (
                                            row.original.shipment_type === 'direct' ? (
                                                <>
                                                    <Row style={{ paddingTop: 20, paddingBottom: 20, marginRight: 0, marginLeft: 0, textAlign: 'left' }}>
                                                        <Col className="col-2">
                                                            <BasicInfoDirectPick shipment={row.original} />
                                                        </Col>
                                                        {row.original.Orders[1] ? (
                                                            <Col className="col-2">
                                                                <BasicInfoDirectDrop shipment={row.original} />
                                                            </Col>
                                                        ) : null}
                                                        <Col className="col-2">
                                                            <strong>Load Details</strong>

                                                            <LoadDetails shipment={row.original} />
                                                        </Col>
                                                        <Col className="col-2">
                                                            <strong>Order Description</strong>
                                                            <DeliveryDescription shipment={row.original} />
                                                        </Col>
                                                        <Col className="col-4">
                                                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'nowrap' }}>
                                                                <div style={{ flex: '0 0 52%', minWidth: 0 }}>
                                                                    <DriverInfo shipment={row.original} showEta={false} />
                                                                </div>
                                                                <div style={{ flex: '0 0 48%', minWidth: 0 }}>
                                                                    <strong>Status</strong>
                                                                    <p>
                                                                        {row.original.status === 'shipment_enroute'
                                                                            ? 'Delivery in Progress'
                                                                            : row.original.status === 'shipment_completed'
                                                                            ? 'Complete'
                                                                            : 'None Found'}
                                                                    </p>
                                                                    <FileViewer
                                                                        shipmentId={row.original.shipment_id}
                                                                        shipment={row.original}
                                                                        shipmentHost={getShipmentActionHost(row.original)}
                                                                        showGenerateBolButton={false}
                                                                        lockGoPilotReports={goPilotReportLocked}
                                                                        deadDropPhotos={
                                                                            row.original.ShipmentHandshakes.length > 0 && row.original.ShipmentHandshakes[0].type === 'dead_drop'
                                                                                ? [row.original.ShipmentHandshakes[0].url]
                                                                                : []
                                                                        }
                                                                        shipFriendId={row.original.shipment_friendly_id}
                                                                        onModalStateChange={setDocumentsModalOpen}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </Col>

                                                        {/* <Col>
                                <strong>Driver Info</strong>
                                <p>{row.original.Drivers[0].driver_blob.first_name}</p>
                                <p>{row.original.Drivers[0].vehicle_blob.year}{" "}{row.original.Drivers[0].vehicle_blob.color}{" "}{row.original.Drivers[0].vehicle_blob.make}{" "}{row.original.Drivers[0].vehicle_blob.model}</p>
                            </Col> */}
                                                    </Row>
                                                    <AIRecommendation shipment={row.original} />
                                                </>
                                            ) : row.original.shipment_type === 'routed_direct' ? (
                                                <>
                                                    <Row className="merchant-order-detail-copy" style={{ paddingTop: 10, paddingBottom: 0, marginRight: 0, marginLeft: 0, textAlign: 'left' }}>
                                                        <BasicInfoRoutedWaypoint orders={row.original.Orders} display={false} />
                                                        <Col className="col-2">
                                                            <strong>Load Details</strong>
                                                            <LoadDetails shipment={row.original} />
                                                        </Col>

                                                        {/* <Col className="col-2">
                        <strong>Description of Item(s)</strong>
                        <RoutedDirectItemList orders={row.original.Orders} />
                      </Col> */}

                                                        <Col className="col-2.5">
                                                            <DriverInfo shipment={row.original} showEta={false} />
                                                        </Col>
                                                        <Col className="col-2.5">
                                                            <strong>Status</strong>
                                                            <p>
                                                                {row.original.status === 'shipment_enroute'
                                                                    ? 'Delivery in Progress'
                                                                    : row.original.status === 'shipment_completed'
                                                                    ? 'Complete'
                                                                    : 'None Found'}
                                                            </p>
                                                        </Col>
                                                    </Row>
                                                </>
                                            ) : row.original.shipment_type === 'multidrop_direct' ? (
                                                <>
                                                    <Row className="merchant-order-detail-copy" style={{ paddingTop: 10, paddingBottom: 0, marginRight: 0, marginLeft: 0, textAlign: 'left' }}>
                                                        <BasicInfoMultidropWaypoint orders={row.original.Orders} display={false} />
                                                        <Col className="col-2">
                                                            <strong>Load Details</strong>
                                                            <LoadDetails shipment={row.original} />
                                                        </Col>
                                                        <MultiDropDocuments shipment={row.original} />
                                                        <Col className="col-2.5">
                                                            <DriverInfo shipment={row.original} showEta={false} />
                                                        </Col>
                                                        <Col className="col-2.5">
                                                            <strong>Status</strong>
                                                            <p>
                                                                {row.original.status === 'shipment_enroute'
                                                                    ? 'Delivery in Progress'
                                                                    : row.original.status === 'shipment_completed'
                                                                    ? 'Complete'
                                                                    : 'None Found'}
                                                            </p>
                                                        </Col>
                                                    </Row>
                                                </>
                                            ) : row.original.shipment_type === 'bridge' ? (
                                                // ! Direct style bridge shipment
                                                // <>
                                                //   <strong style={{ color: '#cdcaca' }}>
                                                //     Bridge Delivery - {row.original.status === 'shipment_completed' ? 'Completed' : 'Status Unknown'}
                                                //     {row.original.is_expedited ? <p style={{ color: '#f7941d' }}>Expedited Delivery</p> : null}
                                                //   </strong>
                                                //   <Row style={{ paddingTop: 20, paddingBottom: 20, marginRight: 0, marginLeft: 0 }}>
                                                //     <Col className="col-2">
                                                //       <strong>Pickup Information</strong>
                                                //       <p>
                                                //         {row.original.Orders[0].location_blob.first_name} {row.original.Orders[0].location_blob.last_name}
                                                //       </p>
                                                //       <p>{row.original.Orders[0].location_blob.phone}</p>
                                                //       <p>{row.original.Orders[0].location_blob.email}</p>
                                                //       <p>{row.original.Orders[0].location_blob.address_1}</p>
                                                //       <p>
                                                //         {row.original.Orders[0].location_blob.city}, {row.original.Orders[0].location_blob.state}, {row.original.Orders[0].location_blob.zip}
                                                //       </p>
                                                //     </Col>
                                                //     {row.original.Orders[1] ? (
                                                //       <Col className="col-2">
                                                //         <strong>Dropoff Location</strong>
                                                //         <p>{row.original.Orders[1].location_blob.phone}</p>
                                                //         <p>{row.original.Orders[1].location_blob.email}</p>
                                                //         <p>{row.original.Orders[1].location_blob.address_1}</p>
                                                //         <p>
                                                //           {row.original.Orders[1].location_blob.city}, {row.original.Orders[1].location_blob.state}, {row.original.Orders[1].location_blob.zip}
                                                //         </p>
                                                //       </Col>
                                                //     ) : null}
                                                //     <Col className="col-2">
                                                //       <strong>Details</strong>
                                                //       <p>
                                                //         Suggested Vehicle:{' '}
                                                //         <strong>
                                                //           {'  ' + row.original.vehicle_type === 'car'
                                                //             ? 'Sedan'
                                                //             : row.original.vehicle_type === 'suv'
                                                //             ? 'SUV'
                                                //             : capitalize(row.original.vehicle_type.split('_').join(' '))}
                                                //         </strong>
                                                //       </p>
                                                //       <p>
                                                //         Estimated Weight: <strong>{'  ' + row.original.weight} lbs.</strong>
                                                //       </p>
                                                //       <p>
                                                //         Gophrs Needed: <strong>{'  ' + row.original.num_of_gophrs}</strong>
                                                //       </p>
                                                //     </Col>
                                                //     <Col className="col-3">
                                                //       <strong>Order Description</strong>
                                                //       <p>{row.original.description}</p>
                                                //     </Col>
                                                //     <Col className="col-2">
                                                //       <strong>Status</strong>
                                                //       <p>{row.original.status === 'shipment_enroute' ? 'Delivery in Progress' : row.original.status === 'shipment_completed' ? 'Complete' : 'None Found'}</p>
                                                //     </Col>
                                                //     {/* <Col>
                                                //             <strong>Driver Info</strong>
                                                //             <p>{row.original.Drivers[0].driver_blob.first_name}</p>
                                                //             <p>{row.original.Drivers[0].vehicle_blob.year}{" "}{row.original.Drivers[0].vehicle_blob.color}{" "}{row.original.Drivers[0].vehicle_blob.make}{" "}{row.original.Drivers[0].vehicle_blob.model}</p>
                                                //         </Col> */}
                                                //   </Row>
                                                // </>
                                                // ! Marketplace style bridge shipment
                                                <>
                                                    {row.original.Orders[0].Items.map((item, index, items) => {
                                                        return (
                                                            <div key={getMarketplaceItemKey(item, index)} style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                                                                <div>
                                                                    <strong>Quantity</strong>
                                                                    <p style={{ textAlign: 'center' }}>{item.quantity}</p>
                                                                </div>
                                                                <div>
                                                                    <strong>Product Name</strong>
                                                                    <p>
                                                                        {item.product_blob.name + ' '}
                                                                        {item.ItemOptions
                                                                            ? item.ItemOptions.length > 0
                                                                                ? item.ItemOptions.map((option, index) => {
                                                                                      if (index === 0) {
                                                                                          return '(' + option.option_name;
                                                                                      } else if (index > 0 && index !== item.ItemOptions.length - 1) {
                                                                                          return ', ' + option.option_name;
                                                                                      } else {
                                                                                          return ', ' + option.option_name + ')';
                                                                                      }
                                                                                  })
                                                                                : null
                                                                            : null}
                                                                    </p>
                                                                </div>
                                                                <div>
                                                                    <strong>Price</strong>
                                                                    {item.product_price === 0.0 ? <p>N/A</p> : <p>item.product_price</p>}
                                                                </div>

                                                                <div>
                                                                    <strong>SKU</strong>
                                                                    {item.product_blob.sku ? <p>item.product_blob.sku</p> : <p>N/A</p>}
                                                                </div>
                                                                <div>
                                                                    <strong>Wight</strong>
                                                                    <p>{item.product_blob.weight.toFixed(2)}</p>
                                                                </div>
                                                                <div>
                                                                    <strong>Dimensions</strong>
                                                                    <p>
                                                                        L: {item.product_blob.length ? item.product_blob.length.toFixed(2) : 'N/A'}, W:{' '}
                                                                        {item.product_blob.width ? item.product_blob.width.toFixed(2) : 'N/A'}, H:{' '}
                                                                        {item.product_blob.height ? item.product_blob.height.toFixed(2) : 'N/A'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            ) : row.original.shipment_type === 'school_lunch' ? (
                                                <SchoolLunchInfo order={row.original.Orders[0]} shipment={row.original} />
                                            ) : (
                                                <>
                                                    {row.original.Orders[0].Items.map((item, index, items) => {
                                                        return (
                                                            <div key={getMarketplaceItemKey(item, index)} style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                                                                <div>
                                                                    <strong>Quantity</strong>
                                                                    <p style={{ textAlign: 'center' }}>{item.quantity}</p>
                                                                </div>
                                                                <div>
                                                                    <strong>Product Name</strong>
                                                                    <p>
                                                                        {item.product_blob.name + ' '}
                                                                        {item.ItemOptions
                                                                            ? item.ItemOptions.length > 0
                                                                                ? item.ItemOptions.map((option, index) => {
                                                                                      if (index === 0) {
                                                                                          return '(' + option.option_name;
                                                                                      } else if (index > 0 && index !== item.ItemOptions.length - 1) {
                                                                                          return ', ' + option.option_name;
                                                                                      } else {
                                                                                          return ', ' + option.option_name + ')';
                                                                                      }
                                                                                  })
                                                                                : null
                                                                            : null}
                                                                    </p>
                                                                </div>
                                                                <div>
                                                                    <strong>Price</strong>
                                                                    {item.product_blob.total ? <p>${item.product_blob.total.toFixed(2)}</p> : <p>Not Found</p>}
                                                                </div>
                                                                <div>
                                                                    <strong>SKU</strong>
                                                                    <p>{item.product_blob.sku}</p>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            )
                                        ) : selected === 'saved' ? (
                                            <div className="saved-container">
                                                {row.original.shipment_type === 'direct' ? (
                                                    <>
                                                        <Row className="merchant-order-detail-copy" style={{ paddingTop: 10, paddingBottom: 0, marginRight: 0, marginLeft: 0, textAlign: 'left' }}>
                                                            <Col className="col-2">
                                                                <BasicInfoDirectPick shipment={row.original} />
                                                            </Col>
                                                            {row.original.Orders[1] ? (
                                                                <Col className="col-2">
                                                                    <BasicInfoDirectDrop shipment={row.original} />
                                                                </Col>
                                                            ) : null}
                                                            <Col className="col-3">
                                                                <strong>Load Details</strong>
                                                                <LoadDetails shipment={row.original} />
                                                                <FileViewer
                                                                    shipmentId={row.original.shipment_id}
                                                                    shipment={row.original}
                                                                    shipmentHost={getShipmentActionHost(row.original)}
                                                                    showGenerateBolButton={false}
                                                                    lockGoPilotReports={goPilotReportLocked}
                                                                    onModalStateChange={setDocumentsModalOpen}
                                                                />
                                                                <FileUpload shipmentId={row.original.shipment_id} shipmentFriendlyId={row.original.shipment_friendly_id} />
                                                            </Col>
                                                            <Col className="col-2">
                                                                <strong>Item(s) Description</strong>
                                                                <DeliveryDescription shipment={row.original} />
                                                            </Col>
                                                            <Col className="col-3">
                                                                <div className="second-row" style={{ display: `flex`, flexDirection: `row`, justifyContent: `center` }}>
                                                                    {canShowSavedColumnActions(row.original) ? <ButtonGroup>
                                                                        <Button
                                                                            color="info"
                                                                            name="ready-pickup"
                                                                            style={{ padding: 10 }}
                                                                            onClick={e => {
                                                                                setSelectSavedAction(e.target.value);
                                                                                setShipment(row.original);
                                                                                setShowAlert1(true);
                                                                            }}
                                                                            className={selectSavedAction === 'ready-pickup' ? 'btn' : 'btn-simple'}
                                                                        >
                                                                            Ready for Pick Up
                                                                        </Button>
                                                                        <Button
                                                                            color="info"
                                                                            name="delete-order"
                                                                            style={{ padding: 10 }}
                                                                            onClick={e => {
                                                                                setSelectSavedAction(e.target.value);
                                                                                setShipment(row.original);
                                                                                setShowAlert(true);
                                                                            }}
                                                                            className={selectSavedAction === 'delete-order' ? 'btn' : 'btn-simple'}
                                                                        >
                                                                            Delete Order
                                                                        </Button>
                                                                    </ButtonGroup> : null}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                        <AIRecommendation shipment={row.original} />
                                                    </>
                                                ) : row.original.shipment_type === 'routed_direct' ? (
                                                    <>
                                                        <Row className="merchant-order-detail-copy" style={{ paddingTop: 10, paddingBottom: 0, marginRight: 0, marginLeft: 0, textAlign: 'left' }}>
                                                            <BasicInfoRoutedWaypoint orders={row.original.Orders} display={false} />
                                                            <Col className="col-2">
                                                                <strong>Load Details</strong>
                                                                <LoadDetails shipment={row.original} />
                                                            </Col>

                                                            {/* <Col className="col-2">
                          <strong>Description of Item(s)</strong>
                          <RoutedDirectItemList orders={row.original.Orders} />
                        </Col> */}
                                                            <Col className="col-2.5">
                                                                <div className="second-row" style={{ display: `flex`, flexDirection: `row`, justifyContent: `center` }}>
                                                                    {canShowSavedColumnActions(row.original) ? <ButtonGroup>
                                                                        <Button
                                                                            color="info"
                                                                            name="ready-pickup"
                                                                            style={{ padding: 10 }}
                                                                            onClick={e => {
                                                                                setSelectSavedAction(e.target.value);
                                                                                setShipment(row.original);
                                                                                setShowAlert1(true);
                                                                            }}
                                                                            className={selectSavedAction === 'ready-pickup' ? 'btn' : 'btn-simple'}
                                                                        >
                                                                            Ready for Pick Up
                                                                        </Button>
                                                                        <Button
                                                                            color="info"
                                                                            name="delete-order"
                                                                            style={{ padding: 10 }}
                                                                            onClick={e => {
                                                                                setSelectSavedAction(e.target.value);
                                                                                setShipment(row.original);
                                                                                setShowAlert(true);
                                                                            }}
                                                                            className={selectSavedAction === 'delete-order' ? 'btn' : 'btn-simple'}
                                                                        >
                                                                            Delete Order
                                                                        </Button>
                                                                    </ButtonGroup> : null}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </>
                                                ) : row.original.shipment_type === 'multidrop_direct' ? (
                                                    <>
                                                        <Row className="merchant-order-detail-copy" style={{ paddingTop: 10, paddingBottom: 0, marginRight: 0, marginLeft: 0, textAlign: 'left' }}>
                                                            <BasicInfoMultidropWaypoint orders={row.original.Orders} display={false} />
                                                            <Col className="col-2">
                                                                <strong>Load Details</strong>
                                                                <LoadDetails shipment={row.original} />
                                                            </Col>
                                                        </Row>
                                                    </>
                                                ) : (
                                                    <>
                                                        {' '}
                                                        {row.original.Orders[0].Items.map((item, index, items) => {
                                                            return (
                                                                <div key={getMarketplaceItemKey(item, index)} style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                                                                    <div>
                                                                        <strong>Quantity</strong>
                                                                        <p style={{ textAlign: 'center' }}>{item.quantity}</p>
                                                                    </div>
                                                                    <div>
                                                                        <strong>Product Name</strong>
                                                                        <p>
                                                                            {item.product_blob.name + ' '}
                                                                            {item.ItemOptions
                                                                                ? item.ItemOptions.length > 0
                                                                                    ? item.ItemOptions.map((option, index) => {
                                                                                          if (index === 0) {
                                                                                              return '(' + option.option_name;
                                                                                          } else if (index > 0 && index !== item.ItemOptions.length - 1) {
                                                                                              return ', ' + option.option_name;
                                                                                          } else {
                                                                                              return ', ' + option.option_name + ')';
                                                                                          }
                                                                                      })
                                                                                    : null
                                                                                : null}
                                                                        </p>
                                                                    </div>
                                                                    <div>
                                                                        <strong>Price</strong>
                                                                        {item.product_price === 0.0 ? <p>N/A</p> : <p>item.product_price</p>}
                                                                    </div>

                                                                    <div>
                                                                        <strong>SKU</strong>
                                                                        {item.product_blob.sku ? <p>item.product_blob.sku</p> : <p>N/A</p>}
                                                                    </div>
                                                                    <div>
                                                                        <strong>Wight</strong>
                                                                        <p>{item.product_blob.weight.toFixed(2)}</p>
                                                                    </div>
                                                                    <div>
                                                                        <strong>Dimensions</strong>
                                                                        <p>
                                                                            L: {item.product_blob.length ? item.product_blob.length.toFixed(2) : 'N/A'}, W:{' '}
                                                                            {item.product_blob.width ? item.product_blob.width.toFixed(2) : 'N/A'}, H:{' '}
                                                                            {item.product_blob.height ? item.product_blob.height.toFixed(2) : 'N/A'}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            }}
                        />
                    </div>
                </ResizableBox>
                <ResizableBox className="box" width={3000} height={1200} minConstraints={[1000, 1000]} maxConstraints={[10000, 2000]} draggableOpts={{ grid: [10, 10] }} axis="x">
                    <div className="float-child-map">
                        <DispatchMap
                            trafficLayer={trafficLayer}
                            statusFilter={[]}
                            focusedDriver={merchantDispatchFocusedDriver}
                            hasActiveOrderExpansion={Boolean(shipmentClick)}
                            weatherLayer={false}
                        />
                    </div>
                </ResizableBox>
            </div>
            <div id="warning-alert" hidden={!showAlert1}>
                <ReactBSAlert
                    warning
                    style={{ display: 'block', marginTop: '-200px' }}
                    title="Are you sure?"
                    onConfirm={() => {
                        // setSelected("pending");
                        setSelected('inProgress');
                        readyShipment(shipment);
                        setShipment('');
                        setShowAlert1(false);
                    }}
                    onCancel={() => {
                        setShipment('');
                        setShowAlert1(false);
                    }}
                    confirmBtnBsStyle="danger"
                    cancelBtnBsStyle="default"
                    confirmBtnText="Confirm"
                    cancelBtnText="Cancel"
                    showCancel
                    btnSize=""
                >
                    <div>You are about to mark an order as Ready for Pick Up.</div>
                    <div>This action cannot be undone.</div>
                </ReactBSAlert>
            </div>
            <div id="warning-alert" hidden={!showAlert}>
                <ReactBSAlert
                    warning
                    style={{ display: 'block', marginTop: '-200px' }}
                    title="Are you sure?"
                    onConfirm={() => {
                        cancelShipment(shipment);
                        setShipment('');
                        setShowAlert(false);
                        // setSelected("pending");
                        setSelected('inProgress');
                    }}
                    onCancel={() => {
                        setShipment('');
                        setShowAlert(false);
                    }}
                    confirmBtnBsStyle="danger"
                    cancelBtnBsStyle="default"
                    confirmBtnText="Confirm"
                    cancelBtnText="Cancel"
                    showCancel
                    btnSize=""
                >
                    <div>You are about to delete a saved delivery.</div>
                    <div>This action cannot be undone.</div>
                </ReactBSAlert>
            </div>
            <div id="warning-alert-decline-quote-shipment" hidden={!showDeclineQuoteShipmentAlert}>
                <ReactBSAlert
                    warning
                    style={{ display: 'block', marginTop: '-200px' }}
                    title="Are you sure?"
                    onConfirm={() => {
                        cancelShipment(shipment);
                        setShipment('');
                        setShowDeclineQuoteShipmentAlert(false);
                        // setSelected('pending');
                        setSelected('inProgress');
                    }}
                    onCancel={() => {
                        setShipment('');
                        setShowDeclineQuoteShipmentAlert(false);
                    }}
                    confirmBtnBsStyle="danger"
                    cancelBtnBsStyle="default"
                    confirmBtnText="Confirm"
                    cancelBtnText="Cancel"
                    showCancel
                    btnSize=""
                >
                    <div>You are about to cancel this delivery.</div>
                    <div>This action cannot be undone.</div>
                </ReactBSAlert>
            </div>
            <div id="payment-success-alert" hidden={!paymentSuccessAlert}>
                {paymentSuccessAlert}
            </div>
            <div id="warning-alert" hidden={!showDecline}>
                {item
                    ? !item.Order.contains_alcohol
                        ? // <ReactBSAlert
                          //   warning
                          //   style={{ display: "block", marginTop: "-200px" }}
                          //   title="Item Unavailable?"
                          //   onConfirm={() => {
                          //     declineItem(item);
                          //     setItem(null);
                          //     setShowDecline(false);
                          //   }}
                          //   onCancel={() => {
                          //     setItem(null);
                          //     setShowDecline(false);
                          //   }}
                          //   confirmBtnBsStyle="danger"
                          //   cancelBtnBsStyle="default"
                          //   confirmBtnText="Confirm"
                          //   cancelBtnText="Cancel"
                          //   showCancel
                          //   btnSize=""
                          // >
                          //   <div>This item will be marked as <strong>"Out of Stock"</strong> on the Gophr app until you manually re-enable it in your Inventory Manager.</div>
                          //   <div>This action cannot be undone.</div>
                          // </ReactBSAlert>
                          defaultAlert()
                        : checkOrder(store.permit_type, shipment, item)
                    : null}
            </div>
            <div>
                <Modal isOpen={showOrderChatModal} toggle={toggleOrderChatModal} className="iphone-chat-modal">
                    <ModalBody className="iphone-chat-modal-body">
                        <div className="iphone-chat-header">
                            <div className="iphone-chat-header__title-row">
                                <div className="iphone-chat-header__avatars">
                                    <div className="iphone-chat-avatar iphone-chat-avatar--self">You</div>
                                    <div className="iphone-chat-avatar iphone-chat-avatar--recipient">{getMerchantChatNameInitials(getMerchantChatDisplayName(orderChatRecipientInfo))}</div>
                                </div>
                                <div className="iphone-chat-header__meta">
                                    <h4 className="iphone-chat-header__title">{getMerchantChatTitle(orderChatRecipientInfo)}</h4>
                                    <p className="iphone-chat-header__subtitle">
                                        {getMerchantChatDisplayName(orderChatRecipientInfo)}
                                        {orderChatRecipientInfo.chatShipmentFriendlyId ? ` • Shipment #${orderChatRecipientInfo.chatShipmentFriendlyId}` : ''}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <MessageLogsContainer driverMessageRetrieved={orderChatMessages} messageRecipientInfo={orderChatRecipientInfo} />

                        <div className="iphone-chat-composer">
                            <textarea
                                id="merchant-order-chat-message"
                                value={orderChatMessage}
                                onChange={event => {
                                    setOrderChatMessage(event.target.value);
                                }}
                                className="iphone-chat-composer__input"
                                placeholder={`Message ${getMerchantChatDisplayName(orderChatRecipientInfo)}...`}
                            />
                        </div>
                    </ModalBody>
                    <ModalFooter className="iphone-chat-modal-footer">
                        <Button
                            color="default"
                            onClick={() => {
                                toggleOrderChatModal();
                            }}
                        >
                            Close
                        </Button>
                        <Button color="warning" onClick={() => sendOrderChatMessage(orderChatRecipientInfo)}>
                            <FontAwesomeIcon icon={faPaperPlane} />
                            {' '}Send
                        </Button>
                    </ModalFooter>
                </Modal>
                <Modal isOpen={showSignature} toggle={toggleSignature}>
                    <ModalBody>
                        <img src={signatureUrl} alt="Signature" />
                    </ModalBody>
                    <ModalFooter style={{ alignSelf: 'center' }}>
                        <Button
                            color="default"
                            onClick={() => {
                                toggleSignature();
                            }}
                        >
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>
                <Modal
                    isOpen={Boolean(activeCustomerDriverReview)}
                    toggle={() => dismissCustomerDriverReviewModal(activeCustomerDriverReview ? activeCustomerDriverReview.shipment_id : null)}
                    centered
                    className="merchant-customer-review-modal"
                >
                    {activeCustomerDriverReview && (
                        <>
                            <ModalBody className="merchant-customer-review-modal-body">
                                <h2 className="merchant-customer-review-modal-title">How was {activeCustomerDriverReview.driver_name}?</h2>
                                <div className="merchant-customer-review-stars" aria-label="Driver rating options">
                                    {[1, 2, 3, 4, 5].map(starValue => {
                                        const currentDraft = getCustomerDriverReviewDraft(activeCustomerDriverReview.shipment_id);
                                        const isSelected = Number(currentDraft.rating || 0) >= starValue;

                                        return (
                                            <button
                                                key={`${activeCustomerDriverReview.shipment_id}-star-${starValue}`}
                                                type="button"
                                                className={`merchant-customer-review-star${isSelected ? ' is-selected' : ''}`}
                                                onClick={() => updateCustomerDriverReviewDraft(activeCustomerDriverReview.shipment_id, { rating: starValue })}
                                                disabled={submittingCustomerDriverReviewShipmentId === activeCustomerDriverReview.shipment_id}
                                                aria-label={`Rate ${starValue} star${starValue === 1 ? '' : 's'}`}
                                            >
                                                {isSelected ? <FaStar className="merchant-customer-review-star-icon" /> : <FaRegStar className="merchant-customer-review-star-icon" />}
                                            </button>
                                        );
                                    })}
                                </div>
                                <textarea
                                    id="customer-driver-review-textarea"
                                    className="form-control merchant-customer-review-textarea"
                                    rows="5"
                                    maxLength={CUSTOMER_DRIVER_REVIEW_MAX_LENGTH}
                                    placeholder="Leave a review (optional)"
                                    value={getCustomerDriverReviewDraft(activeCustomerDriverReview.shipment_id).reviewText}
                                    onChange={event => updateCustomerDriverReviewDraft(activeCustomerDriverReview.shipment_id, { reviewText: event.target.value })}
                                    disabled={submittingCustomerDriverReviewShipmentId === activeCustomerDriverReview.shipment_id}
                                />
                                {customerDriverReviewMessages[activeCustomerDriverReview.shipment_id] && (
                                    <p className="merchant-customer-review-message">{customerDriverReviewMessages[activeCustomerDriverReview.shipment_id]}</p>
                                )}
                            </ModalBody>
                            <ModalFooter className="merchant-customer-review-modal-footer">
                                <Button
                                    color="info"
                                    className="merchant-customer-review-submit-button"
                                    onClick={() => submitCustomerDriverReview(activeCustomerDriverReview.shipment_id)}
                                    disabled={submittingCustomerDriverReviewShipmentId === activeCustomerDriverReview.shipment_id}
                                >
                                    {submittingCustomerDriverReviewShipmentId === activeCustomerDriverReview.shipment_id ? 'Submitting...' : 'Submit review'}
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </Modal>
            </div>
            <FedexLabelModal isOpen={showFedexLabel} toggle={() => setShowFedexLabel(false)} shipmentId={selectedShipmentId} />
            <FedexTrackingModal isOpen={showTracking} toggle={() => setShowTracking(false)} shipmentId={selectedShipmentId} />

            {/* Payment Retry Modal */}
            {paymentRetryModalOpen && selectedShipmentForPayment && (
                <PaymentRetryModal
                    isOpen={paymentRetryModalOpen}
                    toggle={() => {
                        setPaymentRetryModalOpen(false);
                        setSelectedShipmentForPayment(null);
                    }}
                    shipmentId={selectedShipmentForPayment.shipment_id}
                    shipmentFriendlyId={selectedShipmentForPayment.shipment_friendly_id}
                    shipmentData={selectedShipmentForPayment}
                    onSuccess={data => {
                        void data;
                        setPaymentRetryModalOpen(false);
                        setSelectedShipmentForPayment(null);
                        // Apollo subscription will automatically refresh the table
                        setPaymentSuccessAlert(
                            <ReactBSAlert
                                success
                                style={{ display: 'block', marginTop: '-100px' }}
                                title="Success!"
                                onConfirm={() => setPaymentSuccessAlert(false)}
                                confirmBtnBsStyle="success"
                            >
                                Payment processed successfully! Your order is now active.
                            </ReactBSAlert>
                        );
                    }}
                    onClose={() => {
                        return null;
                    }}
                />
            )}
        </div>
    );
};

export default MerchantOrderTable;
